import { Store } from "./store.js";
import { marcadorTempo, periodoDoDia } from "./tempo";
import { Conn, type ConnState } from "./ws.js";
import type {
  AttachmentStored,
  AttachmentWire,
  AudioStored,
  AudioWire,
  ChatMessage,
  Contact,
  Direction,
  Group,
  MessageStatus,
  QuotedRef,
  UserStatus,
  WireIn,
  WireOut,
  WireSend,
} from "./types.js";
import {
  AudioRecorder,
  base64ToBlob,
  blobToBase64,
  formatDuration,
  shortLang,
} from "./audio.js";
import { onWhisperProgress, transcribeBlob } from "./whisper.js";
import { embedText, EMBEDDING_MODEL_ID } from "./embeddings.js";
import {
  RECURSOS,
  ORCAMENTO_MB,
  recursoAtivo,
  setRecurso,
  indisponivel,
  testarFala,
  avisoDeMemoria,
  lerVereditos,
  vereditoDe,
  type EstadoVeredito,
  type RecursoId,
} from "./inferencia.js";
import { diagnosticar, resumo, medirTetoAlocavel, type DiagnosticoAparelho } from "./diagnostico-aparelho.js";
import {
  PROVAS,
  rodarProva,
  enviarResultado,
  provaQueDerrubou,
  planejarBateria,
  rodarBateria,
  condenarQuemDerrubou,
  FOLGA_DO_TETO,
  type EstadoDoPasso,
} from "./banco-de-provas.js";
import * as llmNativo from "./llm-nativo.js";
import * as iaContato from "./ia-contato.js";
import * as postura from "./postura.js";
import * as hf from "./hugging-face.js";
import * as visaoNativa from "./visao-nativa.js";
import * as registro from "./registro-local.js";

/** Porta de troca para os testes: quem mede o teto e quem executa um modelo.
 *
 *  A sequência do diagnóstico continua sendo a de produção — o que se substitui
 *  é só a parte cara. Sem esta porta, provar o que a TELA faz com um resultado
 *  custaria centenas de MB de download por asserção, e o resultado que mais
 *  importa (o modelo que não coube) é justamente o que não dá para provocar de
 *  propósito num aparelho bom.
 *
 *  Nada aqui é lido em produção: `window.__wuweiDiagnostico` não é definido em
 *  lugar nenhum do app, e a ausência cai no caminho normal. */
declare global {
  interface Window {
    __wuweiDiagnostico?: {
      medirTeto?: () => Promise<number | null>;
      executarProva?: typeof rodarProva;
    };
  }
}
// Importado pelo efeito: registra window.__wuweiCripto e garante que o módulo
// entre no bundle. A cifragem das mensagens passa a usá-lo em seguida.
import {
  parDoAparelho,
  publicarChave,
  chavesDe,
  idDoAparelho,
  cifrarParaTodos,
  abrirEnvelope,
  pareceEnvelope,
  cifrarBase64ParaTodos,
  abrirEnvelopeBinario,
  pareceEnvelopeBinario,
  type Envelope,
  type EnvelopeBinario,
} from "./cripto.js";
import { getPool, newInvocationId } from "./agent-pool.js";
import type { AttachmentPageEmbedding } from "./types.js";

/** Traduz o "aproveitado/total" do motor para uma frase curta.
 *
 *  O número cru não diz nada a quem lê a tela; o que importa é a proporção.
 *  "leu 40 de 912 tokens" mostra o cache funcionando; "leu 912 de 912" mostra
 *  que a conversa inteira foi reingerida, que é exatamente o que se quer
 *  detectar sem depender do relógio. */
function leituraDoPrompt(prefill: string): string {
  const [aproveitado, total] = prefill.split("/").map((n) => Number(n) || 0);
  if (!total) return "";
  const lidos = Math.max(0, total - (aproveitado ?? 0));
  return `${lidos} de ${total} tokens`;
}


declare global {
  interface Window {
    WUWEI_CONFIG?: { wsUrl: string };
  }
}

const SESSION_KEY = "wuwei.session";
const EMAIL_KEY = "wuwei.email";
/** Largura da coluna de contatos, em px. Por aparelho, de propósito: o que
 *  cabe no monitor grande não é o que cabe no notebook. */
const LARGURA_KEY = "wuwei.sidebarW";

/** Uma linha da aba Arquivos: o que o índice do servidor devolve, sem bytes. */
interface ArquivoListado {
  id: string;
  from: string;
  to: string;
  name: string;
  mime: string;
  kind: string;
  bytes: number;
  ts: number;
  mine: boolean;
  audio?: boolean;
  durationMs?: number;
}

interface Session {
  email: string;
  session: string;
}

function getSession(): Session | null {
  // Primeiro tenta o hash (vindo do redirect do magic link).
  if (location.hash.startsWith("#")) {
    const params = new URLSearchParams(location.hash.slice(1));
    const session = params.get("session");
    const email = params.get("email");
    if (session && email) {
      localStorage.setItem(SESSION_KEY, session);
      localStorage.setItem(EMAIL_KEY, email);
      // Limpa a URL para não vazar o token em logs/screenshots.
      history.replaceState(null, "", location.pathname);
      return { email, session };
    }
  }
  const session = localStorage.getItem(SESSION_KEY);
  const email = localStorage.getItem(EMAIL_KEY);
  if (session && email) return { email, session };
  return null;
}

function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(EMAIL_KEY);
}

function $(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error("missing #" + id);
  return el;
}

/** Data e hora completas, para decidir se uma chave ainda está viva. Aqui não
 *  cabe o "14:32" do chat: um agente usado ontem e outro usado no ano passado
 *  não podem parecer a mesma coisa na hora de revogar. */
function fmtQuando(ts?: number): string {
  if (!ts) return "—";
  const d = new Date(ts);
  const p = (n: number) => n.toString().padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function fmtTime(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  if (sameDay) return `${hh}:${mm}`;
  const dd = d.getDate().toString().padStart(2, "0");
  const mo = (d.getMonth() + 1).toString().padStart(2, "0");
  return `${dd}/${mo} ${hh}:${mm}`;
}

function avatarInitials(email: string): string {
  const name = email.split("@")[0] ?? email;
  const parts = name.split(/[._-]/).filter(Boolean);
  const a = parts[0]?.[0] ?? "?";
  const b = parts[1]?.[0] ?? "";
  return (a + b).toUpperCase();
}

function colorFor(email: string): string {
  let h = 0;
  for (let i = 0; i < email.length; i++) h = (h * 31 + email.charCodeAt(i)) >>> 0;
  // Paleta exclusivamente em tons de azul/sky/navy/cyan/teal — coerente
  // com o resto da UI.
  const palette = [
    "#2563eb", // blue-600
    "#1d4ed8", // blue-700
    "#1e40af", // blue-800
    "#0ea5e9", // sky-500
    "#0369a1", // sky-700
    "#0891b2", // cyan-600
    "#0e7490", // cyan-700
    "#3b82f6", // blue-500
  ];
  return palette[h % palette.length]!;
}

/** Converte chave pública VAPID (base64url) para Uint8Array que o
 *  pushManager.subscribe espera em applicationServerKey. */
function urlB64ToUint8Array(b64: string): Uint8Array {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** A chave que decide onde entra um divisor: dia + período. */
function chavePeriodo(ts: number): string {
  const d = new Date(ts);
  return `${d.toDateString()}|${periodoDoDia(d)}`;
}

/** O rótulo do divisor: "hoje à tarde", "ontem de manhã", "12/08 à noite". */
function periodoLabel(ts: number): string {
  const d = new Date(ts);
  const periodo = periodoDoDia(d);
  const sufixo = periodo === "manha" ? "de manhã" : periodo === "tarde" ? "à tarde" : "à noite";
  const dia = dayLabel(ts);
  // "Hoje"/"Ontem" viram minúsculas para a frase ficar corrida ("hoje à
  // tarde"); data e dia da semana continuam como estão.
  const diaTexto = dia === "Hoje" || dia === "Ontem" ? dia.toLowerCase() : dia;
  return `${diaTexto} ${sufixo}`;
}

function dayLabel(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return "Hoje";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate();
  if (isYesterday) return "Ontem";
  const daysAgo = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (daysAgo < 7) {
    const wk = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
    return wk[d.getDay()]!;
  }
  const dd = d.getDate().toString().padStart(2, "0");
  const mo = (d.getMonth() + 1).toString().padStart(2, "0");
  const yr = d.getFullYear();
  return `${dd}/${mo}/${yr}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// -------- Login --------

function showLogin() {
  $("login").classList.remove("hidden");
  $("chat").classList.add("hidden");
  const form = $("login-form") as HTMLFormElement;
  const status = $("login-status");
  form.onsubmit = async (e) => {
    e.preventDefault();
    const input = (form.elements.namedItem("email") as HTMLInputElement).value
      .trim()
      .toLowerCase();
    if (!input || !input.includes("@")) {
      status.textContent = "Informe um email válido.";
      status.className = "login-status err";
      return;
    }
    status.textContent = "Entrando...";
    status.className = "login-status muted";
    try {
      const r = await fetch("/auth/dev-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: input }),
      });
      // Servidor de produção não tem dev-login. Aí o caminho é o magic link:
      // pede o envio e para por aqui — quem continua o login é o clique no
      // email. Mensagem igual para email de dentro e de fora da lista, senão
      // a tela de entrada vira um verificador de quem tem conta.
      if (r.status === 403) {
        const pedido = await fetch("/auth/request", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: input }),
        });
        const resp = (await pedido.json().catch(() => ({}))) as {
          fila?: boolean;
          posicao?: number;
        };
        // Quem entrou na fila precisa saber disso. Mostrar "link enviado" para
        // quem não vai receber link nenhum é deixar a pessoa esperando um email
        // que não existe — e ela acharia que errou o próprio endereço.
        if (resp.fila) {
          status.textContent =
            `Estamos com as vagas cheias no momento. Você entrou na fila de espera` +
            (resp.posicao ? ` na posição ${resp.posicao}` : "") +
            `, e avisamos assim que abrir uma vaga.`;
          status.className = "login-status muted";
          return;
        }
        status.textContent = "Link enviado. Abra seu email para entrar.";
        status.className = "login-status muted";
        return;
      }
      if (!r.ok) {
        const err = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error || `falhou (${r.status})`);
      }
      const data = (await r.json()) as { session: string; email: string };
      localStorage.setItem(SESSION_KEY, data.session);
      localStorage.setItem(EMAIL_KEY, data.email);
      location.reload();
    } catch (err) {
      status.textContent =
        "Erro: " + (err instanceof Error ? err.message : String(err));
      status.className = "login-status err";
    }
  };
}

// -------- Chat --------

class ChatApp {
  private store!: Store;
  private conn!: Conn;
  private contacts: Contact[] = [];
  private currentContact: string | null = null;
  private messages: ChatMessage[] = [];
  /** Contato local cuja resposta está sendo gerada agora, ou null.
   *
   *  Guardado por contato, e não como booleano, porque é ele que acende o
   *  "digitando…" na linha certa da lista: quem saiu da conversa enquanto o
   *  modelo escrevia precisa continuar vendo que a resposta está vindo. */
  private iaGerando: string | null = null;
  /** Modelo escolhido no seletor de menções, enquanto o "@ia" está no texto. */
  private modeloDaMencao: string | null = null;
  private status: ConnState = "closed";
  private recorder = new AudioRecorder();
  private recTimerHandle: number | null = null;
  private pendingAttachment: AttachmentStored | null = null;
  private blobUrls = new Map<string, string>();
  private onlineUsers = new Set<string>();
  /** quem está digitando para mim (set de emails) */
  private typingFromSet = new Set<string>();
  private avisoTimer: number | undefined;
  private viewerMsgId: string | null = null;
  /** Zera o zoom do visualizador; definido quando o gesto é instalado. */
  private resetarZoom: (() => void) | null = null;
  private convSelecionada: string | null = null;
  /** Rascunho de membros enquanto a tela de editar grupo está aberta. */
  private grupoEditando: { id: string; membros: string[] } | null = null;
  /** Índice do item destacado na lista de menções (-1 = nenhum). */
  private mencaoIdx = -1;
  private mencaoLista: string[] = [];
  private camStream: MediaStream | null = null;
  private camRec: MediaRecorder | null = null;
  private camGravando = false;
  private camTimer: number | undefined;
  private camFacing: "user" | "environment" = "environment";
  /** Guardado do evento `beforeinstallprompt`: o navegador só deixa abrir o
   *  convite de instalação a partir de um gesto do usuário, então é preciso
   *  segurar o evento até ele clicar. */
  private promptInstalar: (Event & { prompt(): Promise<void>; userChoice: Promise<{ outcome: string }> }) | null = null;
  private selecionadas = new Set<string>();
  /** Conversas apagadas neste aparelho. O histórico continua no servidor, e o
   *  sync do boot o traria de volta — por isso a exclusão precisa ser lembrada
   *  aqui. Sai da lista quando chega mensagem nova (a conversa reabre). */
  private apagadas = new Set<string>(
    JSON.parse(localStorage.getItem("wuwei.apagadas") ?? "[]") as string[],
  );

  /** Apaga a conversa deste aparelho por ordem vinda de fora (eco de outro
   *  device, ou lista de ocultas no sync). Igual ao botão, menos o envio. */
  private async esconderConversaLocal(email: string) {
    await this.store.deleteConversation(email);
    this.marcarApagada(email, true);
    this.contacts = this.contacts.filter((c) => c.email !== email);
    if (this.currentContact === email) {
      this.currentContact = null;
      this.messages = [];
      this.showDashboardView();
    }
    this.renderContacts();
  }

  private marcarApagada(email: string, apagada: boolean) {
    if (apagada) this.apagadas.add(email);
    else this.apagadas.delete(email);
    localStorage.setItem("wuwei.apagadas", JSON.stringify([...this.apagadas]));
  }
  /** controle de envio de typing para o destinatário */
  private typingSentAt = 0;
  private typingStopHandle: number | null = null;
  /** Histórico paginado: tamanho de página, se há mais antigas, e guard de carga. */
  private readonly pageLimit = 50;
  private hasMoreOlder = false;
  private loadingOlder = false;
  private scrollListenerAttached = false;
  /** A pílula da linha do tempo, criada na primeira rolagem e reaproveitada. */
  private marcadorEl: HTMLElement | null = null;
  private marcadorRaf: number | null = null;
  private marcadorTimer: number | null = null;
  /** A alça arrastável ao lado da pílula, e o estado do arraste. */
  private alcaEl: HTMLElement | null = null;
  private reguaEl: HTMLElement | null = null;
  /** Qual lista a linha do tempo está servindo agora, e como achar os itens. */
  /** A lista que a alça controla no momento. Escrito por quem ROLOU (em
   *  atualizarMarcadorTempo), nunca por quem renderizou — foi renderizar que
   *  fazia a lista de conversas roubar o alvo da conversa aberta. */
  private alcaAlvo: { el: HTMLElement; seletor: string } | null = null;
  private listasComScroll = new WeakSet<HTMLElement>();
  private reguaAssinatura = "";
  private arrastandoAlca = false;
  /** Quando o dedo (ou a roda) tocou a lista pela última vez. A linha do tempo
   *  só acende com gesto: enviar mensagem rola sozinho até o fim, e sem isto
   *  ela piscava a cada envio e a cada conversa aberta. */
  private ultimoGesto = 0;
  /** Altura da alça em px — precisa bater com o CSS, porque a conta da fração
   *  desconta ela do trecho percorrível. */
  private readonly ALCA_PX = 44;
  /** Animação de entrada da bolha (`bubble-in`): como renderMessages()
   *  reconstrói a lista com innerHTML, TODA bolha recriada reexecutaria a
   *  animação — a conversa inteira piscava a cada re-render. Estes dois
   *  conjuntos limitam a animação às bolhas que o usuário ainda não viu. */
  private seenBubbleIds = new Set<string>();
  private seenBubbleContact: string | null = null;
  private freshBubbleIds = new Set<string>();
  /** Busca: debounce do input + timer de highlight pós-jump. */
  private searchTimer: number | null = null;
  private highlightTimer: number | null = null;
  /** Quote ativo no composer. Se setado, próxima mensagem é uma reply. */
  private composerQuote: QuotedRef | null = null;
  /** Forward modal: msg sendo encaminhada e set de destinos selecionados. */
  private forwardingMsg: ChatMessage | null = null;
  private forwardSelected = new Set<string>();
  /** ID da mensagem cujo menu/picker está aberto (pra fechar via outside-click). */
  private menuTargetMsgId: string | null = null;
  private menuOutsideHandler: ((e: MouseEvent | TouchEvent) => void) | null = null;
  /** Meu status atual + status de cada contato online. */
  private myStatus: UserStatus = "";
  private statusByEmail = new Map<string, UserStatus>();
  /** Cache local de grupos (id → Group). Hidratado no hello + IDB. */
  private groups = new Map<string, Group>();
  /** Set de emails selecionados durante o create-group modal. */
  private groupSelected = new Set<string>();
  /** Filtro ativo da sidebar: 'all' | 'unread' | 'groups'. */
  private contactFilter: "all" | "unread" | "groups" | "files" = "all";

  constructor(private readonly sess: Session) {}

  async start() {
    this.store = await Store.open(this.sess.email);
    this.contacts = await this.store.listContacts();
    // Carrega grupos persistidos antes da primeira renderContacts.
    const savedGroups = await this.store.listGroups();
    for (const g of savedGroups) this.groups.set(g.id, g);
    // Pré-popula o map hash → contact pra resolver deep-link /r/<hash>
    // antes do popstate inicial.
    await this.warmHashIndex();
    for (const g of savedGroups) await this.hashContact(g.id);

    $("login").classList.add("hidden");
    $("chat").classList.remove("hidden");
    // A faixa com avatar, status e email saiu do menu enxuto. Aqui é cedo
    // demais para estourar por causa dela: estas linhas rodam ANTES do
    // composer, então um `$()` que lança deixaria a tela montada e muda.
    const meuEmail = document.getElementById("me-email");
    if (meuEmail) meuEmail.textContent = this.sess.email;
    const meuAvatar = document.getElementById("me-avatar");
    if (meuAvatar) {
      meuAvatar.textContent = avatarInitials(this.sess.email);
      meuAvatar.style.background = colorFor(this.sess.email);
    }

    // O composer é ligado ANTES de qualquer widget opcional. Ele ficava por
    // último em start(), então bastava um `$()` estourar no meio do caminho
    // (elemento ausente numa variante da UI, API que o navegador não tem) para
    // o listener de `input` nunca ser registrado: a tela aparecia inteira,
    // aceitava texto, e o botão continuava preso no ícone de MICROFONE porque
    // updateActionMode() jamais rodava.
    ($("composer-form") as HTMLFormElement).onsubmit = (e) => {
      e.preventDefault();
      void this.handleSend();
    };
    this.wireComposer();
    this.wireSidebarResize();

    this.renderContacts();
    this.renderEmpty();

    const sair = () => {
      clearSession();
      location.reload();
    };
    ($("logout") as HTMLButtonElement).onclick = sair;
    // Gêmeo do sair que vive no header da conversa (visível só no celular).
    const sairNoChat = document.getElementById("chat-logout") as HTMLButtonElement | null;
    if (sairNoChat) sairNoChat.onclick = sair;

    const notifBtn = document.getElementById("notif-btn") as HTMLButtonElement | null;
    if (notifBtn) {
      notifBtn.onclick = async () => {
        if (typeof Notification === "undefined") return;
        try {
          const perm = await Notification.requestPermission();
          if (perm === "granted") {
            await this.subscribePush();
          }
        } catch {
          /* ignore */
        }
        this.updateNotifBtn();
      };
    }
    this.maybeAskNotificationPermission();
    // Se já temos permissão concedida e SW está disponível, garante subscribe.
    void this.ensurePushSubscribed();
    // Embeda áudios já transcritos que ainda não têm vetor — roda em background,
    // não bloqueia UI. Espera 2s pra não competir com o handshake do WS.
    setTimeout(() => void this.embedBackfill(), 2000);
    // Reage a clicks em notificações que vieram via SW: abre a conversa.
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", (e) => {
        const data = e.data || {};
        if (data.type === "open-conversation" && data.from) {
          void this.openContact(String(data.from).toLowerCase());
        }
      });
    }
    // Mesmo handling pro deep-link ?open=email gerado pelo openWindow do SW.
    const openParam = new URLSearchParams(location.search).get("open");
    if (openParam && openParam.includes("@")) {
      history.replaceState(null, "", location.pathname);
      void this.openContact(openParam.toLowerCase());
    } else {
      // Rota direta /c/<email> ou /g/<id>: abre a conversa correspondente.
      const fromPath = this.pathToContact(location.pathname);
      if (fromPath) void this.openContact(fromPath);
    }
    // Navegação back/forward — sincroniza a UI com a URL.
    window.addEventListener("popstate", () => {
      const target = this.pathToContact(location.pathname);
      if (target) {
        if (this.currentContact !== target) void this.openContact(target);
      } else if (this.currentContact !== null) {
        // Voltou pra raiz → dashboard.
        this.currentContact = null;
        this.showDashboardView();
      }
    });

    // Widgets opcionais: cada um isolado. Um elemento que sumiu do HTML não
    // pode derrubar o resto da inicialização.
    this.safeWire("invite", () => {
      const inviteBtn = document.getElementById(
        "invite-btn",
      ) as HTMLButtonElement | null;
      if (inviteBtn) inviteBtn.onclick = () => void this.openInviteModal();
      this.wireInviteModal();
    });
    this.safeWire("status-select", () => this.wireStatusSelect());
    this.safeWire("theme-toggle", () => this.wireThemeToggle());
    this.safeWire("sidebar-tabs", () => this.wireSidebarTabs());
    this.safeWire("new-conv-modal", () => this.wireNewConvModal());
    this.safeWire("dashboard-actions", () => this.wireDashboardActions());
    this.safeWire("search", () => this.wireSearch());
    this.safeWire("message-menu", () => this.wireMessageMenu());
    this.safeWire("react-picker", () => this.wireReactPicker());
    this.safeWire("selection-bar", () => this.wireSelectionBar());
    this.safeWire("img-viewer", () => this.wireImageViewer());
    this.safeWire("agents", () => this.wireAgents());
    this.safeWire("profile", () => this.wirePerfil());
    this.safeWire("inferencias", () => this.wireInferencias());
    this.safeWire("agentes-locais", () => this.wireAgentesLocais());
    this.safeWire("baixar-app", () => void this.wireBaixarApp());
    this.safeWire("conv-selection", () => this.wireConvSelection());
    this.safeWire("app-menu", () => this.wireAppMenu());
    this.safeWire("camera", () => this.wireCamera());
    this.safeWire("group-edit", () => this.wireGroupEdit());
    this.safeWire("mencoes", () => this.wireMencoes());
    this.safeWire("instalar", () => this.wireInstalar());
    this.safeWire("quote-cancel", () => this.wireQuoteCancel());
    this.safeWire("forward-modal", () => this.wireForwardModal());
    this.safeWire("group-modal", () => {
      this.wireGroupModal();
      const newGroupBtn = document.getElementById(
        "new-group-btn",
      ) as HTMLButtonElement | null;
      if (newGroupBtn) newGroupBtn.onclick = () => this.openGroupModal();
    });
    this.safeWire("new-contact-form", () => {
      ($("new-contact-form") as HTMLFormElement).onsubmit = async (e) => {
        e.preventDefault();
        const inp = (e.target as HTMLFormElement).elements.namedItem(
          "contact",
        ) as HTMLInputElement;
        const email = inp.value.trim().toLowerCase();
        if (!email || !email.includes("@") || email === this.sess.email) {
          inp.focus();
          return;
        }
        await this.store.ensureContact(email);
        this.contacts = await this.store.listContacts();
        this.renderContacts();
        inp.value = "";
        void this.openContact(email);
      };
    });

    // O fallback precisa acompanhar o protocolo da página: `ws://` cru dentro
    // de uma página HTTPS é conteúdo misto — o navegador bloqueia a conexão e
    // marca o site como inseguro.
    const wsScheme = location.protocol === "https:" ? "wss" : "ws";
    const wsUrl =
      window.WUWEI_CONFIG?.wsUrl ?? `${wsScheme}://${location.hostname}:8765`;
    this.conn = new Conn({
      url: wsUrl,
      session: this.sess.session,
      onMessage: (m) => void this.handleWire(m),
      onStatus: (s) => this.setStatus(s),
    });
    this.conn.start();

    this.safeWire("whisper-progress", () => this.wireWhisperProgress());
  }

  /** Liga um widget opcional em isolamento. Se explodir (elemento ausente numa
   *  variante da UI, API que o navegador não expõe), registra e segue — o
   *  resto da tela, e sobretudo o composer, continua funcionando. */
  private safeWire(nome: string, fn: () => void): void {
    try {
      fn();
    } catch (err) {
      console.error(`[wuwei] falha ao ligar "${nome}":`, err);
    }
  }

  private wireWhisperProgress() {
    const wrap = document.getElementById("whisper-progress");
    const label = wrap?.querySelector<HTMLElement>(".whisper-progress-label");
    const fill = wrap?.querySelector<HTMLElement>(".whisper-progress-fill");
    if (!wrap || !label || !fill) return;
    let hideTimer: number | null = null;
    onWhisperProgress((info) => {
      // O transformers.js emite muitos eventos por arquivo do modelo;
      // simplificamos pra um label + barra agregada.
      if (info.status === "downloading" || info.status === "progress") {
        const pct = Math.max(0, Math.min(100, Math.round((info as { progress?: number }).progress ?? 0)));
        wrap.classList.remove("hidden");
        const file = (info as { file?: string }).file ?? "modelo";
        label.textContent = `baixando ${file} ${pct}%`;
        fill.style.width = pct + "%";
        if (hideTimer != null) clearTimeout(hideTimer);
      } else if (info.status === "ready" || info.status === "done") {
        if (hideTimer != null) clearTimeout(hideTimer);
        hideTimer = window.setTimeout(() => {
          wrap.classList.add("hidden");
          fill.style.width = "0%";
          label.textContent = "";
        }, 800);
      }
    });
  }

  // ---------- Composer (anexar / câmera / mic / send / stop / cancel) ----------

  /** Arrastar a borda da lista de conversas. Só faz sentido no desktop: no
   *  celular a lista ocupa a tela e o pegador está escondido por CSS.
   *
   *  A largura vive numa CSS var e é guardada no localStorage — reajustar a
   *  cada abertura seria pior que a largura fixa que isto veio substituir. */
  private wireSidebarResize() {
    const pegador = document.getElementById("sidebar-resizer");
    if (!pegador) return;
    const MIN = 240;
    const MAX = 640;
    const PADRAO = 320;

    const aplicar = (px: number) => {
      const w = Math.round(Math.min(MAX, Math.max(MIN, px)));
      document.documentElement.style.setProperty("--sidebar-w", `${w}px`);
      return w;
    };

    const salva = Number(localStorage.getItem(LARGURA_KEY) || 0);
    if (salva) aplicar(salva);

    let arrastando = false;
    pegador.addEventListener("pointerdown", (e) => {
      arrastando = true;
      pegador.setPointerCapture((e as PointerEvent).pointerId);
      pegador.classList.add("is-dragging");
      document.body.classList.add("is-resizing");
    });
    pegador.addEventListener("pointermove", (e) => {
      if (!arrastando) return;
      // A lista começa na borda esquerda da janela, então a largura é o x do
      // ponteiro. Nada de somar offsets que mudam com a barra de rolagem.
      aplicar((e as PointerEvent).clientX);
    });
    const soltar = () => {
      if (!arrastando) return;
      arrastando = false;
      pegador.classList.remove("is-dragging");
      document.body.classList.remove("is-resizing");
      const atual = getComputedStyle(document.documentElement)
        .getPropertyValue("--sidebar-w")
        .trim();
      if (atual) localStorage.setItem(LARGURA_KEY, String(parseInt(atual, 10)));
    };
    pegador.addEventListener("pointerup", soltar);
    pegador.addEventListener("pointercancel", soltar);
    // Escape de quem arrastou demais e não sabe voltar.
    pegador.addEventListener("dblclick", () => {
      aplicar(PADRAO);
      localStorage.setItem(LARGURA_KEY, String(PADRAO));
    });
  }

  private wireComposer() {
    const ta = $("composer-input") as HTMLTextAreaElement;
    ta.addEventListener("input", () => {
      this.updateActionMode();
      this.onComposerTyping();
    });

    const btnAction = $("btn-action") as HTMLButtonElement;
    btnAction.onclick = () => this.handleActionClick();

    const btnAttach = $("btn-attach") as HTMLButtonElement;
    const btnCamera = $("btn-camera") as HTMLButtonElement;
    const fileInput = $("file-input") as HTMLInputElement;
    const cameraInput = $("camera-input") as HTMLInputElement;
    const btnRecCancel = $("btn-rec-cancel") as HTMLButtonElement;
    const btnRemoveAttach = $("attach-preview-remove") as HTMLButtonElement;

    // Antes o botão inteiro era escondido quando não havia gravação de áudio.
    // Como ele acumula mic/enviar/parar, isso levava junto o botão de ENVIAR —
    // e em qualquer contexto sem `navigator.mediaDevices` (HTTP puro por IP,
    // permissão negada, aparelho sem microfone) o usuário ficava sem conseguir
    // mandar texto. Agora o botão fica, e o modo cai para "send".
    if (!AudioRecorder.supported()) btnAction.classList.add("no-audio");

    btnAttach.onclick = async () => {
      // O <input type="file"> faz o Android oferecer "Câmera / Gravador /
      // Arquivos" — um toque a mais toda vez. O seletor nativo do navegador
      // (File System Access API) abre direto no gerenciador de arquivos.
      // Onde não existir, cai no input de sempre.
      const abrirSeletor = (window as unknown as {
        showOpenFilePicker?: (o?: unknown) => Promise<Array<{ getFile(): Promise<File> }>>;
      }).showOpenFilePicker;
      if (typeof abrirSeletor === "function") {
        try {
          const [handle] = await abrirSeletor({ multiple: false });
          if (handle) {
            void this.attachFile(await handle.getFile());
            return;
          }
        } catch (e) {
          // Cancelar é escolha do usuário: não abre o fallback por cima.
          if ((e as DOMException)?.name === "AbortError") return;
          // Qualquer outra falha (API bloqueada por permissão, contexto
          // inseguro) cai no caminho antigo.
        }
      }
      fileInput.click();
    };
    btnCamera.onclick = async () => {
      // Câmera do app quando possível; o seletor do sistema é a rede de
      // segurança para quem negar a permissão ou não tiver suporte.
      if (!(await this.abrirCamera())) cameraInput.click();
    };
    btnRecCancel.onclick = () => this.cancelRecording();
    btnRemoveAttach.onclick = () => this.discardAttachment();

    fileInput.onchange = () => {
      const f = fileInput.files?.[0];
      if (f) void this.attachFile(f);
      fileInput.value = "";
    };
    cameraInput.onchange = () => {
      const f = cameraInput.files?.[0];
      if (f) void this.attachFile(f);
      cameraInput.value = "";
    };

    this.wireColar();
    this.wireArrastar();
    this.updateActionMode();
  }

  /** Ctrl+V no campo de mensagem anexa o que estiver na área de transferência.
   *
   * Print de tela é o caso que importa: ele chega como imagem sem nome nenhum,
   * e um anexo sem nome fica ilegível na lista de arquivos do outro lado. Por
   * isso o nome é gerado aqui, com a data, em vez de virar "arquivo". */
  private wireColar() {
    const ta = $("composer-input") as HTMLTextAreaElement;
    ta.addEventListener("paste", (ev: ClipboardEvent) => {
      const itens = Array.from(ev.clipboardData?.items ?? []);
      const arquivo = itens.find((i) => i.kind === "file");
      if (!arquivo) return; // texto colado segue o caminho normal
      const f = arquivo.getAsFile();
      if (!f) return;
      // Só aqui o preventDefault: colar texto não pode ser interceptado.
      ev.preventDefault();
      void this.attachFile(nomeSeFaltar(f));
    });
  }

  /** Arrastar um arquivo sobre a conversa e soltar.
   *
   * O alvo é a janela inteira, não só o compositor: quem arrasta mira na
   * conversa, e um alvo do tamanho do campo de texto faz o arquivo cair fora e
   * o navegador ABRIR o arquivo por cima do app — perdendo o que estava escrito. */
  private wireArrastar() {
    const zona = document.body;
    let profundidade = 0;

    const mostrar = (on: boolean) => zona.classList.toggle("arrastando", on);

    zona.addEventListener("dragenter", (ev: DragEvent) => {
      if (!Array.from(ev.dataTransfer?.types ?? []).includes("Files")) return;
      ev.preventDefault();
      // dragenter/dragleave disparam a cada elemento filho que o cursor
      // atravessa. Sem contar a profundidade, o realce pisca sobre cada bolha
      // da conversa.
      profundidade++;
      mostrar(true);
    });
    zona.addEventListener("dragover", (ev: DragEvent) => {
      if (Array.from(ev.dataTransfer?.types ?? []).includes("Files")) ev.preventDefault();
    });
    zona.addEventListener("dragleave", () => {
      profundidade = Math.max(0, profundidade - 1);
      if (!profundidade) mostrar(false);
    });
    zona.addEventListener("drop", (ev: DragEvent) => {
      const f = ev.dataTransfer?.files?.[0];
      profundidade = 0;
      mostrar(false);
      if (!f) return;
      ev.preventDefault();
      void this.attachFile(nomeSeFaltar(f));
    });
  }

  /** Modo do botão direito: mic (vazio) | send (com texto/anexo) | stop (gravando). */
  private getActionMode(): "mic" | "send" | "stop" {
    if (this.recorder.isRecording()) return "stop";
    const ta = $("composer-input") as HTMLTextAreaElement;
    if (ta.value.trim() || this.pendingAttachment) return "send";
    // Sem gravação disponível não existe modo "mic" a oferecer.
    return AudioRecorder.supported() ? "mic" : "send";
  }

  private updateActionMode() {
    const btn = $("btn-action") as HTMLButtonElement;
    const mode = this.getActionMode();
    btn.dataset.mode = mode;
    // "(Enter)" só onde Enter envia: no celular ele quebra linha, e prometer
    // um atalho que não existe é pior do que não prometer nenhum.
    btn.title =
      mode === "send"
        ? enterQuebraLinha()
          ? "Enviar"
          : "Enviar (Enter)"
        : mode === "stop"
          ? "Parar gravação"
          : "Gravar áudio";
    // Em "send" sem nada escrito (só acontece quando não há mic disponível)
    // o botão fica inerte em vez de fingir que envia.
    const ta = $("composer-input") as HTMLTextAreaElement;
    btn.disabled = mode === "send" && !ta.value.trim() && !this.pendingAttachment;
    // Os três ícones são <svg>, e SVGElement NÃO implementa a propriedade
    // `hidden` do HTMLElement: `el.hidden = true` só cria um expando em JS,
    // não reflete no atributo, e o CSS [hidden] nunca chega a valer. Por isso
    // o botão continuava mostrando o microfone mesmo com data-mode="send".
    // Mexer no atributo funciona para HTML e SVG.
    const mostrarSo = (sel: string, visivel: boolean) => {
      const el = btn.querySelector(sel);
      if (!el) return;
      if (visivel) el.removeAttribute("hidden");
      else el.setAttribute("hidden", "");
    };
    mostrarSo(".action-mic", mode === "mic");
    mostrarSo(".action-send", mode === "send");
    mostrarSo(".action-stop", mode === "stop");
  }

  private handleActionClick() {
    const mode = this.getActionMode();
    if (mode === "stop") void this.stopRecording();
    else if (mode === "send") void this.handleSend();
    else void this.startRecording();
  }

  // ---------- Anexo ----------

  private async attachFile(f: File) {
    if (f.size > 6 * 1024 * 1024) {
      alert(`"${f.name}" é grande demais (máx 6 MB).`);
      return;
    }
    this.pendingAttachment = {
      name: f.name || "arquivo",
      mime: f.type || "application/octet-stream",
      blob: f,
    };
    this.renderAttachmentPreview();
    this.updateActionMode();
  }

  private discardAttachment() {
    this.pendingAttachment = null;
    const wrap = $("attach-preview") as HTMLElement;
    const thumb = $("attach-preview-thumb") as HTMLElement;
    if (thumb.dataset.url) {
      URL.revokeObjectURL(thumb.dataset.url);
      delete thumb.dataset.url;
    }
    wrap.classList.add("hidden");
    this.updateActionMode();
  }

  private renderAttachmentPreview() {
    const a = this.pendingAttachment;
    const wrap = $("attach-preview") as HTMLElement;
    const thumb = $("attach-preview-thumb") as HTMLElement;
    const name = $("attach-preview-name") as HTMLElement;
    const size = $("attach-preview-size") as HTMLElement;
    if (thumb.dataset.url) {
      URL.revokeObjectURL(thumb.dataset.url);
      delete thumb.dataset.url;
    }
    thumb.innerHTML = "";
    if (!a) {
      wrap.classList.add("hidden");
      return;
    }
    if (a.mime.startsWith("image/")) {
      const url = URL.createObjectURL(a.blob);
      thumb.dataset.url = url;
      const img = document.createElement("img");
      img.src = url;
      img.alt = a.name;
      thumb.appendChild(img);
    } else if (a.mime.startsWith("video/")) {
      // Vídeo também merece prévia: o primeiro quadro já diz o que é.
      const url = URL.createObjectURL(a.blob);
      thumb.dataset.url = url;
      const vid = document.createElement("video");
      vid.src = url;
      vid.muted = true;
      vid.playsInline = true;
      vid.preload = "metadata";
      thumb.appendChild(vid);
    } else {
      thumb.textContent = fileEmoji(a.mime, a.name);
      thumb.classList.add("attach-preview-icon");
    }
    name.textContent = a.name;
    size.textContent = formatBytes(a.blob.size);
    wrap.classList.remove("hidden");
  }

  // ---------- Gravação ----------

  private async startRecording() {
    if (!this.currentContact) return;
    try {
      await this.recorder.start(undefined, this.buildTranscriptionContext());
    } catch (err) {
      alert(
        "Não consegui acessar o microfone: " +
          (err instanceof Error ? err.message : String(err)),
      );
      return;
    }
    const ta = $("composer-input") as HTMLTextAreaElement;
    ta.style.display = "none";
    ($("rec-indicator") as HTMLElement).hidden = false;
    ($("btn-rec-cancel") as HTMLButtonElement).hidden = false;
    ($("rec-timer") as HTMLElement).textContent = "00:00";
    if (this.recTimerHandle != null) clearInterval(this.recTimerHandle);
    this.recTimerHandle = window.setInterval(() => {
      ($("rec-timer") as HTMLElement).textContent = formatDuration(
        this.recorder.elapsedMs(),
      );
    }, 250);
    this.updateActionMode();
  }

  /** Embedding em fila pra todos os áudios já transcritos que não têm vetor
   *  (ou estão num modelId antigo). Roda em background na inicialização;
   *  serial pra não saturar GPU/CPU em sessões com histórico grande. */
  private async embedBackfill(): Promise<void> {
    try {
      const list = await this.store.listAudiosNeedingEmbedding(EMBEDDING_MODEL_ID);
      if (list.length === 0) return;
      console.log(`[embeddings] backfill de ${list.length} áudios`);
      for (const m of list) {
        if (!m.audio || !m.text) continue;
        await this.embedAudioTranscript(m.id, m.text);
      }
      console.log("[embeddings] backfill concluído");
    } catch (err) {
      console.warn("[embeddings] backfill falhou", err);
    }
  }

  /** O bloco de ingest só existe no DOM com `wuwei.showIngest = "1"`
   *  (ver renderIngestSystemBubble). Sem ele, atualizar o log é invisível —
   *  e um re-render da lista inteira por linha de log é justamente o que
   *  fazia a conversa piscar durante a vetorização. */
  private ingestVisible(): boolean {
    try {
      return localStorage.getItem("wuwei.showIngest") === "1";
    } catch {
      return false;
    }
  }

  /** Apenda evento ao log de ingest da mensagem (timestamp incluso). */
  private logIngest(id: string, line: string): void {
    const i = this.messages.findIndex((x) => x.id === id);
    if (i < 0) return;
    const ts = new Date().toISOString().slice(11, 23); // HH:MM:SS.mmm
    if (!this.messages[i].ingestLog) this.messages[i].ingestLog = [];
    this.messages[i].ingestLog!.push(`${ts} ${line}`);
    if (this.ingestVisible()) this.renderMessages();
  }

  private setIngestStatus(id: string, status: ChatMessage["ingestStatus"]): void {
    const i = this.messages.findIndex((x) => x.id === id);
    if (i >= 0) {
      this.messages[i].ingestStatus = status;
      if (this.ingestVisible()) this.renderMessages();
    }
  }

  private formatErr(err: unknown): string {
    if (err instanceof Error) {
      const stackHead = (err.stack || "").split("\n").slice(0, 3).join(" | ");
      return `${err.name}: ${err.message}${stackHead ? " — " + stackHead : ""}`;
    }
    return String(err);
  }

  /** Gera embedding de texto puro de mensagem e persiste no top-level.
   *  Mesmo modelo usado em áudios, pra que a busca semântica unifique os
   *  espaços. Disparado fora do caminho crítico de envio/recepção. */
  private async embedMessageText(id: string, text: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) {
      this.logIngest(id, "skip: texto vazio");
      return;
    }
    this.setIngestStatus(id, "pending");
    this.logIngest(id, `start kind=text chars=${trimmed.length}`);
    try {
      const t0 = performance.now();
      const vec = await embedText(trimmed, "passage");
      const dt = Math.round(performance.now() - t0);
      this.logIngest(id, `embedText ok dim=${vec.length} ${dt}ms`);
      await this.store.setMessageEmbedding(id, vec, EMBEDDING_MODEL_ID);
      this.logIngest(id, `store.setMessageEmbedding ok model=${EMBEDDING_MODEL_ID}`);
      const i = this.messages.findIndex((x) => x.id === id);
      if (i >= 0) {
        this.messages[i].embedding = vec;
        this.messages[i].embeddingModelId = EMBEDDING_MODEL_ID;
        this.messages[i].ingestStatus = undefined;
        // Só o bloco de ingest reflete isso na tela.
        if (this.ingestVisible()) this.renderMessages();
      }
      console.log(`[embeddings] texto ${id.slice(0, 8)} → ${vec.length}d em ${dt}ms`);
    } catch (err) {
      console.warn("[embeddings] falha ao embedar texto", id, err);
      this.logIngest(id, `ERROR ${this.formatErr(err)}`);
      this.setIngestStatus(id, "error");
    }
  }

  /** Gera embedding do transcript em background e persiste junto com o áudio.
   *  Falhas são silenciosas — o backfill no próximo start() tenta de novo. */
  private async embedAudioTranscript(id: string, text: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) {
      this.logIngest(id, "skip: transcript vazio");
      return;
    }
    this.setIngestStatus(id, "pending");
    this.logIngest(id, `start kind=audio chars=${trimmed.length}`);
    try {
      const t0 = performance.now();
      const vec = await embedText(trimmed, "passage");
      const dt = Math.round(performance.now() - t0);
      this.logIngest(id, `embedText ok dim=${vec.length} ${dt}ms`);
      await this.store.setAudioEmbedding(id, vec, EMBEDDING_MODEL_ID);
      this.logIngest(id, `store.setAudioEmbedding ok model=${EMBEDDING_MODEL_ID}`);
      const i = this.messages.findIndex((x) => x.id === id);
      if (i >= 0 && this.messages[i].audio) {
        this.messages[i].audio!.embedding = vec;
        this.messages[i].audio!.embeddingModelId = EMBEDDING_MODEL_ID;
        this.messages[i].ingestStatus = undefined;
        if (this.ingestVisible()) this.renderMessages();
      }
      console.log(`[embeddings] áudio ${id.slice(0, 8)} → ${vec.length}d em ${dt}ms`);
    } catch (err) {
      console.warn("[embeddings] falha ao embedar", id, err);
      this.logIngest(id, `ERROR ${this.formatErr(err)}`);
      this.setIngestStatus(id, "error");
    }
  }

  /** Mime types pra disparar extract+embed automático. Cobre PDF + Office +
   *  ODF + EPUB + texto/HTML. Imagens NÃO entram aqui (o tool de extract até
   *  aceita, mas não gera texto útil — fica pra fase 2 com OCR). */
  private isExtractableAttachment(mime: string, name: string): boolean {
    if (mime === "application/pdf") return true;
    if (mime.startsWith("text/")) return true;
    if (mime === "application/epub+zip") return true;
    if (mime.startsWith("application/vnd.openxmlformats-officedocument.")) return true;
    if (mime.startsWith("application/vnd.oasis.opendocument.")) return true;
    // Fallback por extensão pra MIMEs vazios ou genéricos.
    const ext = name.split(".").pop()?.toLowerCase() ?? "";
    return ["pdf", "txt", "md", "html", "htm", "epub", "docx", "pptx", "xlsx", "odt", "odp", "ods"].includes(ext);
  }

  /**
   * Extrai texto página-a-página de um anexo (PDF/DOCX/...) via tool
   * `extract` do Agent, embeda cada página e persiste em
   * `attachment.pageEmbeddings`. Tudo em background; falhas viram log.
   */
  private async extractAndEmbedAttachment(msg: ChatMessage): Promise<void> {
    const att = msg.attachment;
    if (!att) return;
    const id = msg.id;
    // O blob vem do msg direto (não depende de this.messages estar populado);
    // assim funciona mesmo quando a mensagem chega numa conversa fechada.
    if (!this.isExtractableAttachment(att.mime, att.name)) {
      this.logIngest(id, `skip: anexo não-extraível (${att.mime || "?"})`);
      return;
    }

    if (!recursoAtivo("documentos")) {
      this.logIngest(id, "skip: leitura de documentos desligada");
      return;
    }

    this.setIngestStatus(id, "pending");
    this.logIngest(id, `start kind=attachment name=${att.name} mime=${att.mime || "?"}`);
    try {
      const pool = getPool();
      const tExtract = performance.now();
      const extractInvId = newInvocationId("extract");
      type ExtractedPage = { pageNumber: number; text: string };
      type ExtractOutput = { pages: ExtractedPage[]; format: string };
      const extracted = await pool.runTool<
        { file: Blob; fileName: string; withPageImages: boolean },
        ExtractOutput
      >(extractInvId, "extract", {
        file: att.blob,
        fileName: att.name,
        // Só queremos texto pra embedar — pula render de página (evita
        // pdfjs tentar `document.createElement` dentro do worker).
        withPageImages: false,
      });
      const dtExtract = Math.round(performance.now() - tExtract);
      this.logIngest(
        id,
        `extract ok pages=${extracted.pages.length} format=${extracted.format} ${dtExtract}ms`,
      );

      const pageVectors: AttachmentPageEmbedding[] = [];
      for (const page of extracted.pages) {
        const text = (page.text || "").trim();
        if (!text) {
          this.logIngest(id, `page ${page.pageNumber}: skip (vazia)`);
          continue;
        }
        const t0 = performance.now();
        const vec = await embedText(text, "passage");
        const dt = Math.round(performance.now() - t0);
        this.logIngest(
          id,
          `embed page ${page.pageNumber}/${extracted.pages.length} ok dim=${vec.length} chars=${text.length} ${dt}ms`,
        );
        pageVectors.push({
          pageNumber: page.pageNumber,
          vector: vec,
          charCount: text.length,
        });
      }

      await this.store.setAttachmentPageEmbeddings(id, pageVectors, EMBEDDING_MODEL_ID);
      this.logIngest(
        id,
        `store.setAttachmentPageEmbeddings ok pages=${pageVectors.length} model=${EMBEDDING_MODEL_ID}`,
      );

      const j = this.messages.findIndex((x) => x.id === id);
      if (j >= 0 && this.messages[j].attachment) {
        this.messages[j].attachment!.pageEmbeddings = pageVectors;
        this.messages[j].attachment!.embeddingModelId = EMBEDDING_MODEL_ID;
        this.messages[j].ingestStatus = undefined;
        if (this.ingestVisible()) this.renderMessages();
      }
      console.log(
        `[embeddings] anexo ${id.slice(0, 8)} ${att.name} → ${pageVectors.length} páginas indexadas`,
      );
    } catch (err) {
      console.warn("[embeddings] falha ao extrair/embedar anexo", id, err);
      this.logIngest(id, `ERROR ${this.formatErr(err)}`);
      this.setIngestStatus(id, "error");
    }
  }

  /** Concatena últimas mensagens textuais da conversa atual pra usar como
   *  prompt do Whisper. Ajuda com nomes próprios, jargões e estilo. Whisper
   *  aceita ~224 tokens de prompt; limita por caracteres como proxy seguro. */
  private buildTranscriptionContext(): string {
    const recent = this.messages
      .filter((m) => !m.deleted && m.text && !m.audio)
      .slice(-6)
      .map((m) => m.text.trim())
      .filter(Boolean)
      .join(" ");
    return recent.length > 800 ? recent.slice(-800) : recent;
  }

  private exitRecordingUI() {
    const ta = $("composer-input") as HTMLTextAreaElement;
    ta.style.display = "";
    ($("rec-indicator") as HTMLElement).hidden = true;
    ($("btn-rec-cancel") as HTMLButtonElement).hidden = true;
    if (this.recTimerHandle != null) {
      clearInterval(this.recTimerHandle);
      this.recTimerHandle = null;
    }
    this.updateActionMode();
  }

  private async stopRecording() {
    if (!this.recorder.isRecording()) return;
    let result;
    try {
      result = await this.recorder.stop();
    } finally {
      this.exitRecordingUI();
    }
    if (result.blob.size === 0 || !this.currentContact) return;
    void this.sendAudioMessage(result.blob, result.mime, result.durationMs, result.transcription);
  }

  private cancelRecording() {
    this.recorder.cancel();
    this.exitRecordingUI();
  }

  /** Envia o áudio AGORA, mostra "transcrevendo…", e atualiza quando vier. */
  private async sendAudioMessage(
    blob: Blob,
    mime: string,
    durationMs: number,
    transcription: Promise<{ text: string; lang: string }>,
  ) {
    if (!this.currentContact) return;
    const id = crypto.randomUUID();
    const ts = Date.now();
    const audio: AudioStored = { blob, mime, durationMs, lang: "" };
    const msg: ChatMessage = {
      id,
      contact: this.currentContact,
      direction: "out",
      text: "",
      ts,
      status: "pending",
      audio,
      transcribing: AudioRecorder.speechSupported(),
    };
    await this.store.putMessage(msg);
    this.messages.push(msg);
    this.renderMessages();
    this.contacts = await this.store.listContacts();
    this.renderContacts();

    const audioWire: AudioWire = {
      mime,
      data: await blobToBase64(blob),
      durationMs,
      lang: "",
    };
    await this.enviarSend({
      type: "send",
      id,
      to: this.currentContact,
      text: "",
      audio: audioWire,
      transcribing: msg.transcribing,
    });

    if (!msg.transcribing) return;
    // Teto de tempo no CONSUMIDOR, não só no produtor. A promise vem de fora
    // (Web Speech, worker do Whisper) e qualquer uma delas pode simplesmente
    // nunca resolver — no celular basta o sistema suspender o temporizador de
    // dentro. Sem isto, a bolha fica em "transcrevendo…" para sempre, que foi
    // o defeito relatado. Com isto, o pior caso é 25s e um áudio sem texto.
    // 3 minutos, não 25 segundos: na primeira vez o Whisper precisa BAIXAR
    // ~150 MB de modelo antes de transcrever qualquer coisa. Medi que 25s
    // cortava enquanto o worker ainda estava carregando — o teto existia para
    // evitar o estado preso, e acabou virando ele próprio a causa da falha.
    const comTeto = Promise.race([
      transcription,
      new Promise<{ text: string; lang: string }>((r) =>
        setTimeout(() => r({ text: "", lang: "" }), 180000),
      ),
    ]);
    comTeto
      .then(async ({ text, lang }) => {
        msg.text = text;
        if (msg.audio) msg.audio.lang = lang;
        msg.transcribing = false;
        await this.store.updateTranscript(id, text, lang);
        this.renderMessages();
        this.contacts = await this.store.listContacts();
        this.renderContacts();
        this.conn.send({
          type: "transcript",
          id,
          to: this.currentContact!,
          text,
          lang,
        });
        if (text) void this.embedAudioTranscript(id, text);
      })
      .catch(() => {
        msg.transcribing = false;
        void this.store.updateTranscript(id, "", "");
        this.renderMessages();
      });
  }

  private setStatus(s: ConnState) {
    this.status = s;
    const dot = $("conn-dot");
    const label = $("conn-label");
    dot.className = "conn-dot conn-" + s;
    label.textContent =
      s === "open" ? "online" : s === "connecting" ? "conectando..." : "desconectado";
    // NÃO limpamos onlineUsers aqui. Quedas momentâneas de WS são frequentes
    // e a reconexão é imediata; o servidor reenvia a lista completa de
    // presence no novo handshake. Limpar agora causaria pisca-pisca nos
    // avatares dos contatos a cada hiccup de rede.
  }

  private async handleWire(m: WireIn) {
    if (m.type === "ready") {
      // Publicar aqui, e não no login: `ready` acontece a cada reconexão, e
      // republicar é idempotente. Aparelho que perdeu o IndexedDB gera par
      // novo, e sem republicar continuaria recebendo mensagens cifradas para
      // uma chave que ele não tem mais.
      void publicarChave(this.sess.session).then((ok) => this.mostrarAvisoE2E(ok));
      void this.resendPendingOutgoing();
      // A fila das outras ações vem depois das mensagens: reagir a uma
      // mensagem que o servidor ainda não recebeu não teria a que se aplicar.
      void this.drenarFila();
      this.republishStatus();
      // Pede ao servidor a lista de contatos+grupos derivada do histórico
      // — em devices novos isso reconstrói a sidebar sem o user precisar
      // abrir cada conversa primeiro.
      this.conn.send({ type: "sync_request" });
    } else if (m.type === "conv_hide") {
      // Outro aparelho da conta apagou esta conversa. Faz aqui o mesmo que o
      // botão faria — sem reenviar o frame, senão os devices ecoam em círculo.
      await this.esconderConversaLocal(m.com);
    } else if (m.type === "sync") {
      // Reconstrói sidebar + dispara catch-up por contato: pra cada
      // conversa onde o servidor tem `lastTs` maior que o nosso local,
      // pede tudo que entrou desde então. Cobre devices offline por dias.
      const localBefore = await this.store.listContacts();
      const localLastTs = new Map<string, number>();
      for (const lc of localBefore) localLastTs.set(lc.email, lc.lastTs ?? 0);
      // Ocultas da CONTA: é o que faz um aparelho novo nascer com a mesma
      // sidebar dos outros. Guarda o instante, não um sim/não — conversa com
      // mensagem posterior ao ocultamento volta sozinha, aqui e no botão.
      const ocultas = new Map<string, number>();
      for (const h of m.hidden ?? []) ocultas.set(h.com, h.desdeTs);
      let touched = false;
      for (const c of m.contacts) {
        const desde = ocultas.get(c.contact);
        if (desde !== undefined) {
          if (c.lastTs <= desde) {
            await this.esconderConversaLocal(c.contact);
            continue;
          }
          // Chegou coisa nova depois do apagar: a conversa reabre.
          this.marcarApagada(c.contact, false);
        }
        // Apagada neste aparelho: não ressuscita no sync.
        if (this.apagadas.has(c.contact)) continue;
        await this.store.upsertContactSummary(
          c.contact,
          c.lastTs,
          c.lastText ?? "",
        );
        // Garante que o hash desse contato esteja no map em memória.
        await this.hashContact(c.contact);
        touched = true;
        const localTs = localLastTs.get(c.contact) ?? 0;
        if (c.lastTs > localTs) {
          // Pede tudo que entrou desde nossa última mensagem nesse contato.
          this.conn.send({
            type: "history_request",
            contact: c.contact,
            after: localTs,
            limit: 500,
          });
        }
      }
      if (touched) {
        this.contacts = await this.store.listContacts();
        this.renderContacts();
        // Se a URL atual é /r/<hash> que não estava no map antes, agora
        // pode estar — tenta abrir.
        if (!this.currentContact) {
          const fromPath = this.pathToContact(location.pathname);
          if (fromPath) void this.openContact(fromPath);
        }
      }
    } else if (m.type === "error") {
      if (m.reason === "auth") {
        clearSession();
        location.reload();
      }
    } else if (m.type === "relay") {
      // Abrir a caixa ANTES de qualquer coisa que leia o texto: indexação para
      // busca, prévia na lista de conversas e notificação usam `m.text`, e
      // todas mostrariam o conteúdo cifrado se rodassem primeiro.
      if (typeof m.text === "string" && m.text.startsWith(ChatApp.MARCA_E2E)) {
        m.text = await this.abrirCaixa(m.text, m.from.toLowerCase());
      }
      if (m.audio?.data?.startsWith(ChatApp.MARCA_E2E)) {
        m.audio.data = await this.abrirBinario(m.audio.data, m.from.toLowerCase());
      }
      if (m.attachment?.data?.startsWith(ChatApp.MARCA_E2E)) {
        m.attachment.data = await this.abrirBinario(m.attachment.data, m.from.toLowerCase());
      }
      const isGroup = m.to.startsWith("g:");
      const fromEmail = m.from.toLowerCase();
      // Eco multi-device: quando o servidor reflete uma mensagem que o
      // PRÓPRIO usuário enviou de outro device, fromEmail === sess.email.
      // Nesse caso a mensagem é "out", não "in", e o contact é o destino.
      const isOwnEcho = fromEmail === this.sess.email;
      const contact = isGroup
        ? m.to
        : isOwnEcho
          ? m.to.toLowerCase()
          : fromEmail;
      // Mensagem nova reabre a conversa apagada — apagar não é bloquear.
      // Precisa ser o CONTATO da conversa, não o remetente: em grupo, quem
      // fala é uma pessoa e a conversa é o grupo. Desmarcar o remetente
      // deixava o grupo apagado para sempre, invisível mesmo recebendo
      // mensagem.
      this.marcarApagada(contact, false);
      const direction: Direction = isOwnEcho ? "out" : "in";
      let audio: AudioStored | undefined;
      if (m.audio && m.audio.data) {
        audio = {
          blob: base64ToBlob(m.audio.data, m.audio.mime),
          mime: m.audio.mime,
          durationMs: m.audio.durationMs,
          lang: m.audio.lang,
        };
      }
      let attachment: AttachmentStored | undefined;
      if (m.attachment && m.attachment.data) {
        attachment = {
          name: m.attachment.name,
          mime: m.attachment.mime,
          blob: base64ToBlob(m.attachment.data, m.attachment.mime),
        };
      }
      const incoming: ChatMessage = {
        id: m.id,
        contact,
        direction,
        text: m.text,
        ts: m.ts,
        fromEmail,
        // Eco do próprio usuário já foi entregue ao(s) destinatário(s)
        // por outro device — chega aqui como "sent" do ponto de vista deste.
        ...(isOwnEcho ? { status: "sent" as const } : {}),
        ...(audio ? { audio } : {}),
        ...(attachment ? { attachment } : {}),
        ...(m.transcribing ? { transcribing: true } : {}),
        ...(m.quoteOf ? { quoteOf: m.quoteOf } : {}),
        ...(m.forwardedFrom ? { forwardedFrom: m.forwardedFrom } : {}),
      };
      const isNew = await this.store.putMessage(incoming);
      // ACK só pra mensagens recebidas de outros — eco próprio não precisa.
      if (!isOwnEcho) this.conn.send({ type: "ack", id: m.id });
      if (!isNew) return;
      // Mensagens com áudio são embedadas no fluxo de transcript; texto puro
      // (e mensagens com anexo + texto) entra aqui mesmo.
      if (m.text && !audio) void this.embedMessageText(m.id, m.text);
      if (attachment) void this.extractAndEmbedAttachment(incoming);
      this.contacts = await this.store.listContacts();
      this.renderContacts();
      // Notificação se tab oculta ou outra conversa aberta
      if (document.hidden || this.currentContact !== contact) {
        this.maybeNotify(contact, m.text, !!audio, !!attachment);
      }
      if (this.currentContact === contact) {
        this.messages.push(incoming);
        await this.store.clearUnread(contact);
        this.contacts = await this.store.listContacts();
        this.renderContacts();
        this.renderMessages();
        // Conversa aberta → marca como lida e avisa o remetente
        if (!incoming.readSent) {
          incoming.readSent = true;
          await this.store.markReadSent(m.id);
          void this.enviarOuEnfileirar({ type: "read", id: m.id, to: contact }, `read:${m.id}`);
        }
      }
    } else if (m.type === "transcript") {
      // Patch assíncrono de transcrição vindo do remetente
      await this.store.updateTranscript(m.id, m.text, m.lang);
      const i = this.messages.findIndex((x) => x.id === m.id);
      if (i >= 0) {
        this.messages[i].text = m.text;
        if (this.messages[i].audio) this.messages[i].audio!.lang = m.lang;
        this.messages[i].transcribing = false;
        this.renderMessages();
      }
      this.contacts = await this.store.listContacts();
      this.renderContacts();
      if (m.text) void this.embedAudioTranscript(m.id, m.text);
    } else if (m.type === "sent") {
      await this.store.updateMessageStatus(m.id, "sent");
      this.applyStatus(m.id, "sent", m.ts);
    } else if (m.type === "delivered") {
      await this.store.updateMessageStatus(m.id, "delivered");
      this.applyStatus(m.id, "delivered");
    } else if (m.type === "read") {
      await this.store.updateMessageStatus(m.id, "read");
      this.applyStatus(m.id, "read");
    } else if (m.type === "presence") {
      this.onlineUsers = new Set(m.users.filter((e) => e !== this.sess.email));
      this.renderContacts();
      this.renderChatHeaderPresence();
    } else if (m.type === "presence_event") {
      if (m.email === this.sess.email) return;
      if (m.event === "join") this.onlineUsers.add(m.email);
      else {
        this.onlineUsers.delete(m.email);
        // se ele saiu enquanto digitava, limpa indicador
        this.typingFromSet.delete(m.email);
      }
      this.renderContacts();
      if (m.email === this.currentContact) this.renderChatHeaderPresence();
    } else if (m.type === "typing") {
      if (m.from === this.sess.email) return;
      if (m.on) this.typingFromSet.add(m.from);
      else this.typingFromSet.delete(m.from);
      this.renderContacts();
      if (m.from === this.currentContact) this.renderChatHeaderPresence();
    } else if (m.type === "edit") {
      const editedTs = Date.now();
      await this.store.editMessageText(m.id, m.text, editedTs);
      const i = this.messages.findIndex((x) => x.id === m.id);
      if (i >= 0) {
        this.messages[i].text = m.text;
        this.messages[i].editedTs = editedTs;
        this.renderMessages();
      }
      this.contacts = await this.store.listContacts();
      this.renderContacts();
    } else if (m.type === "delete") {
      const updated = await this.store.markDeleted(m.id);
      if (updated) {
        const i = this.messages.findIndex((x) => x.id === m.id);
        if (i >= 0) {
          this.messages[i] = updated;
          this.renderMessages();
        }
        this.contacts = await this.store.listContacts();
        this.renderContacts();
      }
    } else if (m.type === "react") {
      // Reactions vêm para o destinatário OU para o remetente (se outra
      // identidade reagir à mensagem dele). O msg.id existe localmente
      // porque ambos têm cópia da bolha.
      await this.applyIncomingReaction(m.id, m.actor, m.emoji, m.op);
    } else if (m.type === "statuses") {
      this.statusByEmail.clear();
      for (const [em, st] of Object.entries(m.users)) {
        if (st) this.statusByEmail.set(em, st as UserStatus);
      }
      this.renderContacts();
      this.renderChatHeaderPresence();
    } else if (m.type === "files") {
      // Só desenha se a aba ainda estiver aberta: uma resposta que chega
      // depois de o usuário voltar para as conversas não pode reabrir a lista.
      if (this.contactFilter === "files") {
        this.renderArquivos(((m as unknown as { files?: ArquivoListado[] }).files ?? []));
      }
    } else if (m.type === "history") {
      // Resposta a um history_request: persiste no IDB + atualiza UI
      const me = this.sess.email;
      let added = 0;
      for (const wm of m.messages) {
        const isGroup = wm.to.startsWith("g:");
        const direction: "in" | "out" = wm.from.toLowerCase() === me ? "out" : "in";
        const contact = isGroup ? wm.to : (direction === "out" ? wm.to : wm.from.toLowerCase());
        let audio: AudioStored | undefined;
        if (wm.audio && wm.audio.data) {
          audio = {
            blob: base64ToBlob(wm.audio.data, wm.audio.mime),
            mime: wm.audio.mime,
            durationMs: wm.audio.durationMs,
            lang: wm.audio.lang,
          };
        }
        let attachment: AttachmentStored | undefined;
        if (wm.attachment && wm.attachment.data) {
          attachment = {
            name: wm.attachment.name,
            mime: wm.attachment.mime,
            blob: base64ToBlob(wm.attachment.data, wm.attachment.mime),
          };
        }
        const cm: ChatMessage = {
          id: wm.id,
          contact,
          direction,
          text: wm.text,
          ts: wm.ts,
          fromEmail: wm.from.toLowerCase(),
          ...(direction === "out" ? { status: "delivered" as MessageStatus } : {}),
          ...(audio ? { audio } : {}),
          ...(attachment ? { attachment } : {}),
          ...(wm.quoteOf ? { quoteOf: wm.quoteOf } : {}),
          ...(wm.forwardedFrom ? { forwardedFrom: wm.forwardedFrom } : {}),
        };
        const isNew = await this.store.putMessage(cm);
        if (isNew) added++;
      }
      if (added > 0) {
        this.contacts = await this.store.listContacts();
        if (this.currentContact === m.contact) {
          // Recarrega a página atual
          const page = await this.store.listMessagesPage(m.contact, { limit: this.pageLimit });
          this.messages = page;
          this.hasMoreOlder = page.length === this.pageLimit;
          this.renderMessages();
        }
        this.renderContacts();
      }
    } else if (m.type === "groups_list") {
      for (const g of m.groups) {
        this.groups.set(g.id, g);
        await this.store.putGroup(g);
        await this.store.ensureContact(g.id);
        await this.hashContact(g.id);
      }
      this.contacts = await this.store.listContacts();
      this.renderContacts();
      if (!this.currentContact) {
        const fromPath = this.pathToContact(location.pathname);
        if (fromPath) void this.openContact(fromPath);
      }
    } else if (m.type === "group_created") {
      this.groups.set(m.group.id, m.group);
      await this.store.putGroup(m.group);
      await this.store.ensureContact(m.group.id);
      this.contacts = await this.store.listContacts();
      this.renderContacts();
    } else if (m.type === "group_removed") {
      // Fui removido do grupo: sai da lista e fecha a conversa se estiver aberta.
      this.groups.delete(m.id);
      this.marcarApagada(m.id, true);
      this.contacts = this.contacts.filter((c) => c.email !== m.id);
      if (this.currentContact === m.id) {
        this.currentContact = null;
        this.messages = [];
        this.showDashboardView();
      }
      this.renderContacts();
      this.mostrarAviso("você saiu de um grupo");
    } else if (m.type === "status_event") {
      if (m.email === this.sess.email) return; // já refletido localmente
      if (m.status) this.statusByEmail.set(m.email, m.status);
      else this.statusByEmail.delete(m.email);
      this.renderContacts();
      if (m.email === this.currentContact) this.renderChatHeaderPresence();
    }
  }

  private async handleSend() {
    if (!this.currentContact) return;
    const ta = $("composer-input") as HTMLTextAreaElement;
    const text = ta.value.trim();
    const att = this.pendingAttachment;
    if (!text && !att) return;

    // "@ia ..." não é mensagem: é uma pergunta ao modelo do aparelho, feita de
    // dentro desta conversa. Nada disso vira mensagem para o outro lado — a
    // resposta chega como rascunho, e só vai embora se você mandar.
    if (this.modeloDaMencao && /^@ia\b/i.test(text)) {
      const pergunta = text.replace(/^@ia\b\s*/i, "");
      if (!pergunta) return;
      ta.value = "";
      ta.style.height = "auto";
      this.pintarComposer();
      this.updateActionMode();
      void this.perguntarAoModeloAqui(this.modeloDaMencao, pergunta);
      return;
    }

    const id = crypto.randomUUID();
    const ts = Date.now();
    const quote = this.composerQuote;
    const msg: ChatMessage = {
      id,
      contact: this.currentContact,
      direction: "out",
      text,
      ts,
      status: "pending",
      fromEmail: this.sess.email,
      ...(att ? { attachment: att } : {}),
      ...(quote ? { quoteOf: quote } : {}),
    };
    await this.store.putMessage(msg);
    this.messages.push(msg);
    ta.value = "";
    ta.style.height = "auto";
    this.discardAttachment();
    this.clearComposerQuote();
    this.sendTypingStop();
    this.renderMessages();
    this.contacts = await this.store.listContacts();
    this.renderContacts();

    // Conversa com modelo local não passa por rede.
    //
    // Este `return` é a linha mais importante do recurso: sem ele a mensagem
    // seguiria por `enviarSend` e o servidor veria o texto de uma conversa que
    // foi oferecida como privada. O modelo mora no aparelho justamente para
    // isso — a promessa se cumpre aqui ou não se cumpre em lugar nenhum.
    if (iaContato.ehContatoLocal(msg.contact)) {
      msg.status = "sent";
      await this.store.putMessage(msg);
      this.renderMessages();
      this.updateActionMode();
      void this.responderComIaLocal(msg.contact);
      return;
    }

    let attWire: AttachmentWire | undefined;
    if (att) {
      attWire = {
        name: att.name,
        mime: att.mime,
        data: await blobToBase64(att.blob),
      };
    }
    await this.enviarSend({
      type: "send",
      id,
      to: this.currentContact,
      text,
      ...(attWire ? { attachment: attWire } : {}),
      ...(quote ? { quoteOf: quote } : {}),
    });
    if (text) void this.embedMessageText(id, text);
    if (att) void this.extractAndEmbedAttachment(msg);
    this.updateActionMode();
  }

  /**
   * Gera a resposta do modelo local e a escreve na conversa, token a token.
   *
   * A bolha nasce vazia e cresce. É de propósito: a alternativa — esperar a
   * resposta inteira e só então mostrá-la — deixa a tela parada por dezenas de
   * segundos num aparelho lento, e tela parada é indistinguível de app travado.
   * Aqui a primeira palavra chega em poucos segundos e o resto vem escrevendo.
   */
  private async responderComIaLocal(contato: string) {
    const arquivo = iaContato.arquivoDoContato(contato);
    const resposta: ChatMessage = {
      id: crypto.randomUUID(),
      contact: contato,
      direction: "in",
      text: "",
      ts: Date.now(),
      fromEmail: contato,
    };
    this.iaGerando = contato;
    this.messages.push(resposta);
    this.renderMessages();
    this.renderContacts();

    // O histórico vai SEM a bolha vazia que acabou de nascer: mandá-la faria o
    // prompt terminar com um turno de assistente em branco, e modelo pequeno
    // responde a isso continuando o vazio.
    const historico = this.messages.filter((m) => m.contact === contato && m.id !== resposta.id);

    // A bolha entra no disco JÁ, vazia.
    //
    // Ela só era gravada quando a geração terminava, e isso significava que o
    // texto crescendo na tela não existia em lugar nenhum: fechar o app no
    // meio apagava uma resposta que a pessoa tinha acabado de ler. Gravar
    // agora, e atualizar enquanto escreve, faz o que está na tela ser o que
    // fica — que é o mínimo que uma conversa promete.
    await this.store.putMessage(resposta);

    // Salvar a cada token seria uma escrita em disco por letra. Um segundo é
    // curto para a pessoa e longo para o IndexedDB.
    let ultimaGravacao = 0;
    const comecouEm = Date.now();
    try {
      const { texto, prefill } = await iaContato.responder(
        arquivo,
        historico,
        (a) => {
        if (a.fase !== "gerando") {
          this.escreverNaBolha(resposta.id, iaContato.textoDaFase(a.fase, a.texto));
          return;
        }
        resposta.text = a.texto;
        this.escreverNaBolha(resposta.id, a.texto);
        const agora = Date.now();
        if (agora - ultimaGravacao > 1000) {
          ultimaGravacao = agora;
          void this.store.atualizarTexto(resposta.id, a.texto);
        }
        },
        "conversa",
        contato,
      );
      resposta.text = texto;
      // Quanto ele levou, dito no fim e em português comum. Durante a espera o
      // relógio anda; terminado o trabalho, o mesmo número vira uma frase que
      // explica o que aconteceu — e some da conversa, porque a conversa é da
      // pessoa e não do diagnóstico.
      this.avisoDaIngestao(`pensou por ${iaContato.relogio(Date.now() - comecouEm)}`);
      window.setTimeout(() => this.avisoDaIngestao(null), 6000);
      console.log("[ia] geracao terminou", resposta.id.slice(0, 8), texto.length, "chars");
      // Quanto do prompt teve de ser lido de novo nesta pergunta.
      //
      // Vai para o registro, e não para a conversa: é número de diagnóstico,
      // e a conversa é da pessoa. Sem ele, "está demorando" e "releu tudo" são
      // a mesma frase — e exigem consertos diferentes.
      if (prefill) {
        const leitura = leituraDoPrompt(prefill);
        registro.anotar("ia-contato", `leu ${leitura} do prompt (aproveitado/total: ${prefill})`);
      }
    } catch (e) {
      // O erro vira mensagem na conversa, e não um alerta que some. Quem pediu
      // a resposta precisa ver ali por que ela não veio — inclusive amanhã,
      // rolando a conversa para cima.
      resposta.text = e instanceof Error ? e.message : String(e);
      registro.anotar("ia-contato", resposta.text, "erro");
    } finally {
      this.iaGerando = null;
      // Gravar é o passo que não pode falhar em silêncio.
      //
      // No aparelho, a resposta aparecia na tela e sumia ao reabrir o app —
      // enquanto no navegador o mesmo caminho persistia. Sem registrar aqui,
      // não há como distinguir "não gravou" de "gravou e o WebView perdeu", e
      // as duas exigem consertos diferentes.
      try {
        await this.store.atualizarTexto(resposta.id, resposta.text);
        registro.anotar("ia-contato", `gravei a resposta ${resposta.id.slice(0, 8)} (${resposta.text.length} chars)`);
      } catch (e) {
        registro.anotar(
          "ia-contato",
          `FALHOU ao gravar a resposta: ${e instanceof Error ? e.message : String(e)}`,
          "erro",
        );
      }
      // `putMessage` conta toda mensagem que entra como não lida. Aqui ela
      // nasceu na tela de quem estava olhando — deixar o contador subir poria
      // um "1" vermelho na conversa que a pessoa acabou de ler.
      if (this.currentContact === contato) await this.store.clearUnread(contato);
      this.contacts = await this.store.listContacts();
      this.renderMessages();
      this.renderContacts();
    }
  }

  /**
   * Pergunta ao modelo do aparelho de dentro de outra conversa.
   *
   * A resposta NÃO entra na conversa: aparece num rascunho que só você vê, com
   * enviar, copiar e descartar. Essa é a diferença entre um assistente e um
   * intruso — o modelo lê o que está na tela e opina, mas quem fala é você.
   *
   * O contexto vem das últimas mensagens desta conversa, lidas do aparelho. Vale
   * dizer o que isso significa: o modelo lê o que a outra pessoa escreveu. Só é
   * defensável porque a leitura acontece dentro do telefone e nada é enviado —
   * com um modelo de nuvem, isto seria repassar a conversa alheia a um terceiro.
   */
  private async perguntarAoModeloAqui(arquivo: string, pergunta: string) {
    const conversa = this.currentContact;
    if (!conversa) return;
    const painel = this.abrirRascunhoIa();
    const corpo = painel.querySelector<HTMLElement>(".rascunho-texto")!;
    // Nasce dizendo o que é verdade agora. Se houver carga, a própria geração
    // avisa e a frase muda; se não houver, ninguém foi informado de uma espera
    // que não existe.
    corpo.textContent = "lendo a conversa…";

    // O histórico da conversa vira contexto, com a pergunta no fim. As mensagens
    // recebidas entram como "assistant" e as suas como "user" só porque é o
    // formato que o motor espera; o que importa é a ordem.
    const historico: ChatMessage[] = [
      ...this.messages.slice(-8),
      {
        id: "pergunta",
        contact: conversa,
        direction: "out",
        text: pergunta,
        ts: Date.now(),
      },
    ];

    try {
      const comecouEm = Date.now();
      const { texto } = await iaContato.responder(
        arquivo,
        historico,
        (a) => {
          corpo.textContent =
            a.fase === "gerando" ? a.texto : iaContato.textoDaFase(a.fase, a.texto);
        },
        "conversa",
        conversa,
      );
      corpo.textContent = texto;
      this.marcarQuantoPensou(painel, Date.now() - comecouEm);
      painel.querySelector<HTMLButtonElement>(".rascunho-enviar")!.disabled = false;
    } catch (e) {
      corpo.textContent = e instanceof Error ? e.message : String(e);
      registro.anotar("ia-mencao", corpo.textContent, "erro");
    }
  }

  /** "pensou por 42s" abaixo da resposta.
   *
   *  Quem esperou dois minutos merece ver o número no fim, e não só enquanto
   *  espera: é o que separa "o aparelho é lento" de "esta pergunta era cara". */
  private marcarQuantoPensou(painel: HTMLElement, ms: number) {
    let selo = painel.querySelector<HTMLElement>(".rascunho-tempo");
    if (!selo) {
      selo = document.createElement("div");
      selo.className = "rascunho-tempo";
      painel.querySelector(".rascunho-texto")?.after(selo);
    }
    selo.textContent = `pensou por ${iaContato.relogio(ms)}`;
  }

  /** O cartão do rascunho, criado sob demanda acima do campo de texto. */
  private abrirRascunhoIa(): HTMLElement {
    this.fecharRascunhoIa();
    const painel = document.createElement("div");
    painel.id = "rascunho-ia";
    painel.className = "rascunho-ia";
    painel.innerHTML =
      '<div class="rascunho-topo">◆ resposta do modelo no aparelho · só você vê</div>' +
      '<div class="rascunho-texto"></div>' +
      '<div class="rascunho-acoes">' +
      '<button type="button" class="rascunho-enviar" disabled>enviar na conversa</button>' +
      '<button type="button" class="rascunho-copiar">copiar</button>' +
      '<button type="button" class="rascunho-descartar">descartar</button>' +
      "</div>";
    const campo = document.getElementById("composer-input");
    const barra = campo?.closest("form") ?? campo?.parentElement;
    barra?.parentElement?.insertBefore(painel, barra);

    const texto = () => painel.querySelector<HTMLElement>(".rascunho-texto")!.textContent ?? "";
    painel.querySelector(".rascunho-enviar")!.addEventListener("click", () => {
      const ta = document.getElementById("composer-input") as HTMLTextAreaElement | null;
      if (ta) {
        // Vai para o CAMPO, não direto para a conversa. Um botão que publica
        // texto de modelo sem passar pelos seus olhos é o jeito mais rápido de
        // alguém mandar, em nome próprio, uma frase que não leu.
        ta.value = texto();
        ta.focus();
        this.updateActionMode();
        this.pintarComposer();
      }
      this.fecharRascunhoIa();
    });
    painel.querySelector(".rascunho-copiar")!.addEventListener("click", () => {
      void navigator.clipboard?.writeText(texto());
    });
    painel.querySelector(".rascunho-descartar")!.addEventListener("click", () => {
      iaContato.parar();
      this.fecharRascunhoIa();
    });
    return painel;
  }

  private fecharRascunhoIa() {
    document.getElementById("rascunho-ia")?.remove();
    this.modeloDaMencao = null;
  }

  /** Escreve texto na bolha sem reconstruir a conversa.
   *
   *  Mesmo motivo medido em `atualizarUltimoTurno`: redesenhar tudo a cada
   *  token faz o custo por token crescer com o tamanho do histórico, que é a
   *  assinatura exata de "trava depois que eu começo a conversar". */
  private escreverNaBolha(msgId: string, texto: string) {
    const el = document.querySelector<HTMLElement>(
      `#messages .bubble[data-msg-id="${CSS.escape(msgId)}"] .bubble-text`,
    );
    if (!el) return;
    if (el.firstChild && el.firstChild.nodeType === Node.TEXT_NODE) {
      el.firstChild.nodeValue = texto;
    } else {
      el.textContent = texto;
    }
    const cont = document.getElementById("messages");
    if (cont) cont.scrollTop = cont.scrollHeight;
  }

  // ---------- New conversation (email) modal ----------

  private wireNewConvModal() {
    const modal = document.getElementById("new-conv-modal");
    const input = document.getElementById("new-conv-email") as HTMLInputElement | null;
    const newConvBtn = document.getElementById("new-conv-btn");
    const closeBtn = document.getElementById("new-conv-close");
    const cancelBtn = document.getElementById("new-conv-cancel");
    const confirmBtn = document.getElementById("new-conv-confirm");
    const open = () => {
      modal?.classList.remove("hidden");
      if (input) {
        input.value = "";
        input.focus();
      }
    };
    const close = () => modal?.classList.add("hidden");
    newConvBtn?.addEventListener("click", open);
    closeBtn?.addEventListener("click", close);
    cancelBtn?.addEventListener("click", close);
    modal?.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).id === "new-conv-modal") close();
    });
    const submit = async () => {
      const email = input?.value.trim().toLowerCase() || "";
      if (!email || !email.includes("@") || email === this.sess.email) {
        input?.focus();
        return;
      }
      close();
      await this.store.ensureContact(email);
      this.contacts = await this.store.listContacts();
      this.renderContacts();
      void this.openContact(email);
    };
    confirmBtn?.addEventListener("click", () => void submit());
    input?.addEventListener("keydown", (e) => {
      if (e.key === "Enter") void submit();
      else if (e.key === "Escape") close();
    });
  }

  // ---------- Dashboard buttons ----------

  private wireDashboardActions() {
    document.getElementById("qa-new-conv")?.addEventListener("click", () => {
      document.getElementById("new-conv-modal")?.classList.remove("hidden");
      (document.getElementById("new-conv-email") as HTMLInputElement | null)?.focus();
    });
    document.getElementById("qa-new-group")?.addEventListener("click", () => this.openGroupModal());
    document.getElementById("qa-invite")?.addEventListener("click", () => void this.openInviteModal());
    // Brand: volta pro dashboard
    document.getElementById("brand-link")?.addEventListener("click", (e) => {
      e.preventDefault();
      this.currentContact = null;
      this.showDashboardView();
    });
    // Back button no chat-header (mobile)
    document.getElementById("chat-back-btn")?.addEventListener("click", () => {
      this.currentContact = null;
      this.showDashboardView();
    });
    // Cmd+K / Cmd+N atalhos
    document.addEventListener("keydown", (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      if (e.key === "k") {
        e.preventDefault();
        (document.getElementById("search-input") as HTMLInputElement | null)?.focus();
      } else if (e.key === "n") {
        e.preventDefault();
        document.getElementById("new-conv-modal")?.classList.remove("hidden");
        (document.getElementById("new-conv-email") as HTMLInputElement | null)?.focus();
      }
    });
  }

  // ---------- Sidebar tabs ----------

  private wireSidebarTabs() {
    const tabs = document.querySelectorAll<HTMLButtonElement>(".sidebar-tabs .tab");
    tabs.forEach((tab) => {
      tab.onclick = () => {
        const v = (tab.dataset.tab || "all") as "all" | "unread" | "groups" | "files";
        this.contactFilter = v;
        tabs.forEach((t) => t.classList.toggle("is-active", t === tab));
        const arquivos = v === "files";
        $("files-panel").classList.toggle("hidden", !arquivos);
        $("contact-list").classList.toggle("hidden", arquivos);
        if (arquivos) this.pedirArquivos();
        else this.renderContacts();
      };
    });

    for (const id of ["files-kind", "files-dir"]) {
      const sel = document.getElementById(id) as HTMLSelectElement | null;
      // `change` e não `input`: o filtro dispara uma consulta ao servidor, e
      // com `input` um teclado navegando pelas opções dispararia uma por item.
      if (sel) sel.onchange = () => this.pedirArquivos();
    }
  }

  /** Pede a lista de anexos ao servidor. Metadado só — os bytes ficam lá. */
  private pedirArquivos() {
    const lista = $("files-list");
    lista.innerHTML = '<div class="empty-hint">carregando…</div>';
    this.conn.send({
      type: "files_request",
      contact: "",
      kind: (document.getElementById("files-kind") as HTMLSelectElement | null)?.value ?? "",
      de: (document.getElementById("files-dir") as HTMLSelectElement | null)?.value ?? "",
      limit: 200,
    });
  }

  private renderArquivos(arquivos: ArquivoListado[]) {
    const lista = $("files-list");
    if (!arquivos.length) {
      lista.innerHTML = '<div class="empty-hint">Nenhum arquivo com esse filtro.</div>';
      return;
    }
    const me = this.sess.email;
    // Mesmo divisor da conversa: a lista vem ordenada por tempo, então basta
    // marcar onde o período vira. Sem ele, 200 arquivos são uma coluna sem
    // referência — e a data de cada linha, sozinha, obriga a ler uma por uma.
    let periodoAnterior = "";
    lista.innerHTML = arquivos
      .map((a) => {
        const chave = chavePeriodo(a.ts);
        const divisor = chave === periodoAnterior ? "" : this.divisorPeriodo(a.ts);
        periodoAnterior = chave;
        return divisor + this.renderLinhaArquivo(a, me);
      })
      .join("");
    this.ativarLinhaDoTempo(lista, ".file-row[data-ts]");
    lista.querySelectorAll<HTMLButtonElement>(".file-row").forEach((b) => {
      // Abrir a mensagem, e não baixar aqui: o arquivo faz sentido junto do que
      // foi dito com ele, e é lá que o app já sabe mostrar e salvar anexo.
      b.onclick = () =>
        void this.openContact(b.dataset.conversa!, { targetMsgId: b.dataset.id! });
    });
  }

  /** O divisor de período, igual ao da conversa — mesma função, mesmo texto. */
  private divisorPeriodo(ts: number): string {
    return (
      `<div class="day-divider"><span class="day-line"></span>` +
      `<span class="day-pill">${escapeHtml(periodoLabel(ts))}</span>` +
      `<span class="day-line"></span></div>`
    );
  }

  /** Monta a lista de conversas com divisores de período.
   *
   *  Tem uma torção que vale explicar: a lista põe **quem está online
   *  primeiro**, e isso quebra a ordem do tempo — um divisor de data no meio
   *  de gente ordenada por presença apontaria para a data errada. Então os
   *  online saem num bloco próprio no topo, e só o resto, que é temporal de
   *  verdade, ganha os divisores. */
  private montarListaConversas(all: Contact[], linha: (c: Contact) => string): string {
    const estaOnline = (c: Contact) => !c.email.startsWith("g:") && this.onlineUsers.has(c.email);
    const ehLocal = (c: Contact) => iaContato.ehContatoLocal(c.email);
    const online = all.filter((c) => estaOnline(c) && !ehLocal(c));
    const locais = all.filter(ehLocal);
    const resto = all.filter((c) => !estaOnline(c) && !ehLocal(c));

    const divisor = (texto: string) =>
      `<div class="day-divider"><span class="day-line"></span>` +
      `<span class="day-pill">${texto}</span>` +
      `<span class="day-line"></span></div>`;

    const partes: string[] = [];
    if (online.length) {
      partes.push(divisor("online agora"));
      partes.push(...online.map(linha));
    }

    // Seção própria para o que roda no aparelho.
    //
    // Antes eles caíam logo abaixo de "online agora" por acidente de ordenação,
    // e a tela dizia que um modelo estava "online" — palavra que promete outra
    // pessoa do outro lado. Aqui a promessa é diferente e mais forte: não
    // depende de rede nenhuma.
    if (locais.length) {
      partes.push(divisor("no aparelho"));
      partes.push(...locais.map(linha));
    } else if (llmNativo.disponivel()) {
      // Nenhum modelo baixado. Sem esta linha o recurso é invisível justamente
      // para quem ainda não o conhece — e o convite fica onde ele vai ser
      // usado, não num menu que ninguém abre.
      partes.push(divisor("no aparelho"));
      partes.push(
        `<button class="contact-row convite-ia" type="button" data-convite-ia="1">` +
          `<span class="avatar-wrap"><span class="avatar avatar-local">◆</span></span>` +
          `<span class="contact-meta"><span class="contact-top">` +
          `<span class="contact-name">Conversar sem internet</span></span>` +
          `<span class="contact-bottom"><span class="preview-text muted">` +
          `baixe um modelo e ele vira uma conversa aqui</span></span></span></button>`,
      );
    }

    let periodoAnterior = "";
    for (const c of resto) {
      // Conversa sem histórico (lastTs 0) não tem data para divisor nenhum.
      if (c.lastTs > 0) {
        const chave = chavePeriodo(c.lastTs);
        if (chave !== periodoAnterior) {
          partes.push(this.divisorPeriodo(c.lastTs));
          periodoAnterior = chave;
        }
      }
      partes.push(linha(c));
    }
    return partes.join("");
  }

  private renderLinhaArquivo(a: ArquivoListado, _me: string): string {
    // De quem é a conversa onde esse arquivo mora: no grupo é o próprio
    // grupo; no direto, o outro lado — que depende de quem mandou.
    const conversa = a.to.startsWith("g:") ? a.to : a.mine ? a.to : a.from;
    const quem = a.mine ? "eu" : a.from.split("@")[0];
    const quando = fmtQuando(a.ts);
    return `
      <button class="file-row" type="button" data-id="${escapeHtml(a.id)}" data-conversa="${escapeHtml(conversa)}" data-ts="${a.ts}">
        <span class="file-row-icone" aria-hidden="true">${fileEmoji(a.mime, a.name)}</span>
        <span class="file-row-meta">
          <span class="file-row-nome">${escapeHtml(a.name)}</span>
          <span class="file-row-sub">${escapeHtml(formatBytes(a.bytes))} · ${escapeHtml(quem)} · ${escapeHtml(quando)}</span>
        </span>
      </button>`;
  }

  // ---------- Theme toggle ----------

  private wireThemeToggle() {
    const btn = document.getElementById("theme-toggle") as HTMLButtonElement | null;
    const apply = (t: "light" | "dark") => {
      document.documentElement.dataset.theme = t;
      const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
      if (meta) meta.content = t === "dark" ? "#0a0d12" : "#2563eb";
    };
    const stored = (localStorage.getItem("wuwei.theme") || "light") as "light" | "dark";
    apply(stored);
    if (!btn) return;
    btn.onclick = () => {
      const cur = document.documentElement.dataset.theme === "dark" ? "dark" : "light";
      const next = cur === "dark" ? "light" : "dark";
      apply(next);
      localStorage.setItem("wuwei.theme", next);
    };
  }

  // ---------- Custom status (busy/away) ----------

  private wireStatusSelect() {
    const select = document.getElementById("me-status") as HTMLSelectElement | null;
    if (!select) return;
    const saved = (localStorage.getItem("wuwei.status") || "") as UserStatus;
    if (saved === "busy" || saved === "away") {
      this.myStatus = saved;
      select.value = saved;
      select.dataset.active = saved;
    }
    const updateDot = (v: UserStatus) => {
      const dot = document.getElementById("user-status-dot");
      if (dot) {
        if (v) dot.dataset.status = v;
        else delete dot.dataset.status;
      }
    };
    updateDot(this.myStatus);
    select.addEventListener("change", () => {
      const v = select.value as UserStatus;
      this.myStatus = v;
      if (v) {
        localStorage.setItem("wuwei.status", v);
        select.dataset.active = v;
      } else {
        localStorage.removeItem("wuwei.status");
        delete select.dataset.active;
      }
      updateDot(v);
      // Pela fila: marcar "ocupado" sem rede e a marca sumir é pior do que
      // não ter a marca — a bolinha diria "livre" para quem está ocupado.
      // Chave fixa porque só o último estado importa.
      void this.enviarOuEnfileirar({ type: "set_status", status: v }, "set_status");
    });
  }

  /** Ao reconectar (ready), re-publica o status atual pro servidor. */
  private republishStatus() {
    if (this.myStatus) {
      this.conn.send({ type: "set_status", status: this.myStatus });
    }
  }

  private statusLabel(s: UserStatus): string {
    return s === "busy" ? "ocupado" : s === "away" ? "ausente" : "";
  }

  // ---------- Invite (QR + link) ----------

  private wireInviteModal() {
    const modal = document.getElementById("invite-view");
    const close = document.getElementById("invite-back") as HTMLButtonElement | null;
    const copy = document.getElementById("invite-copy") as HTMLButtonElement | null;
    if (!modal || !close || !copy) return;
    close.onclick = () => this.closeInviteModal();
    modal.onclick = (ev) => {
      if ((ev.target as HTMLElement).id === "invite-view") this.closeInviteModal();
    };
    copy.onclick = () => {
      const input = document.getElementById("invite-link") as HTMLInputElement | null;
      if (!input) return;
      const status = document.getElementById("invite-status") as HTMLElement | null;
      void navigator.clipboard
        .writeText(input.value)
        .then(() => {
          if (status) {
            status.textContent = "link copiado";
            status.classList.remove("err");
          }
        })
        .catch(() => {
          input.select();
          document.execCommand?.("copy");
          if (status) {
            status.textContent = "selecione e copie manualmente";
            status.classList.add("err");
          }
        });
    };
  }

  private closeInviteModal() {
    this.fecharPagina();
  }

  private async openInviteModal() {
    this.abrirPagina("invite-view");
    const modal = document.getElementById("invite-view");
    const qrBox = document.getElementById("invite-qr");
    const linkInput = document.getElementById("invite-link") as HTMLInputElement | null;
    const status = document.getElementById("invite-status") as HTMLElement | null;
    if (!modal || !qrBox || !linkInput) return;
    modal.classList.remove("hidden");
    qrBox.classList.add("is-loading");
    qrBox.innerHTML = "";
    linkInput.value = "";
    if (status) {
      status.textContent = "";
      status.classList.remove("err");
    }
    try {
      const r = await fetch("/auth/invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // A sessão vai junto porque o servidor devolve um magic link pronto:
        // sem ela, qualquer um pediria o link de qualquer email.
        body: JSON.stringify({ email: this.sess.email, session: this.sess.session }),
      });
      if (!r.ok) {
        const err = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error || `falhou (${r.status})`);
      }
      const data = (await r.json()) as { link: string; qrSvg: string; ttl: number };
      qrBox.classList.remove("is-loading");
      // qrSvg vem do nosso server, é seguro injetar (não vem de input do usuário)
      qrBox.innerHTML = data.qrSvg;
      linkInput.value = data.link;
      if (status) {
        const min = Math.round(data.ttl / 60);
        status.textContent = `expira em ${min} min`;
      }
    } catch (e) {
      qrBox.classList.remove("is-loading");
      qrBox.innerHTML = "";
      if (status) {
        status.textContent =
          "erro: " + (e instanceof Error ? e.message : String(e));
        status.classList.add("err");
      }
    }
  }

  private renderContacts() {
    const list = $("contact-list");
    // União: contatos salvos + usuários online (auto, mesmo sem histórico)
    const byEmail = new Map<string, Contact>();
    for (const c of this.contacts) {
      // Conversa apagada não volta por estar guardada localmente.
      if (this.apagadas.has(c.email)) continue;
      byEmail.set(c.email, c);
    }
    for (const e of this.onlineUsers) {
      if (e === this.sess.email) continue;
      // ...nem por a pessoa ficar online. Este era o caminho que ressuscitava
      // a conversa: qualquer contato conectado entrava na lista mesmo sem
      // histórico, ignorando que tinha sido apagado.
      if (this.apagadas.has(e)) continue;
      if (!byEmail.has(e)) {
        byEmail.set(e, { email: e, lastTs: 0, lastText: "", unread: 0 });
      }
    }
    // Os modelos que estão no aparelho entram na lista mesmo sem conversa
    // nenhuma ainda. É o equivalente ao "online" de uma pessoa: eles estão
    // disponíveis agora, e sem isto o recurso continuaria escondido — só que
    // atrás de uma lista em vez de atrás de uma aba, o que não seria progresso.
    for (const m of iaContato.modelosParaConversa()) {
      if (this.apagadas.has(m.contato)) continue;
      if (!byEmail.has(m.contato)) {
        byEmail.set(m.contato, { email: m.contato, lastTs: 0, lastText: "", unread: 0 });
      }
    }

    const allRaw = [...byEmail.values()].sort((a, b) => {
      // Online primeiro, depois por última mensagem. Modelo no aparelho conta
      // como online — ele está sempre pronto, e é o único contato do app que
      // não depende de rede para responder.
      const aOn = this.onlineUsers.has(a.email) || iaContato.ehContatoLocal(a.email) ? 1 : 0;
      const bOn = this.onlineUsers.has(b.email) || iaContato.ehContatoLocal(b.email) ? 1 : 0;
      if (aOn !== bOn) return bOn - aOn;
      return b.lastTs - a.lastTs;
    });

    // Atualiza badge de não-lidas na tab
    const totalUnread = allRaw.reduce((s, c) => s + (c.unread || 0), 0);
    const tabBadge = document.getElementById("tab-unread-count");
    if (tabBadge) {
      tabBadge.textContent = String(totalUnread);
      tabBadge.hidden = totalUnread === 0;
    }
    this.pintarTituloDaAba(totalUnread);

    const all = allRaw.filter((c) => {
      if (this.contactFilter === "unread") return (c.unread || 0) > 0;
      if (this.contactFilter === "groups") return c.email.startsWith("g:");
      return true;
    });

    if (all.length === 0) {
      list.innerHTML =
        '<div class="empty-hint">Ninguém online ainda.<br>Convide alguém para entrar.</div>';
      return;
    }

    // Perfis que ainda não conhecemos entram numa busca só, depois do desenho:
    // a lista aparece na hora com iniciais e ganha nome e foto quando chegam.
    void this.carregarPerfis(all.map((c) => c.email));

    const linhaConversa = (c: Contact): string => {
        const active = c.email === this.currentContact ? " active" : "";
        const isGroup = c.email.startsWith("g:");
        const isLocal = iaContato.ehContatoLocal(c.email);
        const online = !isGroup && !isLocal && this.onlineUsers.has(c.email);
        const unreadN = c.unread || 0;
        const unreadClass = unreadN > 0 ? " is-unread" : "";
        const unreadBadge = unreadN > 0 ? `<span class="badge">${unreadN}</span>` : "";
        const isTyping =
          (!isGroup && this.typingFromSet.has(c.email)) ||
          (isLocal && this.iaGerando === c.email);
        const sub = isTyping
          ? '<span class="contact-typing">digitando<span class="pontos"></span></span>'
          : c.lastText
            ? `<span class="preview-text">${escapeHtml(c.lastText.slice(0, 60))}</span>`
            : isGroup
              ? `<span class="preview-text muted">${(this.groups.get(c.email)?.members.length ?? 0)} membros</span>`
              : isLocal
                ? '<span class="preview-text muted">no aparelho · não usa internet</span>'
                : online
                  ? '<span class="preview-text muted">online</span>'
                  : '<span class="preview-text muted">sem mensagens ainda</span>';
        const time = c.lastTs ? fmtTime(c.lastTs) : "";
        const cstatus = !isGroup ? (this.statusByEmail.get(c.email) ?? "") : "";
        const statusTag = cstatus
          ? `<span class="status-tag" data-status="${cstatus}">${this.statusLabel(cstatus)}</span>`
          : "";
        const display = this.displayName(c.email);
        // O modelo não tem iniciais nem foto — tem um símbolo fixo. Dar a ele
        // um avatar de gente seria a mentira mais fácil de contar aqui, e a
        // pessoa precisa distinguir num relance com quem está falando.
        const initials = isGroup ? "##" : isLocal ? "◆" : avatarInitials(c.email);
        const foto = isGroup || isLocal ? "" : (this.perfis.get(c.email)?.avatar ?? "");
        // Com foto, o fundo é a imagem e as iniciais somem; sem foto, tudo
        // segue como antes. As duas formas ocupam a mesma caixa, então a lista
        // não muda de altura quando alguém põe retrato.
        const avatarStyle = isGroup || isLocal
          ? ""
          : foto
            ? `style="background-image:url('${escapeHtml(foto)}')"`
            : `style="background:${colorFor(c.email)}"`;
        const avatarClass = isGroup
          ? "avatar group-avatar"
          : isLocal
            ? "avatar avatar-local"
            : foto
              ? "avatar avatar-foto"
              : "avatar";
        // online-dot mantido como alias pra compatibilidade com testes/CSS legado.
        //
        // Ocupado ganha bolinha própria, amarela, e vem ANTES de online: quem
        // está conectado e ocupado precisa aparecer como ocupado, senão a
        // bolinha verde promete uma resposta que não vem. Ausente segue cinza.
        const presence = isLocal
          ? '<span class="presence-dot is-local" title="no aparelho"></span>'
          : cstatus === "busy"
            ? '<span class="presence-dot is-busy online-dot" title="ocupado"></span>'
            : online
              ? '<span class="presence-dot is-active online-dot" title="online"></span>'
              : cstatus === "away"
                ? '<span class="presence-dot is-away" title="ausente"></span>'
                : "";
        return `
          <button data-ts="${c.lastTs}" class="contact-row${active}${unreadClass}" data-email="${escapeHtml(c.email)}">
            <span class="avatar-wrap">
              <span class="${avatarClass}" ${avatarStyle}>${foto ? "" : escapeHtml(initials)}</span>
              ${presence}
            </span>
            <span class="contact-meta">
              <span class="contact-top">
                <span class="contact-name">${escapeHtml(display)}${statusTag}</span>
              </span>
              <span class="contact-bottom">${sub}</span>
            </span>
            <span class="contact-meta-right">
              <span class="contact-time">${time}</span>
              ${unreadBadge}
            </span>
          </button>`;
    };

    list.innerHTML = this.montarListaConversas(all, linhaConversa);
    this.ativarLinhaDoTempo(list, ".contact-row[data-ts]");
    list.querySelectorAll<HTMLElement>(".contact-name").forEach((n) => this.instalarPeek(n));
    // O convite para baixar um modelo não é conversa: leva à tela de modelos.
    list.querySelectorAll<HTMLButtonElement>("[data-convite-ia]").forEach((el) => {
      el.onclick = () => {
        this.abrirPagina("agentes-locais-view");
        this.renderAgentesLocais();
      };
    });
    list.querySelectorAll<HTMLButtonElement>(".contact-row[data-email]").forEach((el) => {
      const email = el.dataset.email!;
      el.onclick = () => {
        // Com a barra aberta, o toque cancela a seleção em vez de abrir a
        // conversa — senão seria impossível sair sem entrar em algo.
        if (this.convSelecionada) {
          this.fecharSelecaoConversa();
          return;
        }
        void this.openContact(email);
      };

      // Segurar abre as ações da conversa, igual ao gesto nas mensagens.
      let timer: number | undefined;
      let moveu = false;
      const cancelar = () => {
        if (timer !== undefined) {
          clearTimeout(timer);
          timer = undefined;
        }
      };
      el.addEventListener(
        "touchstart",
        () => {
          moveu = false;
          cancelar();
          timer = window.setTimeout(() => {
            if (moveu) return;
            navigator.vibrate?.(15);
            this.abrirSelecaoConversa(email);
          }, 450);
        },
        { passive: true },
      );
      el.addEventListener("touchmove", () => {
        moveu = true;
        cancelar();
      }, { passive: true });
      el.addEventListener("touchend", cancelar);
      el.addEventListener("touchcancel", cancelar);
      el.addEventListener("contextmenu", (ev) => {
        ev.preventDefault();
        this.abrirSelecaoConversa(email);
      });
    });
  }

  /** Nome comprido continua legível: a faixa é sempre rolável.
   *
   *  A versão anterior alternava entre "cortado com reticências" e "rolável"
   *  conforme o toque, porque `text-overflow: ellipsis` exige overflow hidden
   *  e rolar exige overflow auto. Só que qualquer troca de estado — o gesto,
   *  ou um redesenho do cabeçalho — devolvia a rolagem ao início. Um estado
   *  só, permanente, não tem como voltar sozinho; o corte fica indicado por um
   *  esmaecido na borda, no lugar das reticências. */
  private instalarPeek(el: HTMLElement) {
    if (el.dataset.peek === "1") return;
    el.dataset.peek = "1";
    el.classList.add("is-peekable");
  }

  /** Menu hambúrguer: recolhe o que antes eram ícones soltos na barra. */
  private wireAppMenu() {
    const btn = document.getElementById("menu-btn");
    const menu = document.getElementById("app-menu");
    if (!btn || !menu) return;
    const fechar = () => {
      menu.classList.add("hidden");
      btn.setAttribute("aria-expanded", "false");
    };
    btn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      const aberto = !menu.classList.contains("hidden");
      menu.classList.toggle("hidden", aberto);
      btn.setAttribute("aria-expanded", String(!aberto));
      // O email pode não caber: deixa a faixa rolável, igual ao nome longo.
      const email = document.getElementById("me-email");
      if (email) this.instalarPeek(email);
    });
    menu.addEventListener("click", (ev) => {
      // O seletor de status vive aqui dentro e não deve fechar o menu.
      const alvo = ev.target as HTMLElement;
      if (alvo.closest("select") || alvo.closest(".user-pill")) return;
      if (alvo.closest("button, a")) fechar();
    });
    document.addEventListener("click", (ev) => {
      if (menu.classList.contains("hidden")) return;
      const alvo = ev.target as HTMLElement;
      if (alvo.closest("#app-menu") || alvo.closest("#menu-btn")) return;
      fechar();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") fechar();
    });
  }

  // ---------- Menções (@) ----------

  /** Só faz sentido em grupo: numa conversa de dois, citar quem está do outro
   *  lado não acrescenta nada. */
  private membrosMencionaveis(): string[] {
    const c = this.currentContact;
    if (!c) return [];
    // Os modelos do aparelho podem ser chamados em QUALQUER conversa — é a
    // ideia inteira da peça: perguntar sem sair de onde se está. Dentro da
    // conversa com o próprio modelo eles não entram, que ali chamá-lo por
    // menção seria chamar quem já está falando.
    const locais = iaContato.ehContatoLocal(c)
      ? []
      : iaContato.modelosParaConversa().map((m) => m.contato);
    if (!c.startsWith("g:")) return locais;
    const g = this.groups.get(c);
    if (!g) return locais;
    return [...locais, ...g.members.filter((m) => m !== this.sess.email)];
  }

  /** Devolve o trecho "@algo" que está sendo digitado no fim do texto, ou null. */
  private mencaoEmCurso(ta: HTMLTextAreaElement): { termo: string; inicio: number } | null {
    const ate = ta.value.slice(0, ta.selectionStart ?? ta.value.length);
    // O @ precisa começar palavra: e-mail no meio do texto não abre a lista.
    const m = ate.match(/(?:^|\s)@([\w.@-]*)$/);
    if (!m) return null;
    return { termo: m[1] ?? "", inicio: ate.length - (m[1] ?? "").length - 1 };
  }

  private fecharMencoes() {
    document.getElementById("mention-box")?.classList.add("hidden");
    this.mencaoIdx = -1;
    this.mencaoLista = [];
  }

  private atualizarMencoes() {
    const caixa = document.getElementById("mention-box");
    const ta = document.getElementById("composer-input") as HTMLTextAreaElement | null;
    if (!caixa || !ta) return;
    const alvo = this.mencaoEmCurso(ta);
    const membros = this.membrosMencionaveis();
    if (!alvo || membros.length === 0) {
      this.fecharMencoes();
      return;
    }
    const termo = alvo.termo.toLowerCase();
    this.mencaoLista = membros.filter((m) => {
      // "ia" acha os modelos do aparelho. Sem este apelido, chamar o modelo
      // exigiria lembrar o nome do arquivo — e ninguém digita "gemma-3-1b-it"
      // no meio de uma conversa.
      if (iaContato.ehContatoLocal(m) && "ia".startsWith(termo)) return true;
      return m.toLowerCase().includes(termo) || this.displayName(m).toLowerCase().includes(termo);
    });
    if (this.mencaoLista.length === 0) {
      this.fecharMencoes();
      return;
    }
    if (this.mencaoIdx < 0 || this.mencaoIdx >= this.mencaoLista.length) this.mencaoIdx = 0;
    caixa.innerHTML = this.mencaoLista
      .map(
        (m, i) => `
      <button type="button" class="mention-item${i === this.mencaoIdx ? " is-on" : ""}" data-email="${escapeHtml(m)}">
        <span class="mention-name">${escapeHtml(this.displayName(m))}</span>
        <span class="mention-mail">${escapeHtml(m)}</span>
      </button>`,
      )
      .join("");
    caixa.querySelectorAll<HTMLButtonElement>(".mention-item").forEach((b) => {
      // mousedown, não click: o click chegaria depois do blur do campo.
      b.addEventListener("mousedown", (ev) => {
        ev.preventDefault();
        this.inserirMencao(b.dataset.email!);
      });
      b.addEventListener("touchstart", (ev) => {
        ev.preventDefault();
        this.inserirMencao(b.dataset.email!);
      }, { passive: false });
    });
    caixa.classList.remove("hidden");
  }

  private inserirMencao(email: string) {
    const ta = document.getElementById("composer-input") as HTMLTextAreaElement | null;
    if (!ta) return;
    const alvo = this.mencaoEmCurso(ta);
    if (!alvo) return;
    const pos = ta.selectionStart ?? ta.value.length;
    const antes = ta.value.slice(0, alvo.inicio);
    const depois = ta.value.slice(pos);
    // Modelo entra como "@ia", não como "@local:gemma-3-1b-it-Q4_K_M.gguf".
    // O nome do arquivo no meio da frase é ilegível, e quem lê a conversa
    // depois não deve tropeçar num caminho de disco. Qual modelo foi escolhido
    // fica guardado aqui, fora do texto.
    const local = iaContato.ehContatoLocal(email);
    this.modeloDaMencao = local ? iaContato.arquivoDoContato(email) : null;
    // Escolher o modelo é o primeiro sinal claro de que ele vai ser usado. A
    // carga começa aqui e corre enquanto a pergunta é digitada.
    if (this.modeloDaMencao) iaContato.preAquecer(this.modeloDaMencao);
    const texto = local ? "@ia " : `@${email} `;
    ta.value = antes + texto + depois;
    const cursor = (antes + texto).length;
    ta.setSelectionRange(cursor, cursor);
    ta.focus();
    this.fecharMencoes();
    this.updateActionMode();
    this.pintarComposer();
  }

  /** Pinta as menções na camada atrás do campo. O textarea não aceita cor
   *  parcial; a saída é desenhar o mesmo texto embaixo, com as métricas
   *  idênticas, e deixar a letra do campo transparente. */
  private pintarComposer() {
    const ta = document.getElementById("composer-input") as HTMLTextAreaElement | null;
    const hl = document.getElementById("composer-hl");
    if (!ta || !hl) return;
    const bruto = ta.value;
    if (!bruto) {
      hl.innerHTML = "";
      ta.classList.remove("tem-mencao");
      return;
    }
    // Desativado: ver comentário em styles.css. A pintura ficou desalinhada
    // no aparelho real e o texto saía duplicado. Mantido o código, desligado
    // o efeito, para não recriar a mecânica do zero se um dia valer a pena.
    const temMencao = false;
    ta.classList.remove("tem-mencao");
    if (!temMencao) {
      hl.innerHTML = "";
      return;
    }
    hl.innerHTML =
      escapeHtml(bruto).replace(
        /(^|\s)(@[\w.-]+@[\w.-]+\.\w+)/g,
        (_m, antes, alvo) => `${antes}<span class="hl-mencao">${alvo}</span>`,
      ) + "\n";
    hl.scrollTop = ta.scrollTop;
  }

  private wireMencoes() {
    const ta = document.getElementById("composer-input") as HTMLTextAreaElement | null;
    if (!ta) return;
    ta.addEventListener("input", () => {
      this.atualizarMencoes();
      this.pintarComposer();
    });
    ta.addEventListener("scroll", () => {
      const hl = document.getElementById("composer-hl");
      if (hl) hl.scrollTop = ta.scrollTop;
    });
    ta.addEventListener("blur", () => window.setTimeout(() => this.fecharMencoes(), 120));
    ta.addEventListener("keydown", (ev) => {
      const caixa = document.getElementById("mention-box");
      if (!caixa || caixa.classList.contains("hidden")) return;
      if (ev.key === "ArrowDown" || ev.key === "ArrowUp") {
        ev.preventDefault();
        const passo = ev.key === "ArrowDown" ? 1 : -1;
        this.mencaoIdx =
          (this.mencaoIdx + passo + this.mencaoLista.length) % this.mencaoLista.length;
        this.atualizarMencoes();
      } else if (ev.key === "Enter" || ev.key === "Tab") {
        // Enter com a lista aberta escolhe, em vez de enviar a mensagem.
        ev.preventDefault();
        const alvo = this.mencaoLista[this.mencaoIdx];
        if (alvo) this.inserirMencao(alvo);
      } else if (ev.key === "Escape") {
        this.fecharMencoes();
      }
    });
  }

  // ---------- Editar grupo ----------

  private abrirEditarGrupo(gid: string) {
    const g = this.groups.get(gid);   // `groups` é um Map, não lista
    if (!g) return;
    this.grupoEditando = { id: gid, membros: [...g.members] };
    const nome = document.getElementById("gv-name") as HTMLInputElement | null;
    if (nome) nome.value = g.name;
    this.renderMembrosGrupo();
    this.abrirPagina("group-view");
  }

  private renderMembrosGrupo() {
    const caixa = document.getElementById("gv-members");
    const st = this.grupoEditando;
    if (!caixa || !st) return;
    caixa.innerHTML = st.membros
      .map((m) => {
        const eu = m === this.sess.email;
        return `
        <div class="agent-row">
          <span class="agent-row-meta">
            <span class="agent-row-name">${escapeHtml(this.displayName(m))}${eu ? " (você)" : ""}</span>
            <span class="agent-row-email">${escapeHtml(m)}</span>
          </span>
          ${
            eu
              ? ""
              : `<button class="modal-btn gv-rm" data-email="${escapeHtml(m)}" type="button">Remover</button>`
          }
        </div>`;
      })
      .join("");
    caixa.querySelectorAll<HTMLButtonElement>(".gv-rm").forEach((b) => {
      b.onclick = () => {
        st.membros = st.membros.filter((m) => m !== b.dataset.email);
        this.renderMembrosGrupo();
      };
    });
  }

  private wireGroupEdit() {
    document.getElementById("group-back")?.addEventListener("click", () => {
      this.grupoEditando = null;
      this.fecharPagina();
    });

    document.getElementById("gv-add-btn")?.addEventListener("click", () => {
      const campo = document.getElementById("gv-add") as HTMLInputElement | null;
      const st = this.grupoEditando;
      const em = campo?.value.trim().toLowerCase();
      if (!st || !em || !em.includes("@")) return;
      if (st.membros.includes(em)) {
        this.mostrarAviso("já está no grupo");
        return;
      }
      st.membros.push(em);
      if (campo) campo.value = "";
      this.renderMembrosGrupo();
    });

    document.getElementById("gv-save")?.addEventListener("click", () => {
      const st = this.grupoEditando;
      if (!st) return;
      const nome = (document.getElementById("gv-name") as HTMLInputElement | null)?.value.trim();
      if (st.membros.length < 2) {
        this.mostrarAviso("um grupo precisa de pelo menos duas pessoas");
        return;
      }
      this.conn.send({
        type: "update_group",
        id: st.id,
        name: nome || undefined,
        members: st.membros,
      });
      this.mostrarAviso("grupo atualizado");
      this.grupoEditando = null;
      this.fecharPagina();
    });
  }

  // ---------- Seleção de conversa na lista ----------

  private abrirSelecaoConversa(email: string) {
    this.convSelecionada = email;
    const bar = document.getElementById("conv-sel-bar");
    const nome = document.getElementById("conv-sel-name");
    if (nome) nome.textContent = this.displayName(email);
    bar?.classList.remove("hidden");
    document
      .querySelectorAll(".contact-row.is-selected")
      .forEach((e) => e.classList.remove("is-selected"));
    document
      .querySelector(`.contact-row[data-email="${CSS.escape(email)}"]`)
      ?.classList.add("is-selected");
  }

  private fecharSelecaoConversa() {
    this.convSelecionada = null;
    document.getElementById("conv-sel-bar")?.classList.add("hidden");
    document
      .querySelectorAll(".contact-row.is-selected")
      .forEach((e) => e.classList.remove("is-selected"));
  }

  private wireConvSelection() {
    document.getElementById("conv-sel-close")?.addEventListener("click", () =>
      this.fecharSelecaoConversa(),
    );
    document.getElementById("conv-sel-delete")?.addEventListener("click", async () => {
      const email = this.convSelecionada;
      if (!email) return;
      const quem = this.displayName(email);
      if (
        !confirm(
          `Apagar a conversa com ${quem}?\n\n` +
            `Some deste aparelho apenas. A outra pessoa continua com o histórico dela, ` +
            `e mensagens novas reabrem a conversa.`,
        )
      ) {
        return;
      }
      const n = await this.store.deleteConversation(email);
      this.marcarApagada(email, true);
      // Some nos outros aparelhos também: o servidor guarda o ocultamento e
      // ecoa na hora. Sem isto, apagar aqui deixava a conversa de pé no resto.
      this.conn.send({ type: "conv_hide", com: email, ts: Date.now() });
      this.contacts = this.contacts.filter((c) => c.email !== email);
      if (this.currentContact === email) {
        this.currentContact = null;
        this.messages = [];
        this.showDashboardView();
      }
      this.fecharSelecaoConversa();
      this.renderContacts();
      this.mostrarAviso(`conversa apagada (${n} mensagens)`);
    });
  }

  /** O avatar do cabeçalho da conversa. Estava só com as iniciais enquanto a
   *  lista ao lado já mostrava o retrato: quem punha foto via a mudança na
   *  lista e não dentro da conversa, que é onde se olha para o rosto. */
  private pintarCabecalhoConversa() {
    if (!this.currentContact) return;
    const av = $("chat-header-avatar");
    const ehGrupo = this.currentContact.startsWith("g:");
    const ehLocal = iaContato.ehContatoLocal(this.currentContact);
    // O modelo tinha ganho iniciais de gente: `local:gemma-…` virava "L3" num
    // círculo colorido, igual ao de uma pessoa. Dentro da conversa, que é onde
    // se esquece com quem se está falando, essa é a confusão mais cara de todas.
    const foto = ehGrupo || ehLocal ? "" : (this.perfis.get(this.currentContact)?.avatar ?? "");
    av.textContent = ehGrupo ? "##" : ehLocal ? "◆" : foto ? "" : avatarInitials(this.currentContact);
    av.classList.toggle("av-local", ehLocal);
    av.classList.toggle("av-foto", !!foto);
    if (foto) {
      // A ordem importa: `background` é atalho e apaga `background-image`, então
      // a cor sai primeiro e a imagem entra depois. O contrário deixava o
      // círculo colorido por cima do retrato.
      av.style.background = "";
      av.style.backgroundImage = `url("${foto}")`;
    } else {
      av.style.backgroundImage = "";
      av.style.background = ehGrupo
        ? "linear-gradient(135deg, var(--accent), var(--info))"
        : ehLocal
          ? ""
          : colorFor(this.currentContact);
    }
  }

  private renderChatHeaderPresence() {
    if (!this.currentContact) return;
    const wrap = $("chat-header-status");
    if (this.currentContact.startsWith("g:")) {
      wrap.className = "chat-header-status";
      const g = this.groups.get(this.currentContact);
      wrap.textContent = g ? `${g.members.length} membros` : "";
      return;
    }
    // Modelo no aparelho: o subtítulo diz onde a conversa mora.
    //
    // É a única linha da tela que informa que nada dali sai, e ela precisa
    // estar à vista o tempo todo — uma promessa de privacidade que só aparece
    // no primeiro uso, ou num menu, não é lida por ninguém no momento em que
    // importa, que é enquanto se digita.
    if (iaContato.ehContatoLocal(this.currentContact)) {
      if (this.iaGerando === this.currentContact) {
        wrap.className = "chat-header-status typing";
        wrap.innerHTML = "escrevendo<span class=\"pontos\"></span>";
      } else {
        wrap.className = "chat-header-status local";
        wrap.textContent = "no aparelho · esta conversa não sai daqui";
      }
      return;
    }
    if (this.typingFromSet.has(this.currentContact)) {
      wrap.className = "chat-header-status typing";
      wrap.innerHTML =
        'digitando<span class="pontos"></span>';
      return;
    }
    const cstatus = this.statusByEmail.get(this.currentContact) ?? "";
    // A descrição é o que a pessoa escolheu dizer sobre si, e não muda; presença
    // e status mudam e são a informação perecível. Por isso ela fica com a linha
    // só quando não há nada perecível ali — nunca empurrando "online" para fora.
    const bio = (this.perfis.get(this.currentContact)?.bio ?? "").trim();
    if (this.onlineUsers.has(this.currentContact)) {
      wrap.className = "chat-header-status online";
      wrap.textContent = cstatus ? `online · ${this.statusLabel(cstatus)}` : "online";
    } else if (cstatus) {
      wrap.className = "chat-header-status";
      wrap.textContent = this.statusLabel(cstatus);
    } else if (bio) {
      wrap.className = "chat-header-status bio";
      wrap.textContent = bio;
    } else {
      wrap.className = "chat-header-status";
      wrap.textContent = "";
    }
  }

  /** Map em memória hash → contact pra resolver paths /r/<hash>. NÃO é
   *  persistido em IndexedDB — a privacidade do esquema vem justamente do
   *  fato de que o histórico do browser nunca vê o email/groupId real. */
  private hashToContact = new Map<string, string>();
  private contactToHashCache = new Map<string, string>();

  /** Hash determinístico SHA-256 trunco em 16 hex (8 bytes). Usa o email
   *  da sessão como salt — assim URLs de uma conta não são reaproveitáveis
   *  em outra conta nem facilmente força-bruteáveis a partir de uma lista
   *  de emails comuns. */
  private async hashContact(contact: string): Promise<string> {
    const cached = this.contactToHashCache.get(contact);
    if (cached) return cached;
    const enc = new TextEncoder();
    const data = enc.encode(`${this.sess.email}::${contact}`);
    const buf = await crypto.subtle.digest("SHA-256", data);
    const hex = Array.from(new Uint8Array(buf).slice(0, 8))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    this.contactToHashCache.set(contact, hex);
    this.hashToContact.set(hex, contact);
    return hex;
  }

  /** Pré-computa hashes pra todos os contatos conhecidos. Chamado no
   *  start() e após sync_request — popula o map em memória pra que
   *  popstate / deep-link em /r/<hash> possam resolver. */
  private async warmHashIndex(): Promise<void> {
    for (const c of this.contacts) await this.hashContact(c.email);
  }

  /** URL canônica de uma conversa: `/r/<hash>` (anônima — não revela
   *  contato real no histórico do browser, share, referer, etc). */
  private async contactToPath(contact: string): Promise<string> {
    const h = await this.hashContact(contact);
    return `/r/${h}`;
  }

  /** Inverso. `/r/<hash>` resolve via map em memória; se o hash não foi
   *  pré-computado (ex: deep-link em device novo antes do sync chegar),
   *  retorna null e a UI cai em dashboard até o map popular. As rotas
   *  antigas /c/ e /g/ continuam aceitas como fallback de compat. */
  private pathToContact(path: string): string | null {
    const r = path.match(/^\/r\/([a-f0-9]+)$/);
    if (r) return this.hashToContact.get(r[1]) ?? null;
    const c = path.match(/^\/c\/(.+)$/);
    if (c) return decodeURIComponent(c[1]).toLowerCase();
    const g = path.match(/^\/g\/(.+)$/);
    if (g) return "g:" + decodeURIComponent(g[1]);
    return null;
  }

  /** Atualiza a URL pra refletir a conversa aberta. Usa `pushState` se a URL
   *  mudou (entrada nova no histórico do browser); `replaceState` quando é
   *  só sincronização (ex: load inicial via deeplink). Async porque o hash
   *  depende de Web Crypto. */
  private async syncUrl(
    contact: string | null,
    opts: { replace?: boolean } = {},
  ): Promise<void> {
    const target = contact ? await this.contactToPath(contact) : "/";
    if (location.pathname === target) return;
    if (opts.replace) history.replaceState({ contact }, "", target);
    else history.pushState({ contact }, "", target);
  }

  private async openContact(email: string, opts?: { targetMsgId?: string }) {
    // Encerra qualquer "typing" pro contato anterior antes de trocar
    this.sendTypingStop();
    this.currentContact = email;
    void this.syncUrl(email);
    await this.store.ensureContact(email);
    // Carrega só a página mais recente; "load older" busca sob demanda no scroll.
    const page = await this.store.listMessagesPage(email, { limit: this.pageLimit });
    this.messages = page;
    this.hasMoreOlder = page.length === this.pageLimit;
    this.loadingOlder = false;
    // Multi-device sync: se IDB local não tem nada, pede histórico durável
    // ao server (que persiste tudo desde o último cleanup TTL).
    //
    // Conversa com modelo local fica de fora, e não por economia: o servidor
    // NUNCA teve esse histórico, então o pedido só poderia voltar vazio — e o
    // preço de perguntar é contar a ele que a conversa existe. Também é o
    // motivo de ela não seguir para outro aparelho: quem quisesse isso teria de
    // mandar a conversa para fora, que é exatamente o que não se faz aqui.
    if (page.length === 0 && !iaContato.ehContatoLocal(email)) {
      this.conn.send({
        type: "history_request",
        contact: email,
        limit: this.pageLimit,
      });
    }
    // Abrir a conversa com o modelo é o outro sinal de intenção: ninguém entra
    // ali para não falar. A carga corre enquanto a conversa é desenhada e a
    // primeira frase é escrita.
    if (iaContato.ehContatoLocal(email)) {
      iaContato.preAquecer(iaContato.arquivoDoContato(email));
      void this.ingerirAdiantado(email, iaContato.arquivoDoContato(email));
    } else {
      // Conversa com gente também ganha, por causa do @ia — mas só se o modelo
      // JÁ estiver na memória. Carregar 800 MB toda vez que alguém abre uma
      // conversa qualquer transformaria um ganho de segundos num app quente e
      // lento, para quem talvez nem fosse usar o modelo ali.
      const naMemoria = iaContato.modeloNaMemoria();
      if (naMemoria) void this.ingerirAdiantado(email, naMemoria);
    }
    await this.store.clearUnread(email);
    this.contacts = await this.store.listContacts();
    this.renderContacts();
    this.revokeAudioUrls();
    this.renderMessages();
    this.attachScrollListener();
    this.renderChatHeaderPresence();
    const ta = $("composer-input") as HTMLTextAreaElement;
    ta.disabled = false;
    ta.focus();
    void this.flushReadReceipts();
    if (opts?.targetMsgId) {
      await this.jumpToMessage(opts.targetMsgId);
    }
  }

  /** Manda o modelo ler a conversa agora, antes de alguém perguntar.
   *
   *  Experimento pedido pelo dono do app, e declarado como tal na tela: o aviso
   *  aparece quando começa e é substituído pelos números quando termina. Sem o
   *  aviso, o aparelho ficaria trabalhando por segundos sem nada explicando —
   *  que é a mesma queixa que originou tudo isto.
   *
   *  Não bloqueia nada: a pessoa pode escrever e enviar durante a ingestão. Se
   *  ela enviar antes de terminar, o envio espera a fila do motor e aproveita
   *  o que já foi ingerido. */
  private async ingerirAdiantado(contato: string, arquivo: string) {
    const historico = this.messages.filter((m) => m.contact === contato);
    if (historico.length === 0) {
      registro.anotar("ia-contato", "ingestão adiantada não rodou: a conversa está vazia");
      return;
    }
    const t0 = Date.now();
    try {
      const r = await iaContato.preIngerir(
        contato,
        arquivo,
        historico,
        // Duas frases, porque são dois trabalhos com custos diferentes: trazer
        // do disco é ler um arquivo, ler a conversa é passar cada token pelo
        // modelo. Uma frase só para os dois faz quem espera concluir a coisa
        // errada sobre o que está demorando.
        () => {
          if (this.currentContact === contato) {
            this.avisoDaIngestao("carregando a conversa do disco…");
          }
        },
        (restaurados) => {
          if (this.currentContact !== contato) return;
          this.avisoDaIngestao(
            restaurados > 0
              ? `${restaurados} tokens vieram do disco — lendo o que falta…`
              : "lendo a conversa…",
          );
        },
      );
      if (!r) {
        // O silêncio precisa ter nome: sem isto, "a faixa não apareceu" e
        // "o recurso não foi acionado" são o mesmo relato.
        registro.anotar(
          "ia-contato",
          `ingestão adiantada não rodou: ${iaContato.motivoDaRecusa || "sem motivo registrado"}`,
        );
        this.avisoDaIngestao(null);
        return;
      }
      const mb = (r.bytesEmDisco / (1024 * 1024)).toFixed(0);
      const partes = [`leu ${leituraDoPrompt(r.prefill)} em ${(r.ingestaoMs / 1000).toFixed(1)}s`];
      if (r.restaurados) partes.push(`${r.restaurados} do disco`);
      if (r.bytesEmDisco) partes.push(`estado de ${mb} MB gravado em ${(r.salvarMs / 1000).toFixed(1)}s`);
      const texto = `pronto · ${partes.join(" · ")}`;
      registro.anotar("ia-contato", `ingestão adiantada: ${texto}`);
      if (this.currentContact === contato) {
        this.avisoDaIngestao(texto);
        // O aviso some sozinho: ele conta o que aconteceu, não é estado da
        // conversa. Deixá-lo fixo poria um rótulo permanente numa tela que é
        // da pessoa, não do experimento.
        window.setTimeout(() => this.avisoDaIngestao(null), 8000);
      }
    } catch (e) {
      registro.anotar(
        "ia-contato",
        `ingestão adiantada falhou em ${((Date.now() - t0) / 1000).toFixed(1)}s: ${
          e instanceof Error ? e.message : String(e)
        }`,
        "erro",
      );
      this.avisoDaIngestao(null);
    }
  }

  /** A faixa de aviso no topo da conversa. `null` a remove. */
  private avisoDaIngestao(texto: string | null) {
    const lista = document.getElementById("messages");
    if (!lista || !lista.parentElement) return;
    let faixa = document.getElementById("ia-ingestao");
    if (texto === null) {
      faixa?.remove();
      return;
    }
    if (!faixa) {
      faixa = document.createElement("div");
      faixa.id = "ia-ingestao";
      faixa.className = "ia-ingestao";
      lista.parentElement.insertBefore(faixa, lista);
    }
    faixa.textContent = texto;
  }

  /** Carrega páginas anteriores até a mensagem alvo aparecer (ou acabar
   *  o histórico), depois rola até ela e aplica highlight. */
  private async jumpToMessage(msgId: string) {
    let guard = 0;
    while (
      !this.messages.some((m) => m.id === msgId) &&
      this.hasMoreOlder &&
      guard < 100
    ) {
      guard++;
      await this.loadOlderMessages();
    }
    const el = document.querySelector<HTMLElement>(
      `#messages .bubble[data-msg-id="${CSS.escape(msgId)}"]`,
    );
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    el.classList.add("msg-highlight");
    if (this.highlightTimer != null) clearTimeout(this.highlightTimer);
    this.highlightTimer = window.setTimeout(() => {
      el.classList.remove("msg-highlight");
      this.highlightTimer = null;
    }, 1600);
  }

  // ---------- Search ----------

  private wireSearch() {
    const input = $("search-input") as HTMLInputElement;
    input.addEventListener("input", () => {
      const q = input.value;
      if (this.searchTimer != null) clearTimeout(this.searchTimer);
      if (!q.trim()) {
        this.hideSearchResults();
        return;
      }
      // 280ms de debounce: lexical é barato mas o embedText da semantic
      // pode levar 100-300ms; evita disparar a cada keystroke.
      this.searchTimer = window.setTimeout(() => {
        void this.runSearch(q);
      }, 280);
    });
    input.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        input.value = "";
        this.hideSearchResults();
      }
    });
  }

  private hideSearchResults() {
    const results = $("search-results");
    results.classList.add("hidden");
    results.innerHTML = "";
  }

  private async runSearch(q: string) {
    const trimmed = q.trim();
    const results = $("search-results");
    results.classList.remove("hidden");

    // Lexical (substring) é instantâneo — mostra primeiro.
    const lexical = await this.store.searchMessages(trimmed);
    const seen = new Set(lexical.map((h) => h.message.id));

    // Semantic em paralelo: embeda a query e roda cosine sim sobre todos os
    // vetores (texto + áudio). Falhas (modelo ainda não baixou, etc) são
    // silenciosas — fica só o lexical.
    let semantic: {
      contact: string;
      message: ChatMessage;
      score: number;
      kind: "text" | "audio" | "pdf-page";
      pageNumber?: number;
    }[] = [];
    try {
      const t0 = performance.now();
      const vec = await embedText(trimmed, "query");
      const dtEmbed = Math.round(performance.now() - t0);
      // Pega top 20 com threshold baixo pra debug, depois aplica filtro real.
      const debug = await this.store.semanticSearch(vec, 20, 0);
      console.groupCollapsed(
        `[search] "${trimmed}" — embed ${dtEmbed}ms, ${debug.length} candidatos`,
      );
      console.table(
        debug.slice(0, 10).map((h) => ({
          score: h.score.toFixed(3),
          kind: h.kind,
          contact: h.contact,
          text: (h.message.text ?? "").slice(0, 60),
        })),
      );
      console.groupEnd();
      const all = debug.filter((h) => h.score >= 0.5);
      semantic = all.filter((h) => !seen.has(h.message.id));
    } catch (err) {
      console.warn("[search] semantic falhou", err);
    }

    if (lexical.length === 0 && semantic.length === 0) {
      results.innerHTML = `<div class="search-empty">sem resultados</div>`;
      return;
    }

    const renderLexical = (h: { contact: string; message: ChatMessage }) => {
      const text = h.message.text ?? "";
      const idx = text.toLowerCase().indexOf(trimmed.toLowerCase());
      let snippet: string;
      if (idx < 0) {
        snippet = escapeHtml(text.slice(0, 80));
      } else {
        const start = Math.max(0, idx - 20);
        const end = Math.min(text.length, idx + trimmed.length + 60);
        const before = (start > 0 ? "…" : "") + text.slice(start, idx);
        const match = text.slice(idx, idx + trimmed.length);
        const after =
          text.slice(idx + trimmed.length, end) + (end < text.length ? "…" : "");
        snippet =
          escapeHtml(before) +
          "<mark>" +
          escapeHtml(match) +
          "</mark>" +
          escapeHtml(after);
      }
      const tag = h.message.audio
        ? `<span class="search-result-tag">áudio</span>`
        : "";
      return `
        <button class="search-result"
                data-msg-id="${escapeHtml(h.message.id)}"
                data-contact="${escapeHtml(h.contact)}">
          <span class="search-result-head">
            <span class="search-result-contact">${escapeHtml(h.contact)}${tag}</span>
            <span>${fmtTime(h.message.ts)}</span>
          </span>
          <span class="search-result-snippet">${snippet}</span>
        </button>`;
    };

    const renderSemantic = (h: {
      contact: string;
      message: ChatMessage;
      score: number;
      kind: "text" | "audio" | "pdf-page";
      pageNumber?: number;
    }) => {
      const pct = Math.round(h.score * 100);
      let snippet: string;
      let tag: string;
      if (h.kind === "pdf-page") {
        const fileName = h.message.attachment?.name ?? "documento";
        snippet = `📄 ${escapeHtml(fileName)} · página ${h.pageNumber ?? "?"}`;
        tag = `<span class="search-result-tag is-doc">doc</span>`;
      } else if (h.kind === "audio") {
        snippet = escapeHtml((h.message.text ?? "").slice(0, 140));
        tag = `<span class="search-result-tag is-audio">🎤 áudio</span>`;
      } else {
        snippet = escapeHtml((h.message.text ?? "").slice(0, 140));
        tag = `<span class="search-result-tag">texto</span>`;
      }
      return `
        <button class="search-result is-semantic"
                data-msg-id="${escapeHtml(h.message.id)}"
                data-contact="${escapeHtml(h.contact)}">
          <span class="search-result-head">
            <span class="search-result-contact">
              ${escapeHtml(h.contact)}
              ${tag}
              <span class="search-result-score">${pct}%</span>
            </span>
            <span>${fmtTime(h.message.ts)}</span>
          </span>
          <span class="search-result-snippet">${snippet}</span>
        </button>`;
    };

    const sections: string[] = [];
    if (lexical.length > 0) {
      sections.push(
        `<div class="search-section-label">no texto</div>` +
          lexical.map(renderLexical).join(""),
      );
    }
    if (semantic.length > 0) {
      sections.push(
        `<div class="search-section-label">semanticamente similares</div>` +
          semantic.map(renderSemantic).join(""),
      );
    }
    results.innerHTML = sections.join("");

    results.querySelectorAll<HTMLButtonElement>(".search-result").forEach((btn) => {
      btn.onclick = () => {
        const contact = btn.dataset.contact!;
        const msgId = btn.dataset.msgId!;
        const input = $("search-input") as HTMLInputElement;
        input.value = "";
        this.hideSearchResults();
        void this.openContact(contact, { targetMsgId: msgId });
      };
    });
  }

  // ---------- Message action menu / reactions / quote / forward ----------

  private wireMessageMenu() {
    const menu = $("msg-menu");
    menu.querySelectorAll<HTMLButtonElement>(".msg-menu-item").forEach((btn) => {
      btn.onclick = (ev) => {
        ev.stopPropagation();
        const act = btn.dataset.act!;
        const id = this.menuTargetMsgId;
        if (!id) return;
        this.closeMenus();
        if (act.startsWith("ia-")) void this.acaoLocalNaMensagem(act, id);
        else if (act === "react") this.openReactPicker(id);
        else if (act === "reply") void this.startReply(id);
        else if (act === "forward") void this.startForward(id);
        else if (act === "edit") void this.handleEditMessage(id);
        else if (act === "delete") void this.handleDeleteMessage(id);
      };
    });
    // Tecla Esc fecha; scroll/click fora também (instalado on demand).
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.closeMenus();
    });
  }

  private wireReactPicker() {
    const picker = $("react-picker");
    picker.querySelectorAll<HTMLButtonElement>(".react-pick").forEach((btn) => {
      btn.onclick = (ev) => {
        ev.stopPropagation();
        const id = this.menuTargetMsgId;
        if (!id) return;
        if (btn.dataset.act === "more") {
          const custom = window.prompt("Reagir com qual emoji?");
          if (!custom) return;
          // pega o primeiro grapheme — segura emojis compostos quando possível
          const first = Array.from(custom)[0] ?? "";
          if (!first) return;
          this.closeMenus();
          void this.toggleReaction(id, first);
          return;
        }
        const emoji = btn.dataset.emoji!;
        this.closeMenus();
        void this.toggleReaction(id, emoji);
      };
    });
  }

  private openMessageMenu(triggerEl: HTMLElement, msgId: string, direction: "in" | "out") {
    this.closeMenus();
    const menu = $("msg-menu") as HTMLElement;
    menu.dataset.direction = direction;
    this.pintarAcoesLocais(menu, msgId, direction);
    menu.classList.remove("hidden");
    this.menuTargetMsgId = msgId;
    triggerEl.classList.add("is-open");
    this.positionPopover(menu, triggerEl);
    this.installOutsideHandler();
  }

  /** Quais ações do modelo aparecem para ESTA mensagem.
   *
   *  Contextual, e não um menu fixo: "responder por mim" não faz sentido numa
   *  mensagem que eu mesmo mandei, e "resumir" num "ok" é piada. Menu que
   *  oferece tudo sempre é menu que a pessoa aprende a ignorar. */
  private pintarAcoesLocais(menu: HTMLElement, msgId: string, direction: "in" | "out") {
    const itens = menu.querySelectorAll<HTMLElement>(".msg-menu-ia");
    const msg = this.messages.find((m) => m.id === msgId);
    const temModelo = iaContato.modelosParaConversa().length > 0;
    const texto = msg?.text ?? "";
    // Numa conversa que JÁ é com o modelo, estas ações seriam pedir ao modelo
    // que comente a si mesmo.
    const ehConversaLocal = !!this.currentContact && iaContato.ehContatoLocal(this.currentContact);

    // Postura depende de OUTRO modelo (o de pose, 9 MB) e de a mensagem ter
    // foto. Não é a mesma chave do modelo de conversa: quem baixou o Gemma não
    // tem, por isso, um detector de corpo.
    const ehImagem = (msg?.attachment?.mime ?? "").startsWith("image/");

    const mostrar: Record<string, boolean> = {
      "ia-postura": ehImagem && postura.disponivel(),
      "ia-responder": !!temModelo && direction === "in" && texto.length > 0,
      // 180 caracteres é onde um texto deixa de caber num relance no celular.
      // Abaixo disso, resumir custa mais tempo que ler.
      "ia-resumir": !!temModelo && texto.length > 180,
      "ia-explicar": !!temModelo && texto.length > 0,
    };
    itens.forEach((el) => {
      const act = el.dataset.act ?? "";
      el.classList.toggle("hidden", ehConversaLocal || !mostrar[act]);
    });
  }

  /** Executa uma ação do modelo sobre uma mensagem e mostra o rascunho.
   *
   *  Tudo cai no mesmo painel de rascunho da menção: uma resposta de modelo
   *  nunca entra na conversa sozinha, venha de onde vier. */
  private async acaoLocalNaMensagem(acao: string, msgId: string) {
    const msg = this.messages.find((m) => m.id === msgId);
    if (!msg) return;

    // Postura sai antes: não passa por modelo de linguagem nenhum. É um
    // detector de pontos do corpo e um pouco de trigonometria — pedir a um
    // modelo de texto que "interprete" os números só acrescentaria invenção
    // a uma medida que já é exata.
    if (acao === "ia-postura") {
      const painel = this.abrirRascunhoIa();
      const corpo = painel.querySelector<HTMLElement>(".rascunho-texto")!;
      corpo.textContent = "medindo a postura…";
      painel.querySelector<HTMLButtonElement>(".rascunho-enviar")!.hidden = true;
      try {
        const { texto } = await postura.analisar(msg.attachment!.blob);
        corpo.textContent = texto;
      } catch (e) {
        corpo.textContent = e instanceof Error ? e.message : String(e);
        registro.anotar("postura", corpo.textContent, "erro");
      }
      return;
    }

    const modelo = iaContato.modelosParaConversa()[0];
    if (!modelo) return;

    const pedido: Record<string, string> = {
      "ia-responder":
        "Escreva uma resposta curta e educada, em português do Brasil, para a mensagem abaixo. " +
        "Escreva só a resposta, sem explicação e sem aspas.",
      "ia-resumir":
        "Resuma a mensagem abaixo em no máximo duas frases, em português do Brasil. " +
        "Mantenha datas, horários, valores e nomes.",
      "ia-explicar":
        "Explique de forma simples, em português do Brasil, o que a mensagem abaixo quer dizer. " +
        "Se houver algum termo técnico ou sigla, explique o termo.",
    };
    const instrucao = pedido[acao];
    if (!instrucao) return;

    const painel = this.abrirRascunhoIa();
    const corpo = painel.querySelector<HTMLElement>(".rascunho-texto")!;
    corpo.textContent = iaContato.textoDaCarga();

    // Resumir a CONVERSA usa a janela larga e rasa; as outras ações operam
    // sobre a mensagem escolhida, e aí o texto exato dela é o que importa.
    const resumirConversa = acao === "ia-resumir";
    const historico: ChatMessage[] = resumirConversa
      ? this.messages.slice()
      : [
          {
            id: "pedido",
            contact: msg.contact,
            direction: "out",
            text: `${instrucao}\n\nMensagem:\n"""${msg.text}"""`,
            ts: Date.now(),
          },
        ];
    try {
      const comecouEm = Date.now();
      const { texto } = await iaContato.responder(
        modelo.arquivo,
        historico,
        (a) => {
          corpo.textContent =
            a.fase === "gerando" ? a.texto : iaContato.textoDaFase(a.fase, a.texto);
        },
        resumirConversa ? "resumo" : "conversa",
        msg.contact,
      );
      corpo.textContent = texto;
      this.marcarQuantoPensou(painel, Date.now() - comecouEm);
      // Só "responder por mim" vira mensagem; resumo e explicação são para
      // você entender, não para mandar de volta a quem escreveu.
      const enviar = painel.querySelector<HTMLButtonElement>(".rascunho-enviar")!;
      enviar.disabled = acao !== "ia-responder";
      if (acao !== "ia-responder") enviar.hidden = true;
    } catch (e) {
      corpo.textContent = e instanceof Error ? e.message : String(e);
      registro.anotar("ia-mensagem", corpo.textContent, "erro");
    }
  }

  // ---------- Agentes ----------

  /** Agentes criados pelo app vivem no domínio @agente.wuwei — dá para
   *  reconhecê-los sem consultar o servidor. */
  private ehAgente(email: string): boolean {
    return email.endsWith("@agente.wuwei");
  }

  /** A capability do agente: uma URL que é, ao mesmo tempo, o endereço e a
   *  autorização. Montada a partir da origem atual, então funciona igual em
   *  localhost e no IP da rede.
   *
   *  Não há mais nada a instalar do lado dele — era esse o ponto de trocar o
   *  MCP por URL. Falar é um GET; receber é o ouvinte do webhook, que o próprio
   *  readme manda subir onde a máquina puder manter um processo. */
  private comandoDoAgente(_email: string, token: string): string {
    // Três linhas: a chave, o manual e o cliente. Nada mais.
    //
    // O bloco de comandos que ficava aqui envelhecia a cada mudança no
    // protocolo, e o dono acabava colando uma versão velha. A capability já
    // sabe explicar a si mesma (`/` devolve o catálogo), e o readme mora ao
    // lado dela — trocar o token por "readme.md" acha o guia. O agente se vira.
    //
    // A terceira linha entrou porque falar e receber deixaram de ser
    // simétricos: falar é um GET, mas receber por webhook só funciona quando o
    // wuwei e o agente estão na mesma rede. Com o wuwei em nuvem, o callback
    // `127.0.0.1` faz o servidor bater em si mesmo — e o agente jura que está
    // ouvindo. O cliente do zip abre a conexão do lado dele, por WebSocket,
    // e não tem esse problema.
    return [
      `${location.origin}/w/${token}`,
      `${location.origin}/w/readme.md`,
      `${location.origin}/agent/wuwei_client.zip`,
    ].join("\n");
  }

  private async carregarAgentes() {
    const lista = document.getElementById("agents-list");
    if (!lista) return;
    try {
      const r = await fetch("/agents/list", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session: this.sess.session }),
      });
      const dados = await r.json();
      const agentes: Array<{
        email: string;
        name: string;
        token: string;
        createdTs?: number;
        lastSeenTs?: number;
        enviadas?: number;
        recebidas?: number;
      }> = dados.agents ?? [];
      const janela = Number(dados.statsDias ?? 7);
      if (!agentes.length) {
        lista.innerHTML = `<p class="agents-hint">Nenhum agente ainda.</p>`;
        return;
      }
      lista.innerHTML = agentes
        .map(
          (a) => `
        <div class="agent-row">
          <span class="agent-row-meta">
            <span class="agent-row-name">${escapeHtml(a.name)}</span>
            <span class="agent-row-email">${escapeHtml(a.email)}</span>
            <span class="agent-row-datas">criado ${escapeHtml(fmtQuando(a.createdTs))} · ${
              a.lastSeenTs
                ? `usado ${escapeHtml(fmtQuando(a.lastSeenTs))}`
                : `<b class="agent-row-nunca">nunca usado</b>`
            }</span>
            <span class="agent-row-stats" title="mensagens dos últimos ${janela} dias">↑ ${
              a.enviadas ?? 0
            } enviadas · ↓ ${a.recebidas ?? 0} recebidas <span class="agent-row-janela">(${janela}d)</span></span>
          </span>
          <button class="modal-btn agent-show" data-email="${escapeHtml(a.email)}" data-token="${escapeHtml(a.token)}" type="button">URL</button>
          <button class="modal-btn agent-revoke" data-email="${escapeHtml(a.email)}" type="button">Revogar</button>
        </div>`,
        )
        .join("");
      lista.querySelectorAll<HTMLButtonElement>(".agent-show").forEach((b) => {
        b.onclick = () => this.mostrarComando(b.dataset.email!, b.dataset.token!);
      });
      lista.querySelectorAll<HTMLButtonElement>(".agent-revoke").forEach((b) => {
        b.onclick = async () => {
          const email = b.dataset.email!;
          if (!confirm(`Revogar o acesso de ${email}? Ele perde a conexão.`)) return;
          await fetch("/agents/revoke", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session: this.sess.session, email }),
          });
          void this.carregarAgentes();
        };
      });
    } catch {
      lista.innerHTML = `<p class="agents-hint">Não consegui carregar a lista.</p>`;
    }
  }

  private mostrarComando(email: string, token: string) {
    const caixa = document.getElementById("agent-result");
    const pre = document.getElementById("agent-cmd");
    if (!caixa || !pre) return;
    pre.textContent = this.comandoDoAgente(email, token);
    caixa.classList.remove("hidden");
  }

  /** Página de agentes: mesma mecânica das outras views — esconde as irmãs,
   *  mostra a sua. Não é modal, então tem cabeçalho e botão de voltar. */
  /** Abre uma página de tela cheia (agentes, convite).
   *
   *  No celular a lista de conversas só desaparece quando o app marca que há
   *  algo ocupando a tela — sem esse marcador a página nova aparecia empilhada
   *  ABAIXO da lista, em vez de substituí-la. */
  private abrirPagina(id: string) {
    // A linha do tempo vive presa à JANELA (position: fixed), não à conversa —
    // então ela sobrevive à troca de tela e fica desenhando pílulas de hora
    // sobre páginas que não são conversa. Foi o que apareceu no print de
    // 7/ago/2026: "01h, 02h, 03h" espalhados pela tela de inferências.
    this.esconderLinhaDoTempo();
    for (const v of ["dashboard-view", "conv-view", "agents-view", "invite-view", "group-view", "inference-view", "agentes-locais-view", "profile-view"]) {
      document.getElementById(v)?.classList.add("hidden");
    }
    document.getElementById(id)?.classList.remove("hidden");
    const chat = document.getElementById("chat");
    if (chat) {
      // Sair do modo "conversa aberta": a regra de celular força a conversa a
      // aparecer enquanto esse marcador existir, e ela reaparecia POR BAIXO da
      // página nova — foi a sobreposição que apareceu na tela.
      delete chat.dataset.convOpen;
      chat.dataset.fullPage = id;
    }
  }

  /** Tira a linha do tempo da tela. Chamada em toda troca de página: os três
   *  elementos são fixos na janela e não somem sozinhos. */
  private esconderLinhaDoTempo(): void {
    // Cancelar o quadro agendado é o que faz a diferença. Um scroll disparado
    // ANTES da troca deixa um requestAnimationFrame pendente; ele roda DEPOIS
    // e reacende tudo — as marcas de hora voltavam à tela mesmo com este
    // método já tendo rodado. Foi o relato de 7/ago/2026: "o ícone some, os
    // números não".
    if (this.marcadorRaf !== null) {
      window.cancelAnimationFrame(this.marcadorRaf);
      this.marcadorRaf = null;
    }
    this.foraDaConversa = true;
    this.marcadorEl?.classList.remove("is-visible");
    this.alcaEl?.classList.remove("is-visible");
    this.reguaEl?.classList.remove("is-visible");
    // A régua guarda as marcas como filhas; esvaziar evita que um reflow as
    // mostre antes de a próxima atualização recalcular.
    if (this.reguaEl) this.reguaEl.innerHTML = "";
    this.reguaAssinatura = "";

  }

  /** Enquanto verdadeiro, a linha do tempo não desenha. Volta a false quando a
   *  conversa é aberta de novo. */
  private foraDaConversa = false;

  private fecharPagina() {
    this.esconderLinhaDoTempo();
    document.getElementById("agents-view")?.classList.add("hidden");
    document.getElementById("profile-view")?.classList.add("hidden");
    document.getElementById("invite-view")?.classList.add("hidden");
    document.getElementById("group-view")?.classList.add("hidden");
    document.getElementById("inference-view")?.classList.add("hidden");
    document.getElementById("agentes-locais-view")?.classList.add("hidden");
    const chat = document.getElementById("chat");
    if (chat) delete chat.dataset.fullPage;
    // Volta para onde o usuário estava: a conversa aberta, ou a lista.
    if (this.currentContact) {
      document.getElementById("conv-view")?.classList.remove("hidden");
      if (chat) chat.dataset.convOpen = "true";
    } else {
      this.showDashboardView();
    }
  }

  // ---------- Perfil ----------

  /** Perfis conhecidos, por email. Fica em memória porque a foto vem em base64
   *  e é lida a cada render da lista de contatos — buscar no servidor a cada
   *  desenho seria uma requisição por linha, por atualização. */
  private perfis = new Map<string, { name: string; bio?: string; avatar: string; ts: number }>();
  private perfisPedidos = new Set<string>();

  private wirePerfil() {
    document.getElementById("profile-btn")?.addEventListener("click", () => {
      // O menu se fecha sozinho no clique de fora; aqui basta abrir a página.
      document.getElementById("app-menu")?.classList.add("hidden");
      document.getElementById("menu-btn")?.setAttribute("aria-expanded", "false");
      this.abrirPerfil();
    });
    document.getElementById("profile-back")?.addEventListener("click", () => this.fecharPagina());

    // O toque no avatar abre a galeria. O roteador escuta `pointerup` e
    // `touchend` além do `click` porque no celular o `click` pode não chegar, e
    // a trava de tempo faz o mesmo gesto abrir o seletor uma vez só.
    const avatarBtn = document.getElementById("profile-foto-btn");
    diag(this.sess.email, "wirePerfil", { achouBotao: !!avatarBtn });
    let ultimoDisparo = 0;
    const escolherFoto = async (ev: Event) => {
      const agora = Date.now();
      if (agora - ultimoDisparo < 700) {
        diag(this.sess.email, "ignorado-pela-trava", { tipo: ev.type });
        return;
      }
      ultimoDisparo = agora;
      ev.preventDefault();
      diag(this.sess.email, "vou-abrir-o-seletor", { tipo: ev.type });
      let f: File | null = null;
      try {
        f = await abrirSeletorDeImagem();
      } catch (e) {
        diag(this.sess.email, "seletor-estourou", { erro: String((e as Error)?.message || e) });
        this.avisoPerfil("o navegador não abriu a galeria");
        return;
      }
      diag(this.sess.email, "seletor-respondeu", {
        temArquivo: !!f,
        nome: f?.name,
        bytes: f?.size,
        tipo: f?.type,
      });
      if (!f) return;
      try {
        const reduzida = await reduzirImagem(f);
        this.perfilFotoNova = reduzida;
        this.pintarFotoPerfil(reduzida);
        this.revisarBotaoPerfil();
        diag(this.sess.email, "foto-reduzida", { bytes: reduzida.length });
      } catch (e) {
        diag(this.sess.email, "reducao-falhou", { erro: String((e as Error)?.message || e) });
        this.avisoPerfil("não consegui ler essa imagem");
      }
    };
    const rotear = (e: Event) => {
      diag(this.sess.email, "toque-no-avatar", { tipo: e.type });
      void escolherFoto(e);
    };
    avatarBtn?.addEventListener("pointerup", rotear);
    avatarBtn?.addEventListener("touchend", rotear, { passive: false });
    avatarBtn?.addEventListener("click", rotear);
    // Registra o começo do gesto também: se `pointerdown` chega e `pointerup`
    // não, o dedo está sendo interpretado como arrasto/rolagem, e o conserto é
    // outro — não adianta trocar mais uma vez a forma de abrir o seletor.
    avatarBtn?.addEventListener("pointerdown", (e) => diag(this.sess.email, "pointerdown", { tipo: e.type }));
    avatarBtn?.addEventListener("touchstart", (e) => diag(this.sess.email, "touchstart", { tipo: e.type }));
    avatarBtn?.addEventListener("keydown", (ev) => {
      const e = ev as KeyboardEvent;
      if (e.key === "Enter" || e.key === " ") void escolherFoto(e);
    });
    // O campo de arquivo de verdade: o dedo cai nele, o navegador abre a galeria
    // sem passar por clique sintético — que é o que o Chrome do Android recusa.
    const inputReal = document.getElementById("profile-foto-real") as HTMLInputElement | null;
    inputReal?.addEventListener("change", () => {
      const f = inputReal.files?.[0] ?? null;
      diag(this.sess.email, "input-real-change", { temArquivo: !!f, bytes: f?.size, tipo: f?.type });
      // Zerar deixa escolher o MESMO arquivo de novo: sem isto, um segundo
      // toque na mesma foto não dispara `change` nenhum.
      inputReal.value = "";
      if (!f) return;
      void (async () => {
        try {
          const reduzida = await reduzirImagem(f);
          this.perfilFotoNova = reduzida;
          this.pintarFotoPerfil(reduzida);
          this.revisarBotaoPerfil();
          diag(this.sess.email, "input-real-foto-pronta", { bytes: reduzida.length });
        } catch (e) {
          diag(this.sess.email, "input-real-reducao-falhou", { erro: String((e as Error)?.message || e) });
          this.avisoPerfil("não consegui ler essa imagem");
        }
      })();
    });

    document.getElementById("profile-foto-remove")?.addEventListener("click", () => {
      this.perfilFotoNova = "";
      this.pintarFotoPerfil("");
      this.revisarBotaoPerfil();
    });
    document.getElementById("profile-nome")?.addEventListener("input", () => this.revisarBotaoPerfil());
    document.getElementById("profile-bio")?.addEventListener("input", () => {
      this.contarBioPerfil();
      this.revisarBotaoPerfil();
    });
    document.getElementById("profile-salvar")?.addEventListener("click", () => void this.salvarPerfil());
  }

  private perfilFotoNova: string | null = null;
  /** O que estava na tela quando ela abriu — a régua para saber se há mudança. */
  private perfilNomeOriginal = "";
  private perfilBioOriginal = "";
  /** Se há foto na tela agora. Guardado aqui em vez de lido do DOM: o atalho
   *  `style.background` escreve `backgroundImage: none`, e ler isso como
   *  "tem foto" deixava o "remover" visível sobre um avatar vazio. */
  private perfilTemFoto = false;

  /** "Salvar" só acende quando existe o que salvar. Botão sempre aceso sobre um
   *  formulário intocado promete uma ação que não faz nada. E "remover foto"
   *  some quando não há foto, pelo mesmo motivo. */
  private revisarBotaoPerfil() {
    const nome = (document.getElementById("profile-nome") as HTMLInputElement | null)?.value.trim() ?? "";
    const bio = (document.getElementById("profile-bio") as HTMLTextAreaElement | null)?.value.trim() ?? "";
    const mudouNome = nome !== this.perfilNomeOriginal;
    const mudouBio = bio !== this.perfilBioOriginal;
    const mudouFoto = this.perfilFotoNova !== null;
    const btn = document.getElementById("profile-salvar") as HTMLButtonElement | null;
    if (btn) btn.disabled = !mudouNome && !mudouBio && !mudouFoto;

    const remover = document.getElementById("profile-foto-remove") as HTMLElement | null;
    if (remover) remover.hidden = !this.perfilTemFoto;
  }

  private abrirPerfil() {
    diag(this.sess.email, "abriu-a-tela-de-perfil");
    this.abrirPagina("profile-view");
    const meu = this.perfis.get(this.sess.email);
    this.perfilFotoNova = null;
    this.perfilNomeOriginal = meu?.name ?? "";
    this.perfilBioOriginal = meu?.bio ?? "";
    (document.getElementById("profile-nome") as HTMLInputElement).value = this.perfilNomeOriginal;
    const campoBio = document.getElementById("profile-bio") as HTMLTextAreaElement | null;
    if (campoBio) campoBio.value = this.perfilBioOriginal;
    ($("profile-email") as HTMLElement).textContent = this.sess.email;
    this.pintarFotoPerfil(meu?.avatar ?? "");
    this.avisoPerfil("");
    this.contarBioPerfil();
    this.revisarBotaoPerfil();
    // Busca o próprio perfil de novo: em outro aparelho ele pode ter mudado, e
    // abrir a tela com o valor velho convida a salvar por cima sem querer.
    void this.carregarPerfis([this.sess.email], true).then(() => {
      const atual = this.perfis.get(this.sess.email);
      if (!atual || this.perfilFotoNova !== null) return;
      this.perfilNomeOriginal = atual.name;
      this.perfilBioOriginal = atual.bio ?? "";
      (document.getElementById("profile-nome") as HTMLInputElement).value = atual.name;
      const campo = document.getElementById("profile-bio") as HTMLTextAreaElement | null;
      if (campo) campo.value = this.perfilBioOriginal;
      this.pintarFotoPerfil(atual.avatar);
      this.contarBioPerfil();
      this.revisarBotaoPerfil();
    });
  }

  private pintarFotoPerfil(dataUrl: string) {
    this.perfilTemFoto = !!dataUrl;
    const el = $("profile-foto") as HTMLElement;
    if (dataUrl) {
      el.style.backgroundImage = `url("${dataUrl}")`;
      el.textContent = "";
    } else {
      el.style.backgroundImage = "";
      el.style.background = colorFor(this.sess.email);
      el.textContent = avatarInitials(this.sess.email);
    }
  }

  /** O limite existe do lado do servidor de qualquer forma; mostrar quanto
   *  falta evita que a frase seja cortada sem aviso na hora de salvar. */
  private contarBioPerfil() {
    const campo = document.getElementById("profile-bio") as HTMLTextAreaElement | null;
    const conta = document.getElementById("profile-bio-conta");
    if (!campo || !conta) return;
    conta.textContent = `${campo.value.length}/140`;
  }

  private avisoPerfil(texto: string) {
    const el = document.getElementById("profile-aviso") as HTMLElement | null;
    if (!el) return;
    el.textContent = texto;
    el.hidden = !texto;
  }

  private async salvarPerfil() {
    const nome = (document.getElementById("profile-nome") as HTMLInputElement).value.trim();
    const bio = (document.getElementById("profile-bio") as HTMLTextAreaElement | null)?.value.trim() ?? "";
    const corpo: Record<string, unknown> = { session: this.sess.session, name: nome, bio };
    // `null` = não mexeu na foto; "" = pediu para remover. Mandar sempre o que
    // está na tela apagaria a foto de quem só editou o nome num aparelho que
    // ainda não tinha carregado a imagem.
    if (this.perfilFotoNova !== null) corpo.avatar = this.perfilFotoNova;
    else corpo.avatar = this.perfis.get(this.sess.email)?.avatar ?? "";
    try {
      const r = await fetch("/profile/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(corpo),
      });
      diag(this.sess.email, "salvou", { status: r.status, temFotoNova: this.perfilFotoNova !== null });
      if (!r.ok) {
        const erro = (await r.json().catch(() => ({}))) as { error?: string };
        this.avisoPerfil(erro.error || `não salvou (HTTP ${r.status})`);
        return;
      }
      await this.carregarPerfis([this.sess.email], true);
      this.perfilFotoNova = null;
      this.perfilNomeOriginal = nome;
      this.perfilBioOriginal = bio;
      this.revisarBotaoPerfil();
      this.renderContacts();
      // O cabeçalho da conversa aberta mostra foto e descrição: sem isto, o
      // que acabou de ser salvo só apareceria na próxima troca de conversa.
      if (this.currentContact) this.pintarCabecalhoConversa();
      this.avisoPerfil("salvo");
    } catch {
      this.avisoPerfil("sem conexão — tente de novo quando voltar");
    }
  }

  /** Busca perfis que ainda não conhecemos (ou força, depois de salvar). */
  private async carregarPerfis(emails: string[], forcar = false): Promise<void> {
    const faltando = emails
      .map((e) => e.toLowerCase())
      .filter(
        (e) =>
          e &&
          !e.startsWith("g:") &&
          // Modelo do aparelho não tem perfil no servidor, e perguntar por ele
          // seria contar ao servidor quais modelos existem neste telefone. Foi
          // exatamente o que passou a acontecer quando eles entraram na lista:
          // cada desenho da lista disparava um `/profile/list` com
          // "local:gemma-…" dentro, o servidor respondia sem o perfil, e o
          // pedido nascia de novo no render seguinte. Além do vazamento, o
          // laço mantinha a rede ocupada — foi assim que o teste do app
          // abrindo offline passou a estourar por `networkidle`.
          !iaContato.ehContatoLocal(e) &&
          (forcar || (!this.perfis.has(e) && !this.perfisPedidos.has(e))),
      );
    if (!faltando.length) return;
    faltando.forEach((e) => this.perfisPedidos.add(e));
    try {
      const r = await fetch("/profile/list", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session: this.sess.session, emails: faltando }),
      });
      if (!r.ok) return;
      const dados = (await r.json()) as {
        profiles: Record<string, { name: string; bio?: string; avatar: string; ts: number }>;
      };
      let mudou = false;
      for (const [email, p] of Object.entries(dados.profiles ?? {})) {
        this.perfis.set(email, p);
        mudou = true;
      }
      if (mudou) {
        this.renderContacts();
        // O cabeçalho da conversa aberta também bebe daqui. Sem esta parte, quem
        // abrisse a conversa ANTES de o perfil chegar ficava com o email no topo
        // e sem a descrição até sair da conversa e voltar — o perfil já estava
        // certo no servidor, só não havia quem repintasse o cabeçalho.
        if (this.currentContact) {
          const head = document.getElementById("chat-header-name");
          const nome = this.displayName(this.currentContact);
          // Mesma guarda do render: regravar o texto zera a rolagem horizontal
          // de um nome comprido.
          if (head && head.textContent !== nome) head.textContent = nome;
          this.pintarCabecalhoConversa();
          this.renderChatHeaderPresence();
        }
      }
    } catch {
      // Sem rede: as iniciais continuam valendo, que é o estado de sempre.
      faltando.forEach((e) => this.perfisPedidos.delete(e));
    }
  }

  private abrirAgentes() {
    this.abrirPagina("agents-view");
    document.getElementById("agent-result")?.classList.add("hidden");
    void this.carregarAgentes();
  }

  private fecharAgentes() {
    this.fecharPagina();
  }

  /** Traduz o código cru da Web Speech para algo acionável. */
  private explicarErroFalaImpl(codigo: string): string {
    switch (codigo) {
      case "network":
        return "sem acesso ao serviço do Google (ele precisa de internet)";
      case "not-allowed":
      case "service-not-allowed":
        return "o app não tem permissão de microfone para o reconhecimento";
      case "no-speech":
        return "não ouviu fala nenhuma";
      case "aborted":
        return "foi interrompido antes de terminar";
      case "audio-capture":
        return "não conseguiu abrir o microfone";
      case "não respondeu — o motor existe mas está inerte":
        return "o motor existe mas não responde — é o caso do app instalado";
      default:
        return codigo;
    }
  }

  /* ── Diagnóstico do aparelho, num botão só ─────────────────────────────────
   *
   * Até 7/ago/2026 esta tela tinha três painéis com botão próprio — medir o
   * aparelho, medir o teto de memória da aba, e a bateria de modelos — e cada
   * um fazia um pedaço da mesma pergunta: "o que dá para ligar aqui?". Pior:
   * havia uma ordem obrigatória entre eles (sem teto medido a bateria se
   * recusa a começar) e essa ordem estava escrita só no aviso de erro. Quem
   * chegava na tela descobria a sequência errando.
   *
   * A sequência é do programa, não de quem usa. Aqui ela virou um botão.
   *
   * As defesas não mudaram de lugar: continuam em `banco-de-provas.ts` — a
   * bateria recusa começar sem teto, para no primeiro que falha, deixa rastro
   * antes de cada tentativa e pausa entre modelos. O que mudou foi quem
   * dispara, não o que protege.
   */

  /** O que foi medido do aparelho nesta sessão.
   *
   *  Null até o botão ser tocado. Medir pede adapter de GPU e roda um cálculo
   *  pesado; cobrar isso de quem abriu a tela só para desligar uma chave seria
   *  cobrar por uma resposta que ninguém pediu. */
  private diagnosticoCache: DiagnosticoAparelho | null = null;

  /** Em que ponto da sequência o diagnóstico está. "parado" cobre o antes e o
   *  depois — quem separa os dois é `diagJaRodou`. */
  private diagFase: "parado" | "aparelho" | "teto" | "bateria" = "parado";
  private diagJaRodou = false;

  /** Uma linha por modelo, na ordem em que a bateria os visitou. É daqui que
   *  sai tanto o resultado enxuto quanto o detalhamento. */
  private diagPassos: { nome: string; estado: EstadoDoPasso; texto: string }[] = [];

  /** Por que a bateria se recusou a rodar, quando se recusou.
   *
   *  Acontece quando o teto não pôde ser medido: sem esse número não existe
   *  margem de segurança, e rodar sem margem é como o app fechou da primeira
   *  vez. Sem guardar aqui, a sequência terminaria calada. */
  private diagRecusa: string | null = null;

  /** Linhas prontas para colar, acumuladas conforme os testes rodam.
   *
   *  Substitui o envio automático para um coletor na rede: o app é servido por
   *  HTTPS e o coletor era HTTP, então o navegador bloqueava o POST como mixed
   *  content — o caminho não podia funcionar. Copiar e colar não depende de
   *  rede, de estar no mesmo wifi, nem de certificado. */
  private provasColhidas: string[] = [];

  /** Quem fechou o app na sessão anterior, guardado para a tela inteira.
   *
   *  `provaQueDerrubou()` lê e limpa — o rastro serve uma vez. Sem guardar
   *  aqui, o aviso sumiria no primeiro redesenho da lista (basta mexer numa
   *  chave) e a pessoa nunca saberia por que o recurso apareceu reprovado. */
  private vitimaDaSessao: { id?: string; nome: string; dtype: string; quando: string } | null =
    null;
  private vitimaLida = false;
  /** Pedido de parada da bateria: lido entre um modelo e o seguinte. */
  private pararBateria = false;
  private bateriaRodando = false;

  /** A sequência inteira, na ordem em que uma resposta depende da anterior.
   *
   *  Mede o aparelho, mede o teto que esta aba aguenta e só então roda a
   *  bateria — que sem o teto se recusaria a começar. Encadear aqui é o que
   *  torna o botão único possível: a dependência deixou de ser combinado com
   *  quem usa e passou a ser código. */
  private async rodarDiagnostico() {
    if (this.bateriaRodando) return;
    this.bateriaRodando = true;
    this.pararBateria = false;
    this.diagRecusa = null;
    this.diagPassos = [];
    this.provasColhidas = [];
    const ganchos = window.__wuweiDiagnostico;
    try {
      this.diagFase = "aparelho";
      this.renderPainelDiagnostico();
      this.diagnosticoCache = await diagnosticar();

      this.diagFase = "teto";
      this.renderPainelDiagnostico();
      const teto = await (ganchos?.medirTeto ?? medirTetoAlocavel)();
      this.diagnosticoCache.tetoAlocavelMb = teto;

      this.diagFase = "bateria";
      this.renderPainelDiagnostico();
      const plano = await rodarBateria(
        teto,
        (a) => {
          this.diagPassos[a.indice] = {
            nome: a.prova.nome,
            estado: a.estado,
            texto: a.texto,
          };
          // Escrita direta no lugar do redesenho: a bateria fala dezenas de
          // vezes por modelo, e refazer o painel a cada fala faria a página
          // pular debaixo do dedo de quem está lendo.
          const linha = document.getElementById("provas-andamento");
          if (linha) {
            linha.textContent = `${a.indice + 1}/${a.total} · ${a.prova.nome}: ${a.texto}`;
            linha.dataset.estado = a.estado;
          }
        },
        () => this.pararBateria,
        // O executor de produção é `rodarProva`; os testes trocam por um falso
        // para provar o que a TELA faz com um resultado, sem baixar meio giga
        // por asserção.
        async (p, aoAndar) => {
          const executar = ganchos?.executarProva ?? rodarProva;
          const r = await executar(p, aoAndar);
          this.provasColhidas.push(
            `${p.nome} [${p.dtype}]: ${r.custoMb} MB (carregar ${r.carregarMb} MB), ${r.totalSeg}s` +
              (p.refMb ? ` · container ${p.refMb} MB` : ""),
          );
          void this.mandarResultado(p, r, teto);
          return r;
        },
      );
      this.diagRecusa = plano.recusa;
      this.diagJaRodou = true;
    } finally {
      this.diagFase = "parado";
      this.bateriaRodando = false;
      // Redesenha a lista de chaves junto: é lá que o resultado tem
      // consequência, e ela precisa aparecer no mesmo instante que o veredito.
      this.renderInferencias();
    }
  }

  /** Liga o que a bateria aprovou e desliga o que ela reprovou.
   *
   *  Fica atrás de um toque, e não sai no fim da bateria sozinho, porque ligar
   *  recurso é gastar memória do app: a medição diz o que CABE, não o que a
   *  pessoa quer carregando. */
  private aplicarVeredito() {
    for (const r of RECURSOS) {
      const estado = vereditoDe(r.id)?.estado ?? "naotestado";
      if (estado === "cabe" && !indisponivel(r.id)) setRecurso(r.id, true);
      // Reprovado já é desligado por `marcarVeredito`; repetir aqui é barato e
      // fecha o caso de um veredito que chegou por outro caminho.
      if (estado === "naocabe") setRecurso(r.id, false);
    }
    this.renderInferencias();
  }

  /** O painel inteiro, desenhado a partir do estado — não há dois caminhos de
   *  desenho que possam discordar um do outro. */
  private renderPainelDiagnostico() {
    const alvo = document.getElementById("device-diag");
    if (!alvo) return;
    const d = this.diagnosticoCache;
    const teto = d?.tetoAlocavelMb ?? null;
    // O rastro precisa virar veredito ANTES de o placar ser somado, senão o
    // recurso que fechou o app aparece contado como "não testado".
    this.colherVitima(teto);
    const rodando = this.diagFase !== "parado";
    alvo.textContent = "";

    const frase = document.createElement("p");
    frase.className = "device-diag-resumo";
    frase.textContent = d
      ? resumo(d)
      : "Um toque mede este aparelho, mede quanta memória esta aba aguenta e tenta " +
        "cada modelo, do mais leve ao mais pesado — e no fim diz o que dá para ligar.";
    alvo.appendChild(frase);

    const placar = document.createElement("p");
    placar.className = "provas-placar";
    placar.id = "provas-placar";
    const v = lerVereditos();
    if (v) {
      const contar = (e: EstadoVeredito) =>
        RECURSOS.filter((r) => (v.itens[r.id]?.estado ?? "naotestado") === e).length;
      const quando = new Date(v.quando).toLocaleString("pt-BR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
      placar.textContent =
        `Medido neste aparelho em ${quando}: ${contar("cabe")} cabem, ` +
        `${contar("naocabe")} não cabem, ${contar("naotestado")} não testados.`;
    } else {
      placar.textContent = "Ainda não foi medido o que cabe neste aparelho.";
    }
    alvo.appendChild(placar);

    if (this.vitimaDaSessao) {
      const vitima = this.vitimaDaSessao;
      const aviso = document.createElement("p");
      aviso.className = "prova-res prova-ruim provas-vitima";
      aviso.textContent =
        `Da última vez o app fechou durante "${vitima.nome}" (${vitima.dtype}). ` +
        `Isso conta como resultado: esse modelo, e todos os mais pesados, foram marcados como ` +
        `"não cabe" aqui.`;
      alvo.appendChild(aviso);
    }

    const rodar = document.createElement("button");
    rodar.type = "button";
    rodar.id = "provas-auto";
    rodar.className = "device-diag-btn provas-auto";
    rodar.textContent = rodando
      ? "rodando o diagnóstico…"
      : this.diagJaRodou || v
        ? "Rodar diagnóstico de novo"
        : "Rodar diagnóstico";
    rodar.disabled = rodando;
    rodar.onclick = () => void this.rodarDiagnostico();
    alvo.appendChild(rodar);

    if (rodando) {
      const parar = document.createElement("button");
      parar.type = "button";
      parar.id = "provas-parar";
      parar.className = "device-diag-btn provas-parar";
      parar.textContent = "parar";
      parar.onclick = () => {
        this.pararBateria = true;
        parar.disabled = true;
        const linha = document.getElementById("provas-andamento");
        if (linha) linha.textContent = "parando depois do modelo atual…";
      };
      alvo.appendChild(parar);
    }

    if (!rodando && !this.diagJaRodou) {
      const aviso = document.createElement("p");
      aviso.className = "device-diag-aviso";
      aviso.textContent =
        "Demora alguns minutos e baixa de 10 a 250 MB por modelo na primeira vez. " +
        "Reserva blocos de memória cada vez maiores para achar o teto desta aba: em " +
        "aparelho apertado, o app pode fechar durante o teste — e se fechar, o que " +
        "já foi medido fica gravado.";
      alvo.appendChild(aviso);
    }

    if (rodando || this.diagJaRodou) alvo.appendChild(this.montarEtapas());

    const andamento = document.createElement("p");
    andamento.className = "prova-res provas-andamento";
    andamento.id = "provas-andamento";
    if (this.diagFase === "aparelho") andamento.textContent = "medindo o aparelho…";
    if (this.diagFase === "teto") {
      andamento.textContent = "medindo a memória que esta aba aguenta…";
    }
    alvo.appendChild(andamento);

    if (this.diagRecusa) {
      const recusa = document.createElement("p");
      recusa.className = "prova-res prova-ruim provas-recusa";
      recusa.id = "provas-recusa";
      recusa.textContent = this.diagRecusa;
      alvo.appendChild(recusa);
    }

    if (!rodando && this.diagJaRodou && !this.diagRecusa) {
      alvo.appendChild(this.montarResultado(teto));
    }

    alvo.appendChild(this.montarDetalhes(d, teto));
  }

  /** As três medições, na ordem, com o que já ficou pronto marcado.
   *
   *  Existe porque a sequência é longa e cada etapa cala por minutos: sem ver
   *  onde está, "parado" e "trabalhando" ficam iguais na tela. */
  private montarEtapas(): HTMLElement {
    const ordem: { chave: "aparelho" | "teto" | "bateria"; texto: string }[] = [
      { chave: "aparelho", texto: "medir o aparelho" },
      { chave: "teto", texto: "medir a memória desta aba" },
      { chave: "bateria", texto: "tentar cada modelo, do mais leve ao mais pesado" },
    ];
    const lista = document.createElement("ol");
    lista.className = "diag-etapas";
    lista.id = "diagnostico-etapas";
    const atual = ordem.findIndex((e) => e.chave === this.diagFase);
    ordem.forEach((e, i) => {
      const li = document.createElement("li");
      li.textContent = e.texto;
      li.dataset.etapa = e.chave;
      // A bateria que se recusou não é etapa cumprida. Marcá-la de verde seria
      // a tela afirmando uma medição que não houve.
      li.dataset.estado =
        this.diagRecusa && e.chave === "bateria"
          ? "recusado"
          : this.diagFase === "parado"
            ? "feito"
            : i < atual
              ? "feito"
              : i === atual
                ? "fazendo"
                : "espera";
      lista.appendChild(li);
    });
    return lista;
  }

  /** O resultado em duas linhas e uma oferta.
   *
   *  Enxuto de propósito: o que decide é quantos cabem e quais. O porquê de
   *  cada veredito continua no selo de cada chave, e a medição bruta em "ver
   *  detalhes" — quem discorda tem onde conferir sem que quem concorda precise
   *  ler. */
  private montarResultado(teto: number | null): HTMLElement {
    const cx = document.createElement("div");
    cx.className = "diag-resultado";
    cx.id = "diagnostico-resultado";

    const estadoDe = (id: RecursoId): EstadoVeredito => vereditoDe(id)?.estado ?? "naotestado";
    const cabem = RECURSOS.filter((r) => estadoDe(r.id) === "cabe");
    const naoCabem = RECURSOS.filter((r) => estadoDe(r.id) === "naocabe");

    const linha = (classe: string, texto: string) => {
      const p = document.createElement("p");
      p.className = `prova-res ${classe}`;
      p.textContent = texto;
      cx.appendChild(p);
    };

    if (cabem.length) {
      linha("prova-ok", `Cabem aqui: ${cabem.map((r) => r.nome).join(", ")}.`);
    } else {
      linha(
        "prova-ruim",
        teto == null
          ? "Nenhum modelo pôde ser tentado — não deu para medir a memória desta aba."
          : `Nenhum modelo coube na margem deste aparelho (teto medido: ${teto} MB).`,
      );
    }
    if (naoCabem.length) {
      linha("prova-ruim", `Não cabem: ${naoCabem.map((r) => r.nome).join(", ")}.`);
    }

    if (cabem.length) {
      const pendentes = cabem.filter((r) => !recursoAtivo(r.id));
      const btn = document.createElement("button");
      btn.type = "button";
      btn.id = "diagnostico-aplicar";
      btn.className = "device-diag-btn diag-aplicar";
      btn.textContent = pendentes.length
        ? pendentes.length === 1
          ? "ligar o recurso que coube"
          : `ligar os ${pendentes.length} recursos que couberam`
        : "já está ligado o que coube";
      btn.disabled = pendentes.length === 0;
      btn.onclick = () => this.aplicarVeredito();
      cx.appendChild(btn);

      // Cabe um por um não é o mesmo que cabem todos juntos. O app tem um
      // orçamento só, e a soma é o número que decide se ligar tudo é escolha
      // ou aposta.
      const soma = cabem.reduce((s, r) => s + (r.heapMb ?? 0), 0);
      if (soma > ORCAMENTO_MB) {
        const nota = document.createElement("p");
        nota.className = "device-diag-aviso";
        nota.textContent =
          `Cada um coube sozinho, mas somados dão ~${soma} MB e o app tem ~${ORCAMENTO_MB} MB. ` +
          `Eles não carregam todos ao mesmo tempo; ligar tudo é apostar que nunca vão coincidir.`;
        cx.appendChild(nota);
      }
    }

    return cx;
  }

  /** O que explica um veredito ruim, fora do caminho de quem só quer o veredito.
   *
   *  Some da vista, não do DOM nem da tela: é a tabela de GPU, núcleos e SIMD
   *  que responde "por que este aparelho reprovou tanta coisa?" — e essa
   *  pergunta chega depois do resultado, nunca antes. */
  private montarDetalhes(d: DiagnosticoAparelho | null, teto: number | null): HTMLElement {
    const det = document.createElement("details");
    det.className = "diag-detalhes";
    det.id = "diagnostico-detalhes";
    const cab = document.createElement("summary");
    cab.textContent = "ver detalhes";
    det.appendChild(cab);

    if (d) det.appendChild(this.montarTabelaAparelho(d));

    if (this.diagPassos.length) {
      const provas = document.createElement("div");
      provas.className = "provas";
      const titulo = document.createElement("h4");
      titulo.className = "provas-titulo";
      titulo.textContent = "Modelo a modelo";
      provas.appendChild(titulo);
      const plano = planejarBateria(teto);
      if (plano.limiteMb != null) {
        const margem = document.createElement("p");
        margem.className = "device-diag-aviso provas-margem";
        margem.textContent =
          `A bateria só tenta o que custa até ${plano.limiteMb} MB — ` +
          `${Math.round(FOLGA_DO_TETO * 100)}% dos ${teto} MB que esta aba conseguiu reservar. ` +
          `O resto é declarado "não cabe" sem ser tentado: passar do teto não dá erro, dá app fechado.`;
        provas.appendChild(margem);
      }
      for (const passo of this.diagPassos) {
        if (!passo) continue;
        const item = document.createElement("div");
        item.className = "prova";
        item.dataset.estado = passo.estado;
        const nome = document.createElement("div");
        nome.className = "prova-nome";
        nome.textContent = passo.nome;
        const res = document.createElement("p");
        res.className = `prova-res ${passo.estado === "cabe" ? "prova-ok" : passo.estado === "naocabe" ? "prova-ruim" : ""}`;
        res.textContent = passo.texto;
        item.append(nome, res);
        provas.appendChild(item);
      }
      det.appendChild(provas);
    }

    // Campo em vez de localStorage pelo console: dentro do WebView não há
    // console para colar comando, e sem isto o resultado só existe na tela —
    // vira captura de tela, que não soma nem compara.
    const coletor = document.createElement("input");
    coletor.type = "url";
    coletor.className = "provas-coletor";
    coletor.placeholder = "http://192.168.1.x:8210 — para onde mandar os resultados (opcional)";
    coletor.value = localStorage.getItem("wuwei.coletor") ?? "";
    coletor.onchange = () => {
      const valor = coletor.value.trim();
      if (valor) localStorage.setItem("wuwei.coletor", valor);
      else localStorage.removeItem("wuwei.coletor");
      coletor.blur();
    };
    det.appendChild(coletor);

    const copiar = document.createElement("button");
    copiar.type = "button";
    copiar.className = "device-diag-btn provas-copiar";
    copiar.textContent = "copiar resultados";
    copiar.onclick = async () => {
      const texto =
        `aparelho: ${navigator.userAgent}\n` +
        `nucleos: ${navigator.hardwareConcurrency ?? "?"} · teto: ${teto ?? "não medido"} MB\n` +
        (this.provasColhidas.length ? this.provasColhidas.join("\n") : "(nenhum teste rodado ainda)");
      try {
        await navigator.clipboard.writeText(texto);
        copiar.textContent = "copiado";
      } catch {
        // Sem permissão de área de transferência, mostrar o texto ainda
        // permite selecionar à mão — melhor que falhar em silêncio.
        const cru = document.createElement("pre");
        cru.className = "provas-cru";
        cru.textContent = texto;
        det.appendChild(cru);
        copiar.textContent = "não consegui copiar — o texto está abaixo";
      }
      setTimeout(() => (copiar.textContent = "copiar resultados"), 3000);
    };
    det.appendChild(copiar);

    return det;
  }

  /** A tabela do aparelho. "não dá para saber" é uma resposta legítima e
   *  aparece como tal: a detecção antiga convertia ausência de dado em veredito
   *  ruim, e foi assim que todo iPhone virava o pior aparelho possível. */
  private montarTabelaAparelho(d: DiagnosticoAparelho): HTMLElement {
    const desconhecido = "não dá para saber";
    const sn = (v: string) => (v === "sim" ? "sim" : v === "nao" ? "não" : desconhecido);
    const linhas: [string, string][] = [
      ["GPU para o navegador", d.webgpuInfo ? `${sn(d.webgpu)} — ${d.webgpuInfo}` : sn(d.webgpu)],
      ["Maior modelo por vez", d.webgpuMaxBufferMb ? `~${d.webgpuMaxBufferMb} MB` : desconhecido],
      ["Núcleos", d.cores > 0 ? String(d.cores) : desconhecido],
      [
        "RAM do aparelho inteiro",
        d.ramReportadaGb != null
          ? `${d.ramReportadaGb} GB — do aparelho, não desta aba${d.pareceCelular ? "; no celular o navegador nunca informa mais que 8" : ""}`
          : `${desconhecido} — este navegador não informa`,
      ],
      ["Teto do heap do JavaScript", d.heapLimiteMb ? `${d.heapLimiteMb} MB (o modelo não usa este espaço)` : desconhecido],
      [
        "Maior bloco que esta aba reservou",
        d.tetoAlocavelMb != null
          ? `${d.tetoAlocavelMb} MB — é este o número que decide se o modelo cabe`
          : "não medido",
      ],
      [
        "Espaço para guardar modelo",
        d.armazenamentoLivreMb != null ? `${d.armazenamentoLivreMb} MB livres` : desconhecido,
      ],
      ["Processador com SIMD", sn(d.wasmSimd)],
      ["Vários núcleos no cálculo", sn(d.wasmThreads)],
      ["Prova de velocidade", d.provaMs != null && d.provaMs >= 0 ? `${d.provaMs} ms` : desconhecido],
      ["Conexão", d.conexao ?? desconhecido],
    ];
    const tabela = document.createElement("dl");
    tabela.className = "device-diag-grid";
    for (const [rotulo, valor] of linhas) {
      const dt = document.createElement("dt");
      dt.textContent = rotulo;
      const dd = document.createElement("dd");
      dd.textContent = valor;
      tabela.append(dt, dd);
    }
    return tabela;
  }

  /** Lê o rastro de quem fechou o app e o transforma em veredito.
   *
   *  Só uma vez por sessão: `provaQueDerrubou()` limpa o rastro ao ler, e esta
   *  função é chamada a cada redesenho da tela. */
  private colherVitima(tetoMb: number | null) {
    if (this.vitimaLida) return;
    this.vitimaLida = true;
    const vitima = provaQueDerrubou();
    if (!vitima) return;
    this.vitimaDaSessao = vitima;
    condenarQuemDerrubou(vitima, tetoMb);
    const destino = localStorage.getItem("wuwei.coletor");
    if (destino) {
      void enviarResultado(destino, {
        nome: vitima.nome,
        dtype: vitima.dtype,
        estado: "derrubou-o-app",
        custoMb: 0,
        totalSeg: 0,
        detalhe: "app encerrado pelo sistema durante o teste",
      });
    }
  }

  /** Manda o resultado para a máquina que estiver coletando.
   *
   *  O endereço fica em localStorage (`wuwei.coletor`) em vez de fixo no
   *  código: quem coleta muda de IP, e recompilar o app para trocar um número
   *  de rede é caro demais para o que vale. Sem endereço configurado, não faz
   *  nada — o teste continua valendo na tela. */
  private async mandarResultado(
    p: (typeof PROVAS)[number],
    r: Awaited<ReturnType<typeof rodarProva>>,
    tetoMb: number | null,
  ) {
    const destino = localStorage.getItem("wuwei.coletor");
    if (!destino) return;
    await enviarResultado(destino, {
      nome: p.nome,
      id: p.id,
      tarefa: p.tarefa,
      dtype: p.dtype,
      teto: tetoMb,
      ...r,
    });
  }

  /** O número de não-lidas na aba do navegador.
   *
   *  O contador dentro da tela só serve para quem já está olhando. Quem está em
   *  outra aba — que é o caso normal enquanto o agente trabalha — não vê nada:
   *  a aba fica idêntica com zero ou com dez mensagens esperando.
   *
   *  Acima de 99 vira "99+": o número exato deixa de importar e o texto começa
   *  a empurrar o nome do app para fora do espaço da aba. */
  private pintarTituloDaAba(quantas: number) {
    const base = "wuwei · chat";
    document.title = quantas > 0 ? `(${quantas > 99 ? "99+" : quantas}) ${base}` : base;
  }

  private renderInferencias() {
    // O rastro precisa virar veredito ANTES de as chaves serem desenhadas.
    // Estava sendo colhido no banco de provas, que roda depois — então o
    // recurso que fechou o app na sessão anterior ainda aparecia com chave
    // ligável, e só passava a ser recusado no redesenho seguinte.
    this.colherVitima(this.diagnosticoCache?.tetoAlocavelMb ?? null);
    this.renderPainelDiagnostico();
    const lista = document.getElementById("inference-list");
    if (!lista) return;
    lista.textContent = "";
    for (const r of RECURSOS) {
      // Ligar a chave não basta: a transcrição do Google não existe dentro do
      // app instalado, só no Chrome. Sem dizer isso aqui, ligar e nada
      // acontecer parece defeito do app.
      const motivo = indisponivel(r.id);
      const ativo = recursoAtivo(r.id) && !motivo;
      const item = document.createElement("div");
      item.className = "inference-item";
      item.dataset.on = ativo ? "1" : "0";
      if (motivo) item.dataset.indisponivel = "1";
      // O que a bateria concluiu sobre ESTE aparelho. "Não testado" é um estado
      // de verdade e aparece como tal: é diferente de "cabe", e esconder a
      // diferença faria a tela afirmar mais do que se mediu.
      const ver = vereditoDe(r.id);
      const estado: EstadoVeredito = ver?.estado ?? "naotestado";
      item.dataset.veredito = estado;

      const texto = document.createElement("div");
      texto.className = "inference-text";
      const nome = document.createElement("p");
      nome.className = "inference-name";
      nome.textContent = r.nome;
      const desc = document.createElement("p");
      desc.className = "inference-desc";
      desc.textContent = r.descricao;
      const meta = document.createElement("p");
      meta.className = "inference-meta";
      meta.textContent = `${r.modelo} · ${r.peso}`;
      const custo = document.createElement("p");
      custo.className = "inference-custo";
      custo.textContent = motivo ? "" : ativo ? "" : r.custo;
      const selo = document.createElement("p");
      selo.className = `inference-veredito veredito-${estado}`;
      if (ver) {
        const quando = new Date(ver.quando).toLocaleString("pt-BR", {
          day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
        const rotulo =
          estado === "cabe" ? "Cabe neste aparelho" : estado === "naocabe" ? "Não cabe aqui" : "Não testado";
        selo.textContent = `${rotulo} — ${ver.motivo} (medido em ${quando})`;
      } else {
        selo.textContent = "Não testado neste aparelho.";
      }
      const aviso = document.createElement("p");
      aviso.className = "inference-aviso";
      const erroFala =
        r.id === "transcricaoGoogle" ? AudioRecorder.ultimoErroFala : null;
      aviso.textContent = motivo
        ? `Indisponível: ${motivo}`
        : erroFala
          ? `Última tentativa falhou: ${this.explicarErroFalaImpl(erroFala)}`
          : r.remoto
            ? "Manda seu áudio para o Google."
            : // O que cabe apertado precisa dizer que cabe apertado: ligar dois
              // pesados é permitido pela tela e fatal para o app.
              (avisoDeMemoria(r.id) ?? "");
      // Linha separada do `aviso`: o botão de teste escreve no aviso, e este
      // diagnóstico é da GRAVAÇÃO — um não pode apagar o outro.
      const diag = document.createElement("p");
      diag.className = "inference-diag";
      if (r.id === "transcricao") {
        const d = AudioRecorder.lerDiagnostico();
        if (d) {
          const h = new Date(d.quando).toLocaleTimeString("pt-BR", {
            hour: "2-digit",
            minute: "2-digit",
          });
          diag.textContent = `Última gravação (${h}) falhou — formato ${d.mime || "?"} — ${d.erro}`;
        }
      }
      texto.append(nome, desc, meta, custo, selo, aviso, diag);

      // Recurso reprovado pela bateria não ganha chave nenhuma.
      //
      // Desabilitar não basta: uma chave cinza convida a insistir, e o usuário
      // fica tentando ligar algo que já se sabe que fecha o app. O selo logo
      // acima explica o que houve; a chave sairia sobrando e só faria prometer
      // uma escolha que não existe.
      const reprovado = estado === "naocabe";

      // <label>+<input type=checkbox> para o toggle: chega teclado e leitor de
      // tela de graça, sem eu reimplementar o papel de switch.
      const label = document.createElement("label");
      label.className = "inference-switch";
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.checked = ativo;
      cb.disabled = !!motivo;
      cb.setAttribute("aria-label", r.nome);
      const trilho = document.createElement("span");
      trilho.className = "inference-track";
      label.append(cb, trilho);
      cb.addEventListener("change", () => {
        setRecurso(r.id, cb.checked);
        this.renderInferencias();
      });


      // A transcrição local ganha teste próprio: o caminho dela tem muitas
      // etapas invisíveis (worker, download de 150 MB, inferência) e, quando
      // falha, falha em silêncio. O botão mostra ONDE parou.
      if (r.id === "transcricao" && !motivo) {
        const bt = document.createElement("button");
        bt.type = "button";
        bt.className = "inference-testar";
        bt.textContent = "testar agora";
        bt.addEventListener("click", async () => {
          bt.disabled = true;
          const t0 = Date.now();
          const seg = () => ((Date.now() - t0) / 1000).toFixed(0);
          aviso.textContent = "0s — iniciando…";
          const parar = onWhisperProgress((info) => {
            const i = info as { status: string; file?: string; progress?: number };
            ultimoDoProgresso =
              i.status === "downloading" || i.status === "progress"
                ? `baixando ${Math.round(i.progress ?? 0)}% ${i.file ?? ""}`
                : i.status;
          });
          // Relógio próprio: sem ele a tela fica parada em "iniciando…" e não
          // dá para distinguir "está carregando" de "morreu". O progresso fino
          // do download nem sempre chega, o tempo sempre chega.
          let ultimoDoProgresso = "";
          const tick = setInterval(() => {
            if (ultimoDoProgresso) aviso.textContent = `${seg()}s — ${ultimoDoProgresso}`;
            else aviso.textContent = `${seg()}s — carregando o modelo (a primeira vez passa de 1 minuto)`;
          }, 1000);
          try {
            const blob = await this.gerarAudioDeTeste();
            const r2 = await transcribeBlob(blob, { langHint: "pt-BR" });
            aviso.textContent = `Funcionou em ${seg()}s. Reconheceu: ${r2.text ? `"${r2.text}"` : "(nada — o áudio de teste é mudo, mas o motor rodou)"}`;
          } catch (e) {
            aviso.textContent = `Falhou aos ${seg()}s: ${String((e as Error)?.message || e).slice(0, 120)}`;
          } finally {
            clearInterval(tick);
            parar();
            bt.disabled = false;
            bt.textContent = "testar de novo";
          }
        });
        texto.append(bt);
      }

      // Só o recurso remoto ganha botão de teste: é o único cujo motor pode
      // existir e não funcionar, e o único que o usuário não consegue julgar
      // sozinho olhando a tela.
      if (r.remoto && !motivo) {
        const bt = document.createElement("button");
        bt.type = "button";
        bt.className = "inference-testar";
        bt.textContent = "testar agora";
        bt.addEventListener("click", async () => {
          bt.disabled = true;
          bt.textContent = "testando…";
          const { ok, motivo: m } = await testarFala();
          aviso.textContent = ok
            ? `Funciona neste aparelho — ${m}.`
            : `Não funciona aqui: ${this.explicarErroFalaImpl(m)}`;
          bt.disabled = false;
          bt.textContent = "testar de novo";
        });
        texto.append(bt);
      }

      // Reprovado não recebe a chave no DOM. Esconder por CSS deixaria o
      // elemento existindo — e o que não existe não pode ser clicado por
      // engano, nem alcançado por teclado ou leitor de tela.
      if (reprovado) item.append(texto);
      else item.append(texto, label);
      lista.append(item);
    }
  }

  /** Mostra "baixar o app" só para quem está pelo navegador e só se o APK
   *  existir de fato no servidor — link para 404 é pior que link nenhum. */
  private async wireBaixarApp() {
    const bt = document.getElementById("apk-btn");
    if (!bt) return;
    // Dentro do app instalado (WebView ou PWA) não há o que baixar.
    const jaEstaNoApp =
      /wuwei-android/i.test(navigator.userAgent) ||
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true;
    if (jaEstaNoApp) return;
    try {
      const r = await fetch("/apk-info");
      const info = await r.json();
      if (info?.disponivel) {
        const mb = (info.bytes / (1024 * 1024)).toFixed(1);
        bt.setAttribute("data-label", `baixar o app (${mb} MB)`);
        bt.removeAttribute("hidden");
      }
    } catch {
      // servidor antigo sem /apk-info: fica escondido, que é o certo
    }
  }

  /** 2 segundos de silêncio em WAV, só para exercitar o caminho inteiro do
   *  Whisper sem depender do microfone nem de permissão. */
  private async gerarAudioDeTeste(): Promise<Blob> {
    const taxa = 16000;
    const amostras = taxa * 2;
    const buf = new ArrayBuffer(44 + amostras * 2);
    const dv = new DataView(buf);
    const txt = (off: number, s: string) => {
      for (let i = 0; i < s.length; i++) dv.setUint8(off + i, s.charCodeAt(i));
    };
    txt(0, "RIFF");
    dv.setUint32(4, 36 + amostras * 2, true);
    txt(8, "WAVEfmt ");
    dv.setUint32(16, 16, true);
    dv.setUint16(20, 1, true);
    dv.setUint16(22, 1, true);
    dv.setUint32(24, taxa, true);
    dv.setUint32(28, taxa * 2, true);
    dv.setUint16(32, 2, true);
    dv.setUint16(34, 16, true);
    txt(36, "data");
    dv.setUint32(40, amostras * 2, true);
    // Um tom baixinho em vez de silêncio puro: o Whisper alucina em faixa
    // totalmente vazia, e aqui só queremos saber se o caminho roda.
    for (let i = 0; i < amostras; i++) {
      dv.setInt16(44 + i * 2, Math.sin((i / taxa) * 2 * Math.PI * 220) * 800, true);
    }
    return new Blob([buf], { type: "audio/wav" });
  }

  private wireInferencias() {
    document.getElementById("inference-btn")?.addEventListener("click", () => {
      this.abrirPagina("inference-view");
      this.renderInferencias();
    });
    document.getElementById("inference-back")?.addEventListener("click", () => {
      this.fecharPagina();
    });
  }

  /** O download em curso, se houver.
   *
   *  Estado no app e não no DOM porque o download roda no lado nativo e
   *  sobrevive a tudo que acontece aqui: sair da aba, redesenhar a lista,
   *  navegar para uma conversa. Guardar o progresso num elemento faria a
   *  contagem sumir a cada `innerHTML =` — e foi assim que "clicar em baixar
   *  não faz nada" pareceu verdade, quando o download estava rodando. */
  private baixaEmCurso: { arquivo: string; pct: number; mb: number; totalMb: number } | null = null;

  private wireAgentesLocais() {
    document.getElementById("agentes-locais-btn")?.addEventListener("click", () => {
      this.abrirPagina("agentes-locais-view");
      this.renderAgentesLocais();
    });

    // Um ouvinte só, ligado uma vez, para TODO download. Registrar dentro de
    // cada `baixar()` faria um ouvinte por toque, e a tela redesenhada perderia
    // o que já estava em curso.
    window.addEventListener("wuwei-llm", (ev) => {
      const d = (ev as CustomEvent).detail as {
        tipo?: string;
        arquivo?: string;
        pct?: number;
        mb?: number;
        totalMb?: number;
        erro?: string;
      };
      if (!d?.arquivo) return;
      if (d.tipo === "baixando") {
        // Primeira notícia deste arquivo: registra o começo. As seguintes só
        // atualizam a barra — anotar cada ponto percentual encheria o registro
        // com cem linhas por download e escoraria o que interessa.
        if (this.baixaEmCurso?.arquivo !== d.arquivo) {
          registro.anotar("download", `começou: ${d.arquivo}`);
        }
        this.baixaEmCurso = {
          arquivo: d.arquivo,
          pct: Number(d.pct) || 0,
          mb: Number(d.mb) || 0,
          totalMb: Number(d.totalMb) || 0,
        };
        this.renderDownload();
      } else if (d.tipo === "baixado" || d.tipo === "erroDownload") {
        if (d.tipo === "baixado") {
          registro.anotar("download", `terminou: ${d.arquivo}`);
        } else {
          registro.anotar("download", `falhou: ${d.arquivo} — ${d.erro ?? "sem detalhe"}`, "erro");
        }
        this.baixaEmCurso = null;
        this.renderDownload();
        this.renderRegistro();
        // Redesenha a lista: o arquivo novo precisa aparecer com "carregar", e
        // o que falhou não pode ficar oferecido como se estivesse lá.
        if (!document.getElementById("agentes-locais-view")?.classList.contains("hidden")) {
          this.renderAgentesLocais();
        }
      }
    });
    document.getElementById("agentes-locais-back")?.addEventListener("click", () => {
      // Sair da tela com um modelo gerando deixaria a geração correndo e
      // gastando bateria por uma resposta que ninguém vai ler.
      llmNativo.parar();
      this.fecharPagina();
    });
  }

  /** Desenha a tela de agentes locais.
   *
   *  Duas telas em uma, e a diferença é o app: dentro dele há motor nativo e a
   *  lista de modelos faz sentido; no navegador não há, e o que se deve à
   *  pessoa é a explicação de por quê — não uma tela vazia que parece quebrada.
   */
  private renderAgentesLocais() {
    const alvoEstado = document.getElementById("agentes-locais-estado");
    const alvoModelos = document.getElementById("agentes-locais-modelos");
    const alvoTeste = document.getElementById("agentes-locais-teste");
    if (!alvoEstado || !alvoModelos || !alvoTeste) return;

    if (!llmNativo.disponivel()) {
      alvoEstado.innerHTML = `<p class="agents-hint">${llmNativo.MOTIVO_SEM_PONTE}</p>`;
      alvoModelos.innerHTML = "";
      alvoTeste.innerHTML = "";
      // Abre o acordeão que tem a explicação, e fecha os outros dois.
      //
      // Acordeão serve para guardar o que raramente se mexe; não serve para
      // esconder o motivo de a tela estar vazia. Sem isto, quem abre a aba no
      // navegador vê três triângulos fechados e nenhuma pista — que é pior do
      // que a tela quebrada que os acordeões vieram consertar.
      (document.getElementById("sanfona-aparelho") as HTMLDetailsElement | null)?.setAttribute("open", "");
      (document.getElementById("sanfona-modelos") as HTMLDetailsElement | null)?.removeAttribute("open");
      (document.getElementById("sanfona-busca") as HTMLDetailsElement | null)?.removeAttribute("open");
      return;
    }

    const e = llmNativo.estado();
    if (!e) {
      alvoEstado.innerHTML = `<p class="agents-hint">A ponte respondeu, mas não deu para ler o estado do motor.</p>`;
      return;
    }

    const linhas: [string, string][] = [
      ["aparelho", e.aparelho || "não informado"],
      ["motor", e.variante ?? (e.motivo || "não carregado")],
      ["núcleos", String(e.nucleos)],
      ["memória livre", e.ramLivreMb != null ? `${e.ramLivreMb} MB de ${e.ramTotalMb} MB` : "não informada"],
      ["espaço em disco", e.discoLivreMb != null ? `${e.discoLivreMb} MB` : "não informado"],
      ["modelo carregado", e.carregado ? `sim, contexto de ${e.contexto} tokens` : "nenhum"],
    ];
    alvoEstado.innerHTML =
      `<dl class="ficha">` +
      linhas.map(([k, v]) => `<dt>${k}</dt><dd>${v}</dd>`).join("") +
      `</dl>`;

    // Guardados para o gerenciador decidir o que cabe sem perguntar de novo.
    this.ramTotalMb = e.ramTotalMb ?? 0;
    this.discoLivreMb = e.discoLivreMb ?? 0;

    const modelos = llmNativo.modelos();
    // Qual modelo de visão está na memória do ONNX Runtime agora. Perguntado ao
    // motor, e não guardado numa variável daqui: o estado real mora lá, e uma
    // cópia nossa erraria depois de um descarregar ou de um recarregar da tela.
    const visaoCarregada = visaoNativa.estado()?.modelo ?? null;
    const jaTem = new Set(modelos.map((m) => m.nome));
    // Sugeridos que ainda não estão no aparelho. Some da lista quando baixa —
    // deixar "baixar" ao lado de um modelo já baixado só produz o toque que
    // responde "já existe".
    // Os de visão entram na mesma lista: para quem usa, a diferença entre
    // "roda no llama.cpp" e "roda no ONNX Runtime" é detalhe de implementação,
    // não critério de escolha. Quem decide qual motor chamar é o tipo do
    // arquivo, e isso o app resolve sozinho.
    const deVisao: llmNativo.ModeloSugerido[] = visaoNativa.VISAO_SUGERIDA.map((v) => ({
      nome: v.nome,
      arquivo: v.arquivo,
      url: v.url,
      mb: v.mb,
      descricao: v.descricao,
    }));
    const faltando = [...llmNativo.SUGERIDOS, ...deVisao].filter((s) => !jaTem.has(s.arquivo));
    const sugestoes = faltando.length
      ? `<div class="sugeridos">` +
        `<p class="agents-hint">Para baixar${modelos.length ? " também" : ""}:</p>` +
        faltando
          .map(
            (s) =>
              `<div class="inference-item" data-sugerido="${s.arquivo}">` +
              `<div class="inference-text">` +
              `<p class="inference-name">${s.nome}</p>` +
              `<p class="inference-desc">${s.descricao}</p>` +
              `</div>` +
              `<span class="inference-peso">${s.mb} MB</span>` +
              `<button class="baixar-modelo" type="button" data-arquivo="${s.arquivo}">baixar</button>` +
              `</div>`,
          )
          .join("") +
        `</div>`
      : "";

    if (modelos.length === 0) {
      alvoModelos.innerHTML =
        `<p class="agents-hint">Nenhum modelo no aparelho ainda.</p>` + sugestoes;
      this.wireBaixarModelos(alvoModelos);
    } else {
      alvoModelos.innerHTML = modelos
        .map(
          (m) =>
            `<div class="inference-item${llmNativo.papelDoModelo(m) === "peca" ? " hf-peca" : ""}" data-modelo="${m.nome}">` +
            `<div class="inference-text">` +
            `<p class="inference-name">${m.nome}</p>` +
            // Peça auxiliar diz o que é, ali mesmo. Sem isso o arquivo fica na
            // lista sem botão e sem explicação, o que parece defeito.
            (llmNativo.papelDoModelo(m) === "peca"
              ? `<p class="inference-desc">${llmNativo.avisoDaPeca(m)} Pode apagar.</p>`
              : "") +
            `</div>` +
            `<span class="inference-peso">${Math.round(m.bytes / 1048576)} MB</span>` +
            // Peça não recebe "carregar": era assim que um `mmproj` chegava ao
            // motor de linguagem e voltava com "unsupported model architecture:
            // 'clip'" — erro correto, pergunta errada.
            (llmNativo.papelDoModelo(m) === "peca"
              ? ""
              : // O botão diz o mesmo, mas por baixo vai para motores
                // diferentes: `.gguf` para o llama.cpp, `.onnx` para o ONNX
                // Runtime. Quem usa não precisa saber.
                //
                // O de visão que já está carregado diz isso no próprio botão.
                // Antes a lista não consultava o estado da visão, então o
                // modelo de imagem carregado ficava idêntico ao não carregado —
                // e a única pista de que a carga funcionou era uma frase que o
                // redesenho apagava.
                (m.tipo === "onnx" && visaoCarregada === m.nome
                  ? `<button class="carregar-modelo" type="button" data-nome="${m.nome}" data-tipo="onnx" disabled>carregado</button>`
                  : `<button class="carregar-modelo" type="button" data-nome="${m.nome}" data-tipo="${m.tipo ?? "gguf"}">carregar</button>`)) +
            `<button class="apagar-modelo" type="button" data-nome="${m.nome}" title="apagar">✕</button>` +
            `</div>`,
        )
        .join("") + sugestoes;
      alvoModelos.querySelectorAll<HTMLButtonElement>(".carregar-modelo").forEach((b) => {
        b.addEventListener("click", () =>
          void this.carregarModeloLocal(b.dataset.nome ?? "", b, b.dataset.tipo ?? "gguf"),
        );
      });
      alvoModelos.querySelectorAll<HTMLButtonElement>(".apagar-modelo").forEach((b) => {
        b.addEventListener("click", () => this.apagarModeloLocal(b.dataset.nome ?? ""));
      });
      this.wireBaixarModelos(alvoModelos);
    }

    this.renderDownload();
    this.renderRegistro();
    this.renderBuscaDeModelos();

    // Só os que conversam entram no seletor. O modelo de busca e os de visão
    // estão no aparelho e são úteis, mas oferecê-los aqui só produziria a
    // tentativa que falha.
    const paraConversa = modelos.filter((m) => llmNativo.papelDoModelo(m) === "conversa");
    if (!this.modeloDaConversa && paraConversa.length) {
      this.modeloDaConversa = paraConversa[0]!.nome;
    }

    alvoTeste.innerHTML =
      `<div id="conversa-turnos" class="conversa-turnos"></div>` +
      (paraConversa.length
        ? `<div class="conversa-caixa">` +
          `<div class="conversa-linha-topo">` +
          `<select id="modelo-da-conversa" title="modelo que vai responder">` +
          paraConversa
            .map(
              (m) =>
                `<option value="${m.nome}"${m.nome === this.modeloDaConversa ? " selected" : ""}>` +
                `${m.nome.replace(/\.gguf$/i, "")}</option>`,
            )
            .join("") +
          `</select>` +
          `<span id="modelo-estado" class="modelo-estado"></span>` +
          `</div>` +
          `<textarea id="agentes-locais-prompt" placeholder="pergunte alguma coisa"></textarea>` +
          `<div class="botoes">` +
          `<button id="agentes-locais-gerar" type="button">enviar</button>` +
          `<button id="agentes-locais-parar" type="button" disabled>parar</button>` +
          `<button id="agentes-locais-descarregar" type="button" ${e.carregado ? "" : "disabled"}>descarregar</button>` +
          `<button id="conversa-limpar" type="button">limpar</button>` +
          `</div>` +
          `</div>`
        : `<p class="agents-hint">Nenhum modelo de conversa no aparelho. Baixe um em ` +
          `"procurar e baixar" — o Gemma 3 1B é um bom começo.</p>`) +
      `<p class="agents-hint" id="agentes-locais-numeros"></p>`;

    this.renderTurnos();
    this.pintarEstadoDoModelo();

    document.getElementById("modelo-da-conversa")?.addEventListener("change", (ev) => {
      this.modeloDaConversa = (ev.target as HTMLSelectElement).value;
      this.pintarEstadoDoModelo();
    });
    document.getElementById("agentes-locais-gerar")?.addEventListener("click", () => {
      void this.gerarComModeloLocal();
    });
    document.getElementById("agentes-locais-parar")?.addEventListener("click", () => {
      llmNativo.parar();
    });
    document.getElementById("agentes-locais-descarregar")?.addEventListener("click", () => {
      llmNativo.descarregar();
      this.modeloCarregado = null;
      setTimeout(() => this.renderAgentesLocais(), 400);
    });
    document.getElementById("conversa-limpar")?.addEventListener("click", () => {
      this.turnos = [];
      this.renderTurnos();
    });
  }

  /** O modelo escolhido no seletor, e o que está de fato na memória.
   *
   *  São coisas diferentes: escolher é instantâneo, carregar leva segundos e
   *  troca o que está na memória. Guardar os dois é o que permite dizer "este
   *  vai ser carregado quando você enviar" em vez de fingir que já está. */
  private modeloDaConversa: string | null = null;
  private modeloCarregado: string | null = null;

  /** As perguntas e respostas desta sessão, com o modelo que respondeu cada uma.
   *
   *  Empilhadas e não substituídas: o objetivo da tela é comparar. Perguntar o
   *  mesmo a dois modelos e ver as duas respostas lado a lado só funciona se a
   *  primeira não sumir quando a segunda chega. */
  private turnos: Array<{ modelo: string; pergunta: string; resposta: string; numeros: string }> = [];

  /** Desenha a pilha inteira. Caro por construção — use só quando a pilha muda
   *  de tamanho, nunca durante o streaming. */
  private renderTurnos() {
    const alvo = document.getElementById("conversa-turnos");
    if (!alvo) return;
    if (!this.turnos.length) {
      alvo.innerHTML = "";
      return;
    }
    alvo.innerHTML = this.turnos
      .map(
        (t) =>
          `<div class="turno">` +
          `<div class="turno-pergunta">${this.escapar(t.pergunta)}</div>` +
          `<div class="turno-modelo">${this.escapar(t.modelo.replace(/\.gguf$/i, ""))}</div>` +
          `<div class="turno-resposta">${this.escapar(t.resposta)}</div>` +
          `<div class="turno-num">${this.escapar(t.numeros)}</div>` +
          `</div>`,
      )
      .join("");
    alvo.scrollTop = alvo.scrollHeight;
  }

  /** Atualiza só o ÚLTIMO turno, sem tocar no resto da pilha.
   *
   *  Existe porque o caminho caro tinha um custo medido: 123 reconstruções da
   *  conversa inteira para 120 tokens, e 1174 nós de DOM destruídos num
   *  histórico de quatro turnos — contra 614 em um. Ou seja, o custo por token
   *  crescia com o tamanho da conversa, que é a assinatura exata do relato
   *  "trava depois que eu começo a conversar".
   *
   *  Aqui só o `textContent` de dois nós muda. O navegador não recria elemento
   *  nenhum, e o custo para de acompanhar o histórico.
   *
   *  `textContent`, e não `innerHTML`: a resposta vem de um modelo de
   *  linguagem, e modelo de linguagem escreve `<` quando quer escrever `<`. */
  private atualizarUltimoTurno() {
    const alvo = document.getElementById("conversa-turnos");
    const ultimo = alvo?.lastElementChild;
    const t = this.turnos[this.turnos.length - 1];
    if (!alvo || !ultimo || !t) return;
    // Escreve no nó de texto existente em vez de trocá-lo.
    //
    // `textContent = x` descarta o nó de texto e cria outro — barato, mas são
    // dois nós por token, e a medição os viu: 242 nós substituídos em 120
    // tokens mesmo já sem reconstruir a pilha. Mexer no `nodeValue` altera o
    // conteúdo no lugar, sem alocar nada.
    const escrever = (el: Element | null, texto: string) => {
      if (!el) return;
      if (el.firstChild && el.firstChild.nodeType === Node.TEXT_NODE) {
        el.firstChild.nodeValue = texto;
      } else {
        el.textContent = texto;
      }
    };
    escrever(ultimo.querySelector(".turno-resposta"), t.resposta);
    escrever(ultimo.querySelector(".turno-num"), t.numeros);
    alvo.scrollTop = alvo.scrollHeight;
  }

  private escapar(s: string): string {
    const d = document.createElement("div");
    d.textContent = s;
    return d.innerHTML;
  }

  private pintarEstadoDoModelo() {
    const el = document.getElementById("modelo-estado");
    if (!el) return;
    if (!this.modeloDaConversa) {
      el.textContent = "";
    } else if (this.modeloCarregado === this.modeloDaConversa) {
      el.textContent = "na memória";
      el.className = "modelo-estado pronto";
    } else {
      el.textContent = "carrega ao enviar";
      el.className = "modelo-estado pendente";
    }
  }

  /** RAM total e disco livre do aparelho, lidos junto com a ficha.
   *
   *  Guardados porque o gerenciador precisa deles a cada resultado de busca, e
   *  perguntar à ponte por linha da lista seria uma travessia por item. */
  private ramTotalMb = 0;
  private discoLivreMb = 0;

  private apagarModeloLocal(nome: string) {
    if (!confirm(`Apagar ${nome}? Para usar de novo, será preciso baixar tudo outra vez.`)) return;
    if (llmNativo.apagarModelo(nome)) {
      this.renderAgentesLocais();
    } else {
      const numeros = document.getElementById("agentes-locais-numeros");
      // Falha mais provável: o modelo está carregado, e o arquivo em uso.
      if (numeros) numeros.textContent = `não deu para apagar ${nome} — descarregue antes, se estiver carregado`;
    }
  }

  /** O que dizer sobre um arquivo ANTES de baixá-lo.
   *
   *  Duas contas, e as duas com números do aparelho: cabe no disco, e cabe na
   *  memória. A segunda é aproximada e vai dita como tal — o llama.cpp mapeia o
   *  arquivo (`mmap`), então o sistema pode despejar páginas e rodar um modelo
   *  maior que a RAM, só que devagar. Prometer precisão aqui seria inventar. */
  private vereditoDeArquivo(bytes: number): { classe: string; texto: string } {
    const mb = Math.round(bytes / 1048576);
    if (this.discoLivreMb && mb + 200 > this.discoLivreMb) {
      return { classe: "nao", texto: `não cabe no disco: ${mb} MB contra ${this.discoLivreMb} MB livres` };
    }
    if (this.ramTotalMb && mb > this.ramTotalMb * 0.55) {
      return {
        classe: "aperta",
        texto: `${mb} MB para ${this.ramTotalMb} MB de RAM — deve rodar por mmap, mas devagar`,
      };
    }
    return { classe: "ok", texto: `${mb} MB` };
  }

  /** MB legível: acima de mil vira GB, porque "2048 MB" faz o leitor converter
   *  de cabeça e "2,0 GB" não. */
  private mb(n: number): string {
    return n >= 1024 ? `${(n / 1024).toFixed(1).replace(".", ",")} GB` : `${n} MB`;
  }

  private renderBuscaDeModelos() {
    const alvo = document.getElementById("agentes-locais-busca");
    if (!alvo) return;
    alvo.innerHTML =
      `<h3 class="agentes-titulo">procurar outros modelos</h3>` +
      `<p class="agents-hint">Busca no Hugging Face, só o que está em GGUF — que é o formato que ` +
      `este motor abre. Os pesos originais da maioria dos modelos não estão nesse formato e não ` +
      `apareceriam aqui mesmo existindo.</p>` +
      `<div class="busca-linha">` +
      `<input id="hf-termo" type="search" placeholder="gemma, qwen, llama…" />` +
      `<button id="hf-buscar" type="button">buscar</button>` +
      `</div>` +
      `<div id="hf-resultados" class="hf-resultados"></div>`;

    const campo = document.getElementById("hf-termo") as HTMLInputElement | null;
    document.getElementById("hf-buscar")?.addEventListener("click", () => {
      void this.buscarNoHf(campo?.value ?? "");
    });
    campo?.addEventListener("keydown", (ev) => {
      if ((ev as KeyboardEvent).key === "Enter") void this.buscarNoHf(campo.value);
    });
  }

  private async buscarNoHf(termo: string) {
    const alvo = document.getElementById("hf-resultados");
    if (!alvo) return;
    if (!termo.trim()) {
      alvo.innerHTML = `<p class="agents-hint">Escreva o nome de um modelo.</p>`;
      return;
    }
    alvo.innerHTML = `<p class="agents-hint">procurando…</p>`;
    try {
      const repos = await hf.buscar(termo.trim());
      if (!repos.length) {
        alvo.innerHTML = `<p class="agents-hint">Nada em GGUF para "${termo}".</p>`;
        return;
      }
      const chave = (id: string) => `hf-${id.replace(/[^a-zA-Z0-9]/g, "_")}`;
      alvo.innerHTML = repos
        .map(
          (r) =>
            `<div class="hf-repo" data-repo="${r.id}">` +
            `<button class="hf-abrir" type="button" data-repo="${r.id}">` +
            `<span class="hf-nome">${r.id}</span>` +
            `<span class="hf-meta">${r.downloads.toLocaleString("pt-BR")} downloads` +
            (r.fechado ? ` · <b>exige token</b>` : "") +
            `</span>` +
            `<span class="hf-tam" id="${chave(r.id)}">medindo…</span>` +
            `</button>` +
            `<div class="hf-arquivos"></div>` +
            `</div>`,
        )
        .join("");
      alvo.querySelectorAll<HTMLButtonElement>(".hf-abrir").forEach((b) => {
        b.addEventListener("click", () => void this.abrirRepoHf(b.dataset.repo ?? "", b));
      });

      // Os tamanhos chegam depois, um repositório de cada vez. A busca aparece
      // na hora e os números vão preenchendo — esperar vinte consultas antes de
      // mostrar a primeira linha faria a busca parecer travada.
      void hf.medir(repos, (r) => {
        const el = document.getElementById(chave(r.id));
        if (!el || r.menorMb == null) return;
        const faixa =
          r.menorMb === r.maiorMb
            ? `${r.menorMb} MB`
            : `${this.mb(r.menorMb)} a ${this.mb(r.maiorMb!)}`;
        el.textContent = `${faixa} · ${r.arquivos} arquivo${r.arquivos === 1 ? "" : "s"}`;
        // Verde quando a menor variante cabe no disco; é o que decide se vale
        // sequer abrir a lista.
        el.className =
          "hf-tam " + (this.discoLivreMb && r.menorMb + 200 > this.discoLivreMb ? "nao" : "ok");
      });
    } catch (err) {
      alvo.innerHTML = `<p class="agents-hint">a busca falhou: ${(err as Error).message}</p>`;
    }
  }

  private async abrirRepoHf(repo: string, botao: HTMLButtonElement) {
    const caixa = botao.parentElement?.querySelector<HTMLElement>(".hf-arquivos");
    if (!caixa) return;
    // Segundo toque fecha: a lista de arquivos é longa e num celular ela empurra
    // o resto da busca para fora da tela.
    if (caixa.innerHTML) {
      caixa.innerHTML = "";
      return;
    }
    caixa.innerHTML = `<p class="agents-hint">lendo os arquivos…</p>`;
    try {
      const arqs = await hf.arquivos(repo);
      const jaTem = new Set(llmNativo.modelos().map((m) => m.nome));
      if (!arqs.length) {
        caixa.innerHTML = `<p class="agents-hint">este repositório não tem .gguf.</p>`;
        return;
      }
      caixa.innerHTML = arqs
        .map((a) => {
          const tem = jaTem.has(a.nome);
          // Peça auxiliar não recebe botão de baixar: ela é sempre a menor do
          // repositório, portanto a que mais parece caber, e baixá-la custa o
          // download inteiro para terminar num erro que não explica nada.
          const v =
            a.papel === "modelo"
              ? this.vereditoDeArquivo(a.bytes)
              : { classe: "nao", texto: a.aviso };
          const acao = tem
            ? `<span class="hf-ja">no aparelho</span>`
            : a.papel === "modelo"
              ? `<button class="hf-baixar" type="button" data-repo="${repo}" data-arquivo="${a.nome}">baixar</button>`
              : `<span class="hf-ja">não é modelo</span>`;
          return (
            `<div class="hf-arquivo${a.papel === "modelo" ? "" : " hf-peca"}">` +
            `<div class="hf-arquivo-txt">` +
            `<span class="hf-quant">${a.quantizacao || a.nome}</span>` +
            `<span class="hf-verdito ${v.classe}">${v.texto}</span>` +
            `</div>` +
            acao +
            `</div>`
          );
        })
        .join("");
      caixa.querySelectorAll<HTMLButtonElement>(".hf-baixar").forEach((b) => {
        b.addEventListener("click", () =>
          void this.baixarDoHf(b.dataset.repo ?? "", b.dataset.arquivo ?? "", b),
        );
      });
    } catch (err) {
      caixa.innerHTML = `<p class="agents-hint">não deu para ler: ${(err as Error).message}</p>`;
    }
  }

  private async baixarDoHf(repo: string, arquivo: string, botao: HTMLButtonElement) {
    botao.disabled = true;
    botao.textContent = "…";
    try {
      // O progresso não é acompanhado aqui: quem cuida disso é o ouvinte único
      // registrado em `wireAgentesLocais`, que sobrevive ao redesenho da lista.
      // Esta promessa serve só para saber o desfecho.
      await llmNativo.baixar({
        nome: arquivo,
        arquivo,
        url: hf.urlDe(repo, arquivo),
        mb: 0,
        descricao: "",
      });
    } catch (err) {
      botao.disabled = false;
      botao.textContent = "baixar";
      this.avisoNoItem(botao, (err as Error).message);
    }
  }

  private renderRegistro() {
    const alvo = document.getElementById("agentes-locais-registro");
    const selo = document.getElementById("registro-selo");
    if (!alvo) return;
    const linhas = registro.ler();

    // O selo no cabeçalho é o que faz o registro servir para o caso que o
    // motivou: um download que falhou enquanto a pessoa estava em outra tela.
    // Registro que só conta quando alguém procura não teria resolvido nada.
    if (selo) selo.hidden = !registro.temErroNaoVisto();

    if (!linhas.length) {
      alvo.innerHTML = `<p class="agents-hint">Nada registrado ainda.</p>`;
      return;
    }
    alvo.innerHTML =
      `<div class="registro-linhas">` +
      linhas
        .map((o) => {
          const h = new Date(o.quando).toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          });
          return (
            `<div class="registro-item ${o.nivel}">` +
            `<span class="registro-quando">${h}</span>` +
            `<span class="registro-origem">${this.escapar(o.origem)}</span>` +
            `<span class="registro-texto">${this.escapar(o.texto)}</span>` +
            `</div>`
          );
        })
        .join("") +
      `</div>` +
      `<div class="botoes"><button id="registro-limpar" type="button">limpar registro</button></div>`;

    document.getElementById("registro-limpar")?.addEventListener("click", () => {
      registro.limpar();
      this.renderRegistro();
    });
  }

  /** O bloco do download em curso: barra, números e o botão de cancelar. */
  private renderDownload() {
    const alvo = document.getElementById("agentes-locais-download");
    if (!alvo) return;
    const d = this.baixaEmCurso;
    if (!d) {
      alvo.innerHTML = "";
      return;
    }
    alvo.innerHTML =
      `<div class="baixa">` +
      `<div class="baixa-topo">` +
      `<span class="baixa-nome">${d.arquivo}</span>` +
      `<button id="baixa-cancelar" type="button">cancelar</button>` +
      `</div>` +
      `<div class="baixa-barra"><i style="width:${Math.max(0, Math.min(100, d.pct))}%"></i></div>` +
      `<span class="baixa-num">${d.pct}% · ${d.mb} de ${d.totalMb} MB</span>` +
      `</div>`;
    document.getElementById("baixa-cancelar")?.addEventListener("click", () => {
      llmNativo.cancelarDownload();
    });
  }

  /** Diz alguma coisa NO item que foi tocado.
   *
   *  A primeira versão mandava tudo para uma única linha no fim da página, e no
   *  celular ela fica fora da tela quando se toca num resultado de busca. O
   *  efeito era o pior possível: o botão voltava ao normal, a explicação ficava
   *  invisível, e "não funciona" virou a leitura correta do que se via. */
  private avisoNoItem(botao: HTMLButtonElement, texto: string, classe = "erro") {
    const pai = botao.parentElement;
    if (!pai) return;
    pai.querySelector(".aviso-item")?.remove();
    const span = document.createElement("span");
    span.className = `aviso-item ${classe}`;
    span.textContent = texto;
    pai.appendChild(span);
  }

  private wireBaixarModelos(raiz: HTMLElement) {
    raiz.querySelectorAll<HTMLButtonElement>(".baixar-modelo").forEach((b) => {
      b.addEventListener("click", () => void this.baixarModeloLocal(b.dataset.arquivo ?? "", b));
    });
  }

  /** Baixa um sugerido, mostrando o progresso no próprio botão.
   *
   *  No botão, e não numa barra à parte, por um motivo prático: são centenas de
   *  MB, a pessoa vai sair da tela, e o botão é o lugar onde ela olha ao
   *  voltar. O download em si roda no lado nativo e sobrevive à saída — o que
   *  se perde ao sair é só a contagem na tela. */
  private async baixarModeloLocal(arquivo: string, botao: HTMLButtonElement) {
    const visao = visaoNativa.VISAO_SUGERIDA.find((v) => v.arquivo === arquivo);
    const m: llmNativo.ModeloSugerido | undefined =
      llmNativo.SUGERIDOS.find((s) => s.arquivo === arquivo) ??
      (visao
        ? { nome: visao.nome, arquivo: visao.arquivo, url: visao.url, mb: visao.mb, descricao: "" }
        : undefined);
    const numeros = document.getElementById("agentes-locais-numeros");
    if (!m) return;
    botao.disabled = true;
    botao.textContent = "…";
    try {
      await llmNativo.baixar(m);
      if (numeros) numeros.textContent = `${m.nome} baixado. Agora é só carregar.`;
    } catch (err) {
      botao.disabled = false;
      botao.textContent = "baixar";
      this.avisoNoItem(botao, (err as Error).message);
    }
  }

  private async carregarModeloLocal(nome: string, botao: HTMLButtonElement, tipo = "gguf") {
    const numeros = document.getElementById("agentes-locais-numeros");
    botao.disabled = true;
    botao.textContent = "carregando…";

    // Visão vai para o ONNX Runtime, e a carga lá é síncrona: são dezenas de
    // MB, não centenas, e uma promessa por evento custaria mais coordenação do
    // que o tempo que economiza.
    if (tipo === "onnx") {
      const erro = visaoNativa.carregar(nome);
      botao.disabled = false;
      botao.textContent = "carregar";

      // Redesenhar ANTES de escrever a frase.
      //
      // Na ordem inversa — escrever e depois redesenhar — a confirmação durava
      // o tempo de um quadro: `renderAgentesLocais` reescreve o innerHTML do
      // bloco inteiro, e `agentes-locais-numeros` volta vazio junto. O modelo
      // carregava e a tela não dizia nada, o que é indistinguível de um botão
      // que não faz nada. Foi assim que este defeito chegou ao celular.
      //
      // Só no caminho de sucesso: no erro não há estado novo para desenhar, e
      // redesenhar à toa custaria a frase que explica a falha.
      if (!erro) this.renderAgentesLocais();

      // Relido depois do redesenho: o parágrafo de antes já não está no DOM.
      const alvo = document.getElementById("agentes-locais-numeros");
      if (alvo) {
        alvo.textContent = erro
          ? `não carregou: ${erro}`
          : `${nome} carregado no ONNX Runtime nativo.`;
      }
      return;
    }

    try {
      const r = await llmNativo.carregar(nome, { contexto: 2048, threads: 4 });
      // Quem carrega pela lista também passa a ser o modelo da conversa: seria
      // estranho carregar um modelo e a caixa de baixo continuar apontada para
      // outro.
      this.modeloCarregado = nome;
      this.modeloDaConversa = nome;
      if (numeros) numeros.textContent = `carregou em ${(r.ms / 1000).toFixed(1)}s, contexto de ${r.contexto} tokens`;
      this.renderAgentesLocais();
    } catch (err) {
      botao.disabled = false;
      botao.textContent = "carregar";
      // A frase vem do motor, e é a única coisa que distingue "o arquivo não é
      // GGUF" de "não coube na memória". Substituí-la por "erro ao carregar"
      // apagaria justamente o que serve para decidir o que fazer.
      if (numeros) numeros.textContent = `não carregou: ${(err as Error).message}`;
    }
  }

  private async gerarComModeloLocal() {
    const campo = document.getElementById("agentes-locais-prompt") as HTMLTextAreaElement | null;
    const prompt = campo?.value ?? "";
    const numeros = document.getElementById("agentes-locais-numeros");
    const bGerar = document.getElementById("agentes-locais-gerar") as HTMLButtonElement | null;
    const bParar = document.getElementById("agentes-locais-parar") as HTMLButtonElement | null;
    const modelo = this.modeloDaConversa;
    if (!prompt.trim() || !modelo) return;

    if (bGerar) bGerar.disabled = true;
    if (bParar) bParar.disabled = false;

    // O turno entra na pilha ANTES da resposta, com a resposta vazia. Assim o
    // texto aparece crescendo no lugar onde vai ficar, e não numa caixa
    // separada que depois some — o que faria a resposta "pular" de lugar ao
    // terminar.
    const turno = { modelo, pergunta: prompt.trim(), resposta: "", numeros: "" };
    this.turnos.push(turno);
    if (campo) campo.value = "";
    this.renderTurnos();

    try {
      // Trocar de modelo é descarregar e carregar: existe um contexto de
      // conversa por vez. Custa segundos, e por isso só acontece quando o
      // modelo escolhido não é o que está na memória.
      if (this.modeloCarregado !== modelo) {
        turno.numeros = "carregando o modelo…";
        this.atualizarUltimoTurno();
        const r = await llmNativo.carregar(modelo, { contexto: 2048, threads: 4 });
        this.modeloCarregado = modelo;
        this.pintarEstadoDoModelo();
        registro.anotar("modelo", `carregado: ${modelo} em ${(r.ms / 1000).toFixed(1)}s`);
        turno.numeros = `carregou em ${(r.ms / 1000).toFixed(1)}s`;
        this.atualizarUltimoTurno();
      }

      // `gerarChat`, não `gerar`: o modelo precisa da marcação de conversa do
      // próprio GGUF. Com texto cru ele continua a frase em vez de responder —
      // "Olá" saiu como o começo de uma redação sobre educação.
      const r = await llmNativo.gerarChat(
        [{ role: "user", content: turno.pergunta }],
        { maxTokens: 256, temperatura: 0.7 },
        (t) => {
          turno.resposta += t;
          // Caminho quente: um token por vez, dezenas por segundo. Refazer a
          // pilha aqui era o que travava a tela.
          this.atualizarUltimoTurno();
        },
      );
      turno.numeros =
        `${r.pedacos} pedaços em ${(r.ms / 1000).toFixed(1)}s · ` +
        `${r.porSegundo}/s · primeiro em ${(r.primeiroTokenMs / 1000).toFixed(1)}s` +
        // Só aparece quando o motor sabe informar. Dois números: quanto do
        // prompt veio pronto da memória e quanto teve de ser lido de novo.
        (r.prefill ? ` · leu ${leituraDoPrompt(r.prefill)}` : "");
      this.atualizarUltimoTurno();
    } catch (err) {
      turno.numeros = `falhou: ${(err as Error).message}`;
      this.atualizarUltimoTurno();
      registro.anotar("conversa", `${modelo}: ${(err as Error).message}`, "erro");
      if (numeros) numeros.textContent = `falhou: ${(err as Error).message}`;
      // Um carregamento que falhou deixa a memória em estado desconhecido; não
      // se pode continuar afirmando que este modelo está lá.
      this.modeloCarregado = null;
      this.pintarEstadoDoModelo();
    } finally {
      if (bGerar) bGerar.disabled = false;
      if (bParar) bParar.disabled = true;
    }
  }

  private wireAgents() {
    const modal = document.getElementById("agents-view");
    if (!modal) return;
    document.getElementById("agents-btn")?.addEventListener("click", () => {
      this.abrirAgentes();
    });
    document.getElementById("agents-back")?.addEventListener("click", () => {
      this.fecharAgentes();
    });
    document.getElementById("agent-create")?.addEventListener("click", async () => {
      const campo = document.getElementById("agent-name") as HTMLInputElement | null;
      const nome = campo?.value.trim();
      if (!nome) return;
      const r = await fetch("/agents/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session: this.sess.session, name: nome }),
      });
      const dados = await r.json();
      if (!r.ok) {
        this.mostrarAviso(dados.error ?? "falhou ao criar");
        return;
      }
      if (campo) campo.value = "";
      this.mostrarComando(dados.email, dados.token);
      void this.carregarAgentes();
    });
    document.getElementById("agent-copy")?.addEventListener("click", async () => {
      const pre = document.getElementById("agent-cmd");
      try {
        await navigator.clipboard.writeText(pre?.textContent ?? "");
        this.mostrarAviso("URL copiada");
      } catch {
        this.mostrarAviso("não consegui copiar");
      }
    });
  }

  /** Botão "instalar app" no menu. Aparece só quando o navegador sinaliza que
   *  a instalação é possível, e some depois de instalado. */
  private wireInstalar() {
    const btn = document.getElementById("install-btn") as HTMLButtonElement | null;
    if (!btn) return;

    // Já rodando instalado: não faz sentido oferecer.
    const instalado =
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true;
    if (instalado) return;

    // O botão fica visível mesmo sem o evento do navegador. O Chrome só
    // dispara `beforeinstallprompt` uma vez por período — se o usuário já
    // dispensou ou instalou antes, ele nunca mais avisa, e um botão que só
    // aparece com o evento fica invisível justamente para quem precisa dele.
    btn.hidden = false;
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      this.promptInstalar = e as never;
      btn.hidden = false;
    });

    window.addEventListener("appinstalled", () => {
      btn.hidden = true;
      this.promptInstalar = null;
      this.mostrarAviso("app instalado");
    });

    btn.onclick = async () => {
      if (!this.promptInstalar) {
        // Sem o evento nativo não há como abrir o convite por código —
        // o caminho manual do navegador continua funcionando.
        alert(
          "Para instalar:\n\n" +
            "Abra o menu ⋮ do Chrome (canto superior direito) e toque em " +
            '"Instalar app" ou "Adicionar à tela inicial".\n\n' +
            "O convite automático não apareceu porque o Chrome só o oferece " +
            "uma vez — se você já instalou ou dispensou antes, ele não " +
            "pergunta de novo.",
        );
        return;
      }
      await this.promptInstalar.prompt();
      const { outcome } = await this.promptInstalar.userChoice;
      // O evento é de uso único: depois de respondido, não serve mais.
      this.promptInstalar = null;
      btn.hidden = true;
      if (outcome !== "accepted") this.mostrarAviso("instalação cancelada");
    };
  }

  // ---------- Câmera no app ----------

  /** Abre a câmera dentro do app. Devolve false se o navegador não permitir —
   *  aí o chamador cai no seletor do sistema, em vez de deixar o botão morto. */
  private async abrirCamera(): Promise<boolean> {
    const box = document.getElementById("camera-view");
    const video = document.getElementById("cam-preview") as HTMLVideoElement | null;
    if (!box || !video || !navigator.mediaDevices?.getUserMedia) return false;
    try {
      this.camStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: this.camFacing },
        audio: true, // precisa estar no stream desde já: vídeo sem som é pior
      });
    } catch {
      return false;
    }
    video.srcObject = this.camStream;
    // O preview da frontal precisa ser espelhado, senão escrever some errado.
    video.style.transform = this.camFacing === "user" ? "scaleX(-1)" : "none";
    box.classList.remove("hidden");
    document.body.classList.add("no-scroll");
    return true;
  }

  private fecharCamera() {
    this.camStream?.getTracks().forEach((t) => t.stop());
    this.camStream = null;
    document.getElementById("camera-view")?.classList.add("hidden");
    document.body.classList.remove("no-scroll");
    this.camGravando = false;
    if (this.camTimer !== undefined) {
      clearInterval(this.camTimer);
      this.camTimer = undefined;
    }
  }

  /** Foto: um quadro do preview vira JPEG. */
  private async tirarFoto() {
    const video = document.getElementById("cam-preview") as HTMLVideoElement | null;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    if (this.camFacing === "user") {
      // Desespelha na hora de gravar: o espelho é conforto de quem se vê, não
      // do que a foto deve mostrar.
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0);
    const blob: Blob | null = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.9));
    if (!blob) return;
    const nome = `foto-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.jpg`;
    this.fecharCamera();
    void this.attachFile(new File([blob], nome, { type: "image/jpeg" }));
  }

  private iniciarGravacao() {
    if (!this.camStream || this.camGravando) return;
    const tipos = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];
    const mime = tipos.find((t) => MediaRecorder.isTypeSupported(t)) ?? "";
    const rec = new MediaRecorder(this.camStream, mime ? { mimeType: mime } : undefined);
    const pedacos: Blob[] = [];
    rec.ondataavailable = (e) => e.data.size && pedacos.push(e.data);
    rec.onstop = () => {
      const blob = new Blob(pedacos, { type: rec.mimeType || "video/webm" });
      const ext = (rec.mimeType || "video/webm").includes("mp4") ? "mp4" : "webm";
      const nome = `video-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.${ext}`;
      this.fecharCamera();
      // O servidor recusa acima de ~6 MB; avisar é melhor que falhar no envio.
      if (blob.size > 6 * 1024 * 1024) {
        this.mostrarAviso(`vídeo grande demais (${(blob.size / 1048576).toFixed(1)} MB)`);
        return;
      }
      void this.attachFile(new File([blob], nome, { type: blob.type }));
    };
    this.camRec = rec;
    this.camGravando = true;
    rec.start();
    navigator.vibrate?.(20);

    const inicio = Date.now();
    const timer = document.getElementById("cam-timer");
    const shutter = document.getElementById("cam-shutter");
    shutter?.classList.add("is-recording");
    if (timer) timer.hidden = false;
    this.camTimer = window.setInterval(() => {
      const s = Math.floor((Date.now() - inicio) / 1000);
      if (timer) timer.textContent = `0:${String(s).padStart(2, "0")}`;
      // Teto de 20s: acima disso o arquivo não passaria no envio de qualquer
      // forma, e é melhor parar sozinho do que perder a gravação inteira.
      if (s >= 20) this.pararGravacao();
    }, 250);
  }

  private pararGravacao() {
    if (!this.camGravando) return;
    this.camGravando = false;
    document.getElementById("cam-shutter")?.classList.remove("is-recording");
    const timer = document.getElementById("cam-timer");
    if (timer) timer.hidden = true;
    if (this.camTimer !== undefined) {
      clearInterval(this.camTimer);
      this.camTimer = undefined;
    }
    try {
      this.camRec?.stop();
    } catch {
      /* já parado */
    }
  }

  private wireCamera() {
    document.getElementById("cam-close")?.addEventListener("click", () => this.fecharCamera());
    document.getElementById("cam-flip")?.addEventListener("click", async () => {
      this.camFacing = this.camFacing === "user" ? "environment" : "user";
      this.camStream?.getTracks().forEach((t) => t.stop());
      await this.abrirCamera();
    });

    const shutter = document.getElementById("cam-shutter");
    if (!shutter) return;
    let apertadoEm = 0;
    let segurar: number | undefined;
    const LIMIAR = 320; // acima disso é "segurar", não "tocar"

    const pressionar = (ev: Event) => {
      ev.preventDefault();
      apertadoEm = Date.now();
      segurar = window.setTimeout(() => this.iniciarGravacao(), LIMIAR);
    };
    const soltar = (ev: Event) => {
      ev.preventDefault();
      if (segurar !== undefined) {
        clearTimeout(segurar);
        segurar = undefined;
      }
      if (this.camGravando) {
        this.pararGravacao();
      } else if (Date.now() - apertadoEm < LIMIAR) {
        void this.tirarFoto();
      }
    };
    shutter.addEventListener("touchstart", pressionar, { passive: false });
    shutter.addEventListener("touchend", soltar);
    shutter.addEventListener("touchcancel", soltar);
    shutter.addEventListener("mousedown", pressionar);
    shutter.addEventListener("mouseup", soltar);
    shutter.addEventListener("mouseleave", () => {
      if (this.camGravando) this.pararGravacao();
    });
  }

  /** Abre a imagem em tela cheia, com autor, data, legenda e ações. */
  private openImageViewer(msgId: string) {
    const msg = this.messages.find((m) => m.id === msgId);
    if (!msg?.attachment) return;
    const box = document.getElementById("img-viewer");
    const img = document.getElementById("iv-img") as HTMLImageElement | null;
    if (!box || !img) return;

    this.viewerMsgId = msgId;
    const url = this.blobUrlFor("f:" + msgId, msg.attachment.blob);
    img.src = url;
    img.alt = msg.attachment.name;

    const autor =
      msg.direction === "out" ? "Você" : this.displayName(msg.contact ?? this.currentContact ?? "");
    const quando = new Date(msg.ts);
    const hoje = new Date();
    const mesmoDia = quando.toDateString() === hoje.toDateString();
    const hora = quando.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const texto = document.getElementById("iv-author");
    const data = document.getElementById("iv-date");
    if (texto) texto.textContent = autor;
    if (data) {
      data.textContent = mesmoDia
        ? `Hoje ${hora}`
        : `${quando.toLocaleDateString([], { day: "2-digit", month: "2-digit" })} ${hora}`;
    }

    const cap = document.getElementById("iv-caption");
    if (cap) {
      cap.textContent = msg.text ?? "";
      cap.hidden = !msg.text;
    }

    const baixar = document.getElementById("iv-download") as HTMLAnchorElement | null;
    if (baixar) {
      baixar.href = url;
      baixar.download = msg.attachment.name;
    }
    // Apagar só nas próprias mensagens.
    box.querySelectorAll<HTMLElement>(".iv-only-out").forEach((el) => {
      el.hidden = msg.direction !== "out";
    });

    box.classList.remove("hidden");
    document.body.classList.add("no-scroll");
    // Imagem nova começa no tamanho normal, não no zoom da anterior.
    this.resetarZoom?.();
  }

  private closeImageViewer() {
    this.resetarZoom?.();
    document.getElementById("img-viewer")?.classList.add("hidden");
    document.body.classList.remove("no-scroll");
    this.viewerMsgId = null;
  }

  /** Pinça para ampliar e arrastar para navegar, dentro do visualizador.
   *
   *  Escrito à mão em vez de biblioteca: são dois gestos e um limite de
   *  borda. O ponto delicado é ampliar em torno do PONTO ENTRE OS DEDOS — se
   *  ampliar a partir do centro da tela, a imagem foge de baixo do dedo e o
   *  gesto parece quebrado. */
  private wireZoomImagem() {
    const palco = document.querySelector<HTMLElement>(".iv-stage");
    const img = document.getElementById("iv-img") as HTMLImageElement | null;
    if (!palco || !img) return;

    let escala = 1;
    let x = 0;
    let y = 0;
    let distIni = 0;
    let escalaIni = 1;
    let focoX = 0;
    let focoY = 0;
    let arrastando = false;
    let ultimoX = 0;
    let ultimoY = 0;
    let ultimoToque = 0;

    const aplicar = () => {
      // Sem passar do limite: com a imagem ampliada, o quanto ela pode deslizar
      // é metade do excedente para cada lado.
      const r = palco.getBoundingClientRect();
      const limiteX = Math.max(0, (r.width * escala - r.width) / 2);
      const limiteY = Math.max(0, (r.height * escala - r.height) / 2);
      x = Math.min(limiteX, Math.max(-limiteX, x));
      y = Math.min(limiteY, Math.max(-limiteY, y));
      img.style.transform = `translate(${x}px, ${y}px) scale(${escala})`;
      img.style.cursor = escala > 1 ? "grab" : "";
      // Com zoom, o gesto é nosso; sem zoom, deixa o navegador rolar.
      palco.style.touchAction = escala > 1 ? "none" : "";
    };

    const resetar = () => {
      escala = 1;
      x = 0;
      y = 0;
      img.style.transition = "transform 0.2s ease";
      aplicar();
      window.setTimeout(() => (img.style.transition = ""), 220);
    };
    this.resetarZoom = resetar;

    const dist = (t: TouchList) =>
      Math.hypot(t[0]!.clientX - t[1]!.clientX, t[0]!.clientY - t[1]!.clientY);

    palco.addEventListener(
      "touchstart",
      (ev) => {
        if (ev.touches.length === 2) {
          ev.preventDefault();
          distIni = dist(ev.touches);
          escalaIni = escala;
          const r = palco.getBoundingClientRect();
          focoX = (ev.touches[0]!.clientX + ev.touches[1]!.clientX) / 2 - r.left - r.width / 2;
          focoY = (ev.touches[0]!.clientY + ev.touches[1]!.clientY) / 2 - r.top - r.height / 2;
        } else if (ev.touches.length === 1) {
          const agora = Date.now();
          if (agora - ultimoToque < 300) {
            // Toque duplo: alterna entre tamanho normal e 2,5×.
            ev.preventDefault();
            img.style.transition = "transform 0.2s ease";
            escala = escala > 1 ? 1 : 2.5;
            if (escala === 1) {
              x = 0;
              y = 0;
            }
            aplicar();
            window.setTimeout(() => (img.style.transition = ""), 220);
          }
          ultimoToque = agora;
          if (escala > 1) {
            arrastando = true;
            ultimoX = ev.touches[0]!.clientX;
            ultimoY = ev.touches[0]!.clientY;
          }
        }
      },
      { passive: false },
    );

    palco.addEventListener(
      "touchmove",
      (ev) => {
        if (ev.touches.length === 2) {
          ev.preventDefault();
          const nova = Math.min(5, Math.max(1, escalaIni * (dist(ev.touches) / (distIni || 1))));
          // Mantém o ponto entre os dedos parado enquanto a escala muda.
          const fator = nova / escala;
          x = focoX - (focoX - x) * fator;
          y = focoY - (focoY - y) * fator;
          escala = nova;
          if (escala === 1) {
            x = 0;
            y = 0;
          }
          aplicar();
        } else if (arrastando && ev.touches.length === 1 && escala > 1) {
          ev.preventDefault();
          x += ev.touches[0]!.clientX - ultimoX;
          y += ev.touches[0]!.clientY - ultimoY;
          ultimoX = ev.touches[0]!.clientX;
          ultimoY = ev.touches[0]!.clientY;
          aplicar();
        }
      },
      { passive: false },
    );

    const soltar = () => {
      arrastando = false;
      if (escala <= 1.02) resetar();
    };
    palco.addEventListener("touchend", soltar);
    palco.addEventListener("touchcancel", soltar);

    // No desktop, roda do mouse com Ctrl amplia — o mesmo gesto do sistema.
    palco.addEventListener(
      "wheel",
      (ev) => {
        if (!ev.ctrlKey) return;
        ev.preventDefault();
        const r = palco.getBoundingClientRect();
        focoX = ev.clientX - r.left - r.width / 2;
        focoY = ev.clientY - r.top - r.height / 2;
        const nova = Math.min(5, Math.max(1, escala * (ev.deltaY < 0 ? 1.12 : 0.89)));
        const fator = nova / escala;
        x = focoX - (focoX - x) * fator;
        y = focoY - (focoY - y) * fator;
        escala = nova;
        if (escala === 1) {
          x = 0;
          y = 0;
        }
        aplicar();
      },
      { passive: false },
    );
  }

  private wireImageViewer() {
    this.wireZoomImagem();
    document.getElementById("iv-close")?.addEventListener("click", () => this.closeImageViewer());
    document.getElementById("iv-forward")?.addEventListener("click", () => {
      const id = this.viewerMsgId;
      this.closeImageViewer();
      if (id) void this.startForward(id);
    });
    document.getElementById("iv-delete")?.addEventListener("click", () => {
      const id = this.viewerMsgId;
      this.closeImageViewer();
      if (id) void this.handleDeleteMessage(id);
    });
    document.getElementById("iv-reply")?.addEventListener("click", () => {
      const id = this.viewerMsgId;
      this.closeImageViewer();
      if (id) void this.startReply(id);
    });
    document.querySelectorAll<HTMLButtonElement>(".iv-quick").forEach((btn) => {
      btn.onclick = () => {
        const id = this.viewerMsgId;
        const emoji = btn.dataset.emoji;
        if (id && emoji) void this.toggleReaction(id, emoji);
        this.closeImageViewer();
      };
    });
    // Esc fecha, como qualquer modal.
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.viewerMsgId) this.closeImageViewer();
    });
  }

  /** Aviso efêmero no rodapé. O app não tinha nenhum feedback desse tipo, e
   *  "copiar" sem confirmação deixa o usuário sem saber se funcionou. */
  private mostrarAviso(texto: string) {
    let el = document.getElementById("mini-toast");
    if (!el) {
      el = document.createElement("div");
      el.id = "mini-toast";
      el.className = "mini-toast";
      document.body.appendChild(el);
    }
    el.textContent = texto;
    el.classList.add("is-on");
    if (this.avisoTimer !== undefined) clearTimeout(this.avisoTimer);
    this.avisoTimer = window.setTimeout(() => el?.classList.remove("is-on"), 1600);
  }

  /** Modo seleção: o cabeçalho da conversa vira barra de ações da mensagem
   *  selecionada, e a bolha ganha destaque. Espelha o WhatsApp. */
  private openSelectionBar(msgId: string, _direction: "in" | "out") {
    this.selecionadas.add(msgId);
    this.atualizarBarraSelecao();
  }

  /** Marca/desmarca uma mensagem. Sair da última desliga o modo seleção. */
  private alternarSelecao(msgId: string) {
    if (this.selecionadas.has(msgId)) this.selecionadas.delete(msgId);
    else this.selecionadas.add(msgId);
    if (this.selecionadas.size === 0) this.closeSelectionBar();
    else this.atualizarBarraSelecao();
  }

  /** Redesenha a barra a partir da seleção atual. Ações que só fazem sentido
   *  sobre uma mensagem (responder, encaminhar, editar) somem quando há
   *  várias; apagar só aparece se TODAS forem minhas. */
  private atualizarBarraSelecao() {
    const bar = document.getElementById("selection-bar");
    if (!bar) return;
    const ids = [...this.selecionadas];
    const msgs = this.messages.filter((m) => this.selecionadas.has(m.id));
    const uma = ids.length === 1;
    const todasMinhas = msgs.length > 0 && msgs.every((m) => m.direction === "out");

    // menuTargetMsgId continua servindo o picker de reação e o menu antigo,
    // que operam sobre uma mensagem só.
    this.menuTargetMsgId = uma ? ids[0]! : null;

    const contador = bar.querySelector<HTMLElement>(".sel-count");
    if (contador) contador.textContent = String(ids.length);
    bar.querySelectorAll<HTMLElement>(".sel-act").forEach((el) => {
      const act = el.dataset.act;
      if (act === "reply" || act === "forward" || act === "edit") el.hidden = !uma;
      if (act === "delete") el.hidden = !todasMinhas;
    });
    bar.querySelectorAll<HTMLElement>(".sel-only-out").forEach((el) => {
      if (el.dataset.act === "edit") el.hidden = !uma || !todasMinhas;
    });
    // O botão do modelo só faz sentido sobre UMA mensagem, e só se houver algo
    // que ele possa fazer com ela — o mesmo julgamento do menu.
    const botaoIa = bar.querySelector<HTMLElement>(".sel-ia");
    if (botaoIa) {
      const msg = uma ? msgs[0] : undefined;
      const ehImagem = (msg?.attachment?.mime ?? "").startsWith("image/");
      const temTexto = (msg?.text ?? "").length > 0;
      const podeConversa = iaContato.modelosParaConversa().length > 0 && temTexto;
      const naConversaLocal =
        !!this.currentContact && iaContato.ehContatoLocal(this.currentContact);
      const util = uma && !naConversaLocal && (podeConversa || (ehImagem && postura.disponivel()));
      botaoIa.classList.toggle("hidden", !util);
      botaoIa.hidden = !util;
    }
    bar.classList.remove("hidden");

    document
      .querySelectorAll("#messages .bubble.is-selected")
      .forEach((el) => el.classList.remove("is-selected"));
    for (const id of ids) {
      document
        .querySelector(`#messages .bubble[data-msg-id="${CSS.escape(id)}"]`)
        ?.classList.add("is-selected");
    }
  }

  private closeSelectionBar() {
    this.selecionadas.clear();
    document.getElementById("selection-bar")?.classList.add("hidden");
    document
      .querySelectorAll("#messages .bubble.is-selected")
      .forEach((el) => el.classList.remove("is-selected"));
  }

  private wireSelectionBar() {
    const bar = document.getElementById("selection-bar");
    if (!bar) return;
    document.getElementById("sel-close")?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      this.closeMenus();
    });
    bar.querySelectorAll<HTMLButtonElement>(".sel-act").forEach((btn) => {
      btn.onclick = async (ev) => {
        ev.stopPropagation();
        const alvos = [...this.selecionadas];
        const id = this.menuTargetMsgId ?? alvos[0];
        if (!id && alvos.length === 0) return;
        const act = btn.dataset.act;
        if (act === "ia") {
          // Abre o menu do modelo pelo gesto que o celular tem.
          //
          // A âncora é a BOLHA, não este botão: `openMessageMenu` começa
          // fechando tudo, e a barra de seleção some junto — posicionar o menu
          // por um elemento que acabou de ser escondido o joga para o canto
          // superior esquerdo da tela.
          const alvo = id!;
          const direcao = this.messages.find((m) => m.id === alvo)?.direction ?? "in";
          const bolha = document.querySelector<HTMLElement>(
            `#messages .bubble[data-msg-id="${CSS.escape(alvo)}"]`,
          );
          this.openMessageMenu(bolha ?? btn, alvo, direcao);
          return;
        }
        if (act === "copy") {
          // Várias mensagens: junta na ordem da conversa, uma por linha.
          const escolhidas = this.messages.filter((m) => this.selecionadas.has(m.id));
          const texto = escolhidas.map((m) => m.text ?? "").join("\n");
          try {
            await navigator.clipboard.writeText(texto);
            this.mostrarAviso("copiado");
          } catch {
            // clipboard exige contexto seguro e permissão; sem ele não dá pra
            // fingir sucesso.
            this.mostrarAviso("não consegui copiar");
          }
          this.closeMenus();
          return;
        }
        this.closeMenus();
        if (act === "reply") void this.startReply(id);
        else if (act === "forward") void this.startForward(id);
        else if (act === "edit") void this.handleEditMessage(id);
        else if (act === "delete") void this.apagarSelecionadas(alvos);
      };
    });
  }

  /** Apaga em lote, uma chamada por mensagem — o protocolo só conhece delete
   *  individual, então o lote é conveniência da interface, não do servidor. */
  private async apagarSelecionadas(ids: string[]) {
    for (const id of ids) await this.handleDeleteMessage(id);
    this.mostrarAviso(ids.length === 1 ? "apagada" : `${ids.length} apagadas`);
  }

  /** Abre a barra de reações. Sem âncora explícita usa o botão de ações
   *  (desktop); no celular a âncora é a própria bolha, aberta por long-press. */
  private openReactPicker(msgId: string, anchorEl?: HTMLElement) {
    const trigger =
      anchorEl ??
      document.querySelector<HTMLElement>(
        `#messages .msg-trigger[data-msg-id="${CSS.escape(msgId)}"]`,
      );
    if (!trigger) return;
    const picker = $("react-picker") as HTMLElement;
    picker.classList.remove("hidden");
    this.menuTargetMsgId = msgId;
    if (trigger.classList.contains("msg-trigger")) trigger.classList.add("is-open");
    this.positionPopover(picker, trigger);
    this.installOutsideHandler();
  }

  /** Posiciona popover acima ou abaixo do trigger conforme espaço disponível. */
  private positionPopover(popover: HTMLElement, anchor: HTMLElement) {
    // Reset pra medir tamanho real
    popover.style.left = "0";
    popover.style.top = "0";
    const rect = anchor.getBoundingClientRect();
    const pw = popover.offsetWidth;
    const ph = popover.offsetHeight;
    const margin = 6;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // horizontal: alinha pela direita do anchor se OUT, esquerda se IN
    const direction = anchor.dataset.direction === "out" ? "right" : "left";
    let left =
      direction === "right"
        ? rect.right - pw
        : rect.left;
    left = Math.max(8, Math.min(left, vw - pw - 8));
    // vertical: tenta acima; se estoura, abaixo
    let top = rect.top - ph - margin;
    if (top < 8) top = rect.bottom + margin;
    if (top + ph > vh - 8) top = Math.max(8, vh - ph - 8);
    popover.style.left = `${left}px`;
    popover.style.top = `${top}px`;
  }

  private closeMenus() {
    ($("msg-menu") as HTMLElement).classList.add("hidden");
    ($("react-picker") as HTMLElement).classList.add("hidden");
    this.closeSelectionBar();
    document.querySelectorAll(".msg-trigger.is-open").forEach((el) => {
      (el as HTMLElement).classList.remove("is-open");
    });
    this.menuTargetMsgId = null;
    if (this.menuOutsideHandler) {
      document.removeEventListener("mousedown", this.menuOutsideHandler);
      document.removeEventListener("touchstart", this.menuOutsideHandler);
      this.menuOutsideHandler = null;
    }
  }

  private installOutsideHandler() {
    if (this.menuOutsideHandler) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // A barra de seleção faz parte do menu, não é "fora". Sem esta linha o
      // mousedown/touchstart nela fechava tudo e zerava menuTargetMsgId antes
      // do click chegar ao botão — as ações não faziam nada.
      if (target.closest("#selection-bar")) return;
      if (target.closest("#msg-menu") || target.closest("#react-picker")) return;
      if (target.closest(".msg-trigger")) return;
      this.closeMenus();
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    this.menuOutsideHandler = handler;
    // Fecha se a lista de mensagens scrollar
    const m = $("messages");
    m.addEventListener("scroll", () => this.closeMenus(), { once: true });
  }

  // ---------- Reactions ----------

  private async toggleReaction(msgId: string, emoji: string) {
    const me = this.sess.email;
    const stored = await this.store.getMessageById(msgId);
    if (!stored) return;
    const current = stored.reactions?.[emoji] ?? [];
    const op: "add" | "remove" = current.includes(me) ? "remove" : "add";
    const updated = await this.store.applyReaction(msgId, me, emoji, op);
    if (updated) this.patchLocalMessage(updated, { animatePillEmoji: op === "add" ? emoji : null });
    // O contato é o "outro lado" da conversa onde a msg vive
    void this.enviarOuEnfileirar(
      { type: "react", id: msgId, to: stored.contact, emoji, op },
      `react:${msgId}:${emoji}`,
    );
  }

  /** Re-renderiza só a bolha alterada (sem perder scroll). Se animatePillEmoji,
   *  destaca a pill que acabou de aparecer. */
  private patchLocalMessage(updated: ChatMessage, opts?: { animatePillEmoji?: string | null }) {
    const i = this.messages.findIndex((m) => m.id === updated.id);
    if (i >= 0) this.messages[i] = updated;
    if (this.currentContact !== updated.contact) return;
    const wrapper = document.querySelector<HTMLElement>(
      `#messages .bubble[data-msg-id="${CSS.escape(updated.id)}"]`,
    );
    if (!wrapper) return;
    // Substitui só o bloco .bubble-reactions (preserva o restante)
    const old = wrapper.querySelector(".bubble-reactions");
    const newHtml = this.renderReactions(updated);
    if (old) old.remove();
    if (newHtml) {
      const meta = wrapper.querySelector(".bubble-meta");
      const tmp = document.createElement("div");
      tmp.innerHTML = newHtml;
      const node = tmp.firstElementChild as HTMLElement | null;
      if (node && meta) {
        wrapper.insertBefore(node, meta);
        // re-cabea click handlers
        node.querySelectorAll<HTMLButtonElement>(".reaction-pill").forEach((btn) => {
          btn.onclick = (ev) => {
            ev.stopPropagation();
            const id = btn.dataset.msgId!;
            const emoji = btn.dataset.emoji!;
            void this.toggleReaction(id, emoji);
          };
          if (opts?.animatePillEmoji && btn.dataset.emoji === opts.animatePillEmoji) {
            btn.classList.add("is-new");
            setTimeout(() => btn.classList.remove("is-new"), 320);
          }
        });
      }
    }
  }

  private async applyIncomingReaction(
    msgId: string,
    actor: string,
    emoji: string,
    op: "add" | "remove",
  ) {
    const updated = await this.store.applyReaction(msgId, actor, emoji, op);
    if (updated) this.patchLocalMessage(updated, { animatePillEmoji: op === "add" ? emoji : null });
  }

  // ---------- Quote / Reply ----------

  private async startReply(msgId: string) {
    const msg = await this.store.getMessageById(msgId);
    if (!msg) return;
    const quote: QuotedRef = {
      id: msg.id,
      from: msg.direction === "out" ? this.sess.email : msg.contact,
      snippet: this.buildQuoteSnippet(msg),
      hasMedia: !!(msg.audio || msg.attachment),
    };
    this.setComposerQuote(quote);
    const ta = $("composer-input") as HTMLTextAreaElement;
    ta.focus();
  }

  private buildQuoteSnippet(msg: ChatMessage): string {
    const text = (msg.text || "").trim().replace(/\s+/g, " ");
    if (text) return text.slice(0, 200);
    if (msg.audio) return "🎤 áudio";
    if (msg.attachment) return `📎 ${msg.attachment.name}`;
    return "";
  }

  private setComposerQuote(q: QuotedRef) {
    this.composerQuote = q;
    const wrap = $("quote-preview");
    ($("quote-preview-from") as HTMLElement).textContent = q.from;
    ($("quote-preview-snippet") as HTMLElement).textContent =
      q.snippet || (q.hasMedia ? "🎤 mídia" : "(sem texto)");
    wrap.classList.remove("hidden");
  }

  private clearComposerQuote() {
    this.composerQuote = null;
    $("quote-preview").classList.add("hidden");
  }

  private wireQuoteCancel() {
    ($("quote-preview-cancel") as HTMLButtonElement).onclick = () =>
      this.clearComposerQuote();
  }

  // ---------- Forward ----------

  private async startForward(msgId: string) {
    const msg = await this.store.getMessageById(msgId);
    if (!msg) return;
    this.forwardingMsg = msg;
    this.forwardSelected.clear();
    this.contacts = await this.store.listContacts();
    this.renderForwardModal();
    ($("forward-modal") as HTMLElement).classList.remove("hidden");
  }

  private renderForwardModal() {
    const msg = this.forwardingMsg;
    if (!msg) return;
    const preview = $("forward-preview");
    const text = msg.text || "";
    const fromLabel = msg.direction === "out" ? this.sess.email : msg.contact;
    const mediaTag = msg.audio
      ? "🎤 áudio"
      : msg.attachment
        ? `📎 ${msg.attachment.name}`
        : "";
    preview.innerHTML = `
      <span class="forward-preview-from">${escapeHtml(fromLabel)}</span>
      ${text ? `<span class="forward-preview-text">${escapeHtml(text)}</span>` : ""}
      ${mediaTag ? `<span class="forward-preview-media">${escapeHtml(mediaTag)}</span>` : ""}
    `;

    const list = $("forward-list");
    const candidates = this.contacts.filter(
      (c) => c.email !== this.sess.email && c.email !== msg.contact,
    );
    if (candidates.length === 0) {
      list.innerHTML = `<div class="forward-empty">Sem outros contatos para encaminhar.</div>`;
    } else {
      list.innerHTML = candidates
        .map((c) => {
          const checked = this.forwardSelected.has(c.email);
          return `
            <button class="forward-row${checked ? " is-checked" : ""}"
                    data-email="${escapeHtml(c.email)}" type="button">
              <span class="avatar avatar-sm" style="background:${colorFor(c.email)}">${escapeHtml(avatarInitials(c.email))}</span>
              <span class="forward-row-meta">
                <span class="forward-row-name">${escapeHtml(c.email)}</span>
                ${c.lastText ? `<span class="forward-row-last">${escapeHtml(c.lastText)}</span>` : ""}
              </span>
              <span class="forward-check">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
            </button>`;
        })
        .join("");
      list.querySelectorAll<HTMLButtonElement>(".forward-row").forEach((row) => {
        row.onclick = () => {
          const em = row.dataset.email!;
          if (this.forwardSelected.has(em)) this.forwardSelected.delete(em);
          else this.forwardSelected.add(em);
          row.classList.toggle("is-checked");
          this.updateForwardCount();
        };
      });
    }
    this.updateForwardCount();
  }

  private updateForwardCount() {
    const n = this.forwardSelected.size;
    ($("forward-count") as HTMLElement).textContent = `(${n})`;
    ($("forward-confirm") as HTMLButtonElement).disabled = n === 0;
  }

  private wireForwardModal() {
    ($("forward-close") as HTMLButtonElement).onclick = () => this.closeForwardModal();
    ($("forward-cancel") as HTMLButtonElement).onclick = () => this.closeForwardModal();
    ($("forward-modal") as HTMLElement).onclick = (ev) => {
      if ((ev.target as HTMLElement).id === "forward-modal") this.closeForwardModal();
    };
    ($("forward-confirm") as HTMLButtonElement).onclick = () => {
      void this.confirmForward();
    };
  }

  private closeForwardModal() {
    ($("forward-modal") as HTMLElement).classList.add("hidden");
    this.forwardingMsg = null;
    this.forwardSelected.clear();
  }

  private async confirmForward() {
    const msg = this.forwardingMsg;
    if (!msg) return;
    const recipients = Array.from(this.forwardSelected);
    this.closeForwardModal();
    const original = msg.forwardedFrom
      ?? (msg.direction === "out" ? this.sess.email : msg.contact);
    for (const to of recipients) {
      await this.sendForwardCopy(msg, to, original);
    }
    // Refresca contatos (lastTs/lastText podem ter mudado)
    this.contacts = await this.store.listContacts();
    this.renderContacts();
  }

  private async sendForwardCopy(msg: ChatMessage, to: string, originalFrom: string) {
    const id = crypto.randomUUID();
    const ts = Date.now();
    const copy: ChatMessage = {
      id,
      contact: to,
      direction: "out",
      text: msg.text,
      ts,
      status: "pending",
      forwardedFrom: originalFrom,
    };
    let audioWire;
    if (msg.audio) {
      copy.audio = { ...msg.audio };
      audioWire = {
        mime: msg.audio.mime,
        data: await blobToBase64(msg.audio.blob),
        durationMs: msg.audio.durationMs,
        lang: msg.audio.lang,
      };
    }
    let attWire;
    if (msg.attachment) {
      copy.attachment = { ...msg.attachment };
      attWire = {
        name: msg.attachment.name,
        mime: msg.attachment.mime,
        data: await blobToBase64(msg.attachment.blob),
      };
    }
    await this.store.ensureContact(to);
    await this.store.putMessage(copy);
    await this.enviarSend({
      type: "send",
      id,
      to,
      text: msg.text,
      ...(audioWire ? { audio: audioWire } : {}),
      ...(attWire ? { attachment: attWire } : {}),
      forwardedFrom: originalFrom,
    });
  }

  // ---------- Groups ----------

  private wireGroupModal() {
    const modal = document.getElementById("group-modal");
    const close = document.getElementById("group-close") as HTMLButtonElement | null;
    const cancel = document.getElementById("group-cancel") as HTMLButtonElement | null;
    const confirm = document.getElementById("group-confirm") as HTMLButtonElement | null;
    const nameInput = document.getElementById("group-name") as HTMLInputElement | null;
    if (!modal || !close || !cancel || !confirm || !nameInput) return;
    close.onclick = () => this.closeGroupModal();
    cancel.onclick = () => this.closeGroupModal();
    modal.onclick = (ev) => {
      if ((ev.target as HTMLElement).id === "group-modal") this.closeGroupModal();
    };
    nameInput.addEventListener("input", () => this.updateGroupConfirm());
    confirm.onclick = () => void this.confirmCreateGroup();
  }

  private openGroupModal() {
    this.groupSelected.clear();
    const nameInput = document.getElementById("group-name") as HTMLInputElement | null;
    if (nameInput) nameInput.value = "";
    this.renderGroupMembers();
    this.updateGroupConfirm();
    document.getElementById("group-modal")?.classList.remove("hidden");
    nameInput?.focus();
  }

  private closeGroupModal() {
    document.getElementById("group-modal")?.classList.add("hidden");
  }

  private renderGroupMembers() {
    const list = document.getElementById("group-members");
    if (!list) return;
    const candidates = this.contacts.filter(
      (c) => !c.email.startsWith("g:") && c.email !== this.sess.email,
    );
    if (candidates.length === 0) {
      list.innerHTML = `<div class="forward-empty">Sem contatos. Adicione um contato antes de criar grupo.</div>`;
      return;
    }
    list.innerHTML = candidates
      .map((c) => {
        const checked = this.groupSelected.has(c.email);
        return `
          <button class="forward-row${checked ? " is-checked" : ""}"
                  data-email="${escapeHtml(c.email)}" type="button">
            <span class="avatar avatar-sm" style="background:${colorFor(c.email)}">${escapeHtml(avatarInitials(c.email))}</span>
            <span class="forward-row-meta">
              <span class="forward-row-name">${escapeHtml(c.email)}</span>
            </span>
            <span class="forward-check">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
            </span>
          </button>`;
      })
      .join("");
    list.querySelectorAll<HTMLButtonElement>(".forward-row").forEach((row) => {
      row.onclick = () => {
        const em = row.dataset.email!;
        if (this.groupSelected.has(em)) this.groupSelected.delete(em);
        else this.groupSelected.add(em);
        row.classList.toggle("is-checked");
        this.updateGroupConfirm();
      };
    });
  }

  private updateGroupConfirm() {
    const n = this.groupSelected.size;
    const nameInput = document.getElementById("group-name") as HTMLInputElement | null;
    const hasName = !!nameInput?.value.trim();
    const btn = document.getElementById("group-confirm") as HTMLButtonElement | null;
    const counter = document.getElementById("group-count");
    if (counter) counter.textContent = `(${n})`;
    if (btn) btn.disabled = !(hasName && n >= 1);
  }

  private async confirmCreateGroup() {
    const nameInput = document.getElementById("group-name") as HTMLInputElement | null;
    const name = nameInput?.value.trim() || "";
    const members = Array.from(this.groupSelected);
    if (!name || members.length === 0) return;
    this.closeGroupModal();
    this.conn.send({ type: "create_group", name, members });
    // O resto vem via group_created event handler.
  }

  /** Nome amigável pra exibição. Para email, retorna o próprio. Para grupo
   *  ("g:..."), retorna o name do Group. */
  private displayName(idOrEmail: string): string {
    if (idOrEmail.startsWith("g:")) {
      return this.groups.get(idOrEmail)?.name ?? idOrEmail;
    }
    // Modelo no aparelho: o nome sai do arquivo, e não de perfil nenhum. Ele
    // não tem conta, não tem email e nunca vai ter — o endereço é o arquivo.
    if (iaContato.ehContatoLocal(idOrEmail)) {
      return iaContato.nomeDoModelo(iaContato.arquivoDoContato(idOrEmail));
    }
    // O nome escolhido pela pessoa ganha do email. Sem perfil, o email segue
    // sendo o nome — é feio, mas é verdadeiro, e não inventa identidade.
    return this.perfis.get(idOrEmail.toLowerCase())?.name || idOrEmail;
  }

  private attachScrollListener() {
    if (this.scrollListenerAttached) return;
    const m = $("messages");
    m.addEventListener("scroll", () => {
      if (m.scrollTop < 80) void this.loadOlderMessages();
    });
    this.scrollListenerAttached = true;
    this.ativarLinhaDoTempo(m, ".stack[data-ts]");
  }

  /** Aponta a linha do tempo para uma lista. A mesma pílula, alça e régua
   *  servem as três telas — conversa, arquivos e conversas — porque são a
   *  mesma experiência, e duas implementações divergiriam na primeira correção.
   *
   *  Só exige que os itens tragam `data-ts` e estejam em ordem de tempo. */
  private ativarLinhaDoTempo(alvo: HTMLElement, seletor: string) {
    this.reguaAssinatura = "";
    if (this.listasComScroll.has(alvo)) return;
    this.listasComScroll.add(alvo);

    const gesto = () => { this.ultimoGesto = performance.now(); };
    alvo.addEventListener("pointerdown", gesto, { passive: true });
    alvo.addEventListener("touchstart", gesto, { passive: true });
    alvo.addEventListener("wheel", gesto, { passive: true });

    // Quem rola manda, e leva o próprio seletor. Guardar "a lista ativa" num
    // campo não funciona: a lista de conversas se re-renderiza a cada mensagem
    // e roubava o alvo da conversa aberta — rolar as mensagens deixava de
    // acender nada, e quando acendia lia `lastTs` de contato e escrevia
    // "dez 1969". Sem estado global não há o que roubar.
    alvo.addEventListener("scroll", () => this.agendarMarcadorTempo(alvo, seletor));
  }

  /** O scroll dispara muitas vezes por segundo e a leitura do marcador mede
   *  geometria — medir a cada evento força reflow em rajada. Uma medição por
   *  quadro basta: o olho não acompanha mais que isso. */
  private agendarMarcadorTempo(alvo: HTMLElement, seletor: string) {
    // Cancelar e reagendar, em vez de "já tem um agendado, desiste": o quadro
    // agendado pode nunca rodar — é o que acontece quando o app vai para
    // segundo plano no meio do gesto. Com o desiste, a flag ficava presa e
    // TODA chamada seguinte voltava na porta: a linha do tempo morria e só
    // reiniciar o app trazia de volta. O throttle continua o mesmo (um por
    // quadro, o último vence), sem estado que possa emperrar.
    if (this.marcadorRaf !== null) window.cancelAnimationFrame(this.marcadorRaf);
    this.marcadorRaf = window.requestAnimationFrame(() => {
      this.marcadorRaf = null;
      if (this.foraDaConversa) return;
      this.atualizarMarcadorTempo(alvo, seletor);
    });
  }

  /** Cria (uma vez) a pílula e a alça, e devolve as duas. */
  private garantirLinhaDoTempo(): { pilula: HTMLElement; alca: HTMLElement; regua: HTMLElement } {
    if (!this.marcadorEl) {
      const el = document.createElement("div");
      el.className = "timeline-marker";
      // Decoração de navegação: quem usa leitor de tela já tem o divisor de
      // período no fluxo, e anunciar isto a cada quadro seria ruído.
      el.setAttribute("aria-hidden", "true");
      document.body.appendChild(el);
      this.marcadorEl = el;
    }
    if (!this.alcaEl) {
      const a = document.createElement("div");
      a.className = "timeline-grip";
      a.setAttribute("role", "slider");
      a.setAttribute("aria-label", "navegar pela conversa");
      a.innerHTML =
        `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"` +
        ` stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
        `<polyline points="8 9 12 5 16 9"/><polyline points="8 15 12 19 16 15"/></svg>`;
      document.body.appendChild(a);
      this.alcaEl = a;
      this.ligarArrasteDaAlca(a);
    }
    if (!this.reguaEl) {
      const r = document.createElement("div");
      r.className = "timeline-ruler";
      r.setAttribute("aria-hidden", "true");
      document.body.appendChild(r);
      this.reguaEl = r;
    }
    return { pilula: this.marcadorEl, alca: this.alcaEl, regua: this.reguaEl };
  }

  /** A régua: os rótulos fixos na lateral, que dizem para onde a alça leva
   *  ANTES de soltar. Sem ela, arrastar é às cegas — foi o que o print do
   *  Google Fotos mostrava com os anos na borda.
   *
   *  Recalculada só quando a lista muda de altura ou de conteúdo, não a cada
   *  quadro: são medições de offset em N elementos, caro demais para o scroll. */
  private atualizarReguaTempo(alvo: HTMLElement, regua: HTMLElement, seletor: string) {
    const assinatura = `${alvo.id}|${alvo.scrollHeight}|${alvo.querySelectorAll(seletor).length}`;
    if (assinatura === this.reguaAssinatura) return;
    this.reguaAssinatura = assinatura;

    const nodes = Array.from(alvo.querySelectorAll<HTMLElement>(seletor));
    const rolavel = alvo.scrollHeight - alvo.clientHeight;
    if (nodes.length === 0 || rolavel <= 0) {
      regua.innerHTML = "";
      return;
    }

    // Uma marca por faixa, na posição da PRIMEIRA ocorrência dela — é onde a
    // faixa começa, e é para lá que quem mira nela quer ir.
    const vistos = new Set<string>();
    const marcas: { texto: string; fracao: number }[] = [];
    for (const node of nodes) {
      const ts = Number(node.dataset.ts);
      if (!Number.isFinite(ts)) continue;
      const texto = marcadorTempo(ts);
      if (vistos.has(texto)) continue;
      vistos.add(texto);
      marcas.push({ texto, fracao: Math.min(1, Math.max(0, node.offsetTop / alvo.scrollHeight)) });
    }

    // Numa conversa longa as faixas viram dezenas e a régua vira uma parede
    // ilegível. Fica com as extremidades e um recorte espaçado do meio.
    const MAX = 8;
    const escolhidas =
      marcas.length <= MAX
        ? marcas
        : marcas.filter((_, i) => i === 0 || i === marcas.length - 1 || i % Math.ceil(marcas.length / (MAX - 2)) === 0);

    regua.innerHTML = escolhidas
      .map(
        (mk) =>
          `<span class="timeline-tick" style="top:${(mk.fracao * 100).toFixed(2)}%">${escapeHtml(mk.texto)}</span>`,
      )
      .join("");
  }

  /** Arrastar a alça percorre a conversa inteira: a posição vertical do dedo
   *  vira fração do scroll. É o que torna útil um histórico de meses — rolar
   *  por gesto levaria dezenas de flings. */
  private ligarArrasteDaAlca(alca: HTMLElement) {
    const mover = (clientY: number) => {
      const m = this.alcaAlvo?.el;
      if (!m) return;
      const caixa = m.getBoundingClientRect();
      const alcance = Math.max(1, caixa.height - this.ALCA_PX);
      const y = clientY - caixa.top - this.ALCA_PX / 2;
      const fracao = Math.min(1, Math.max(0, y / alcance));
      m.scrollTop = fracao * (m.scrollHeight - m.clientHeight);
      this.agendarMarcadorTempo(m, this.alcaAlvo?.seletor ?? ".stack[data-ts]");
    };
    alca.addEventListener("pointerdown", (e: PointerEvent) => {
      e.preventDefault();
      this.arrastandoAlca = true;
      alca.setPointerCapture(e.pointerId);
      alca.classList.add("is-dragging");
      mover(e.clientY);
    });
    alca.addEventListener("pointermove", (e: PointerEvent) => {
      if (this.arrastandoAlca) mover(e.clientY);
    });
    const soltar = (e: PointerEvent) => {
      if (!this.arrastandoAlca) return;
      this.arrastandoAlca = false;
      alca.classList.remove("is-dragging");
      try { alca.releasePointerCapture(e.pointerId); } catch { /* já solto */ }
      const atual = this.alcaAlvo;
      if (atual) this.agendarMarcadorTempo(atual.el, atual.seletor);
    };
    alca.addEventListener("pointerup", soltar);
    alca.addEventListener("pointercancel", soltar);
  }

  /** Mostra a que altura do tempo o scroll chegou, à direita e no meio da
   *  altura da conversa — que é para onde o olho vai enquanto se rola. Aparece
   *  ao rolar e some sozinho: parado, seria enfeite competindo com a mensagem. */
  private atualizarMarcadorTempo(m: HTMLElement, seletor: string) {
    if (!m.isConnected || m.clientHeight === 0) return;
    this.alcaAlvo = { el: m, seletor };
    const { pilula, alca, regua } = this.garantirLinhaDoTempo();
    const caixa = m.getBoundingClientRect();

    // A primeira stack que ainda não saiu por cima é a que manda no rótulo.
    let ts: number | null = null;
    for (const node of Array.from(m.querySelectorAll<HTMLElement>(seletor))) {
      const r = node.getBoundingClientRect();
      if (r.bottom >= caixa.top + 4) {
        const n = Number(node.dataset.ts);
        if (Number.isFinite(n)) ts = n;
        break;
      }
    }
    if (ts === null) return;

    // A alça acompanha a fração já rolada; a pílula anda colada nela.
    const rolavel = m.scrollHeight - m.clientHeight;
    const fracao = rolavel > 0 ? Math.min(1, Math.max(0, m.scrollTop / rolavel)) : 0;
    const topoAlca = caixa.top + fracao * Math.max(0, caixa.height - this.ALCA_PX);
    const direita = Math.max(6, window.innerWidth - caixa.right + 6);

    alca.style.top = `${topoAlca}px`;
    alca.style.right = `${direita}px`;

    this.atualizarReguaTempo(m, regua, seletor);
    regua.style.top = `${caixa.top}px`;
    regua.style.height = `${caixa.height}px`;
    regua.style.right = `${direita + this.ALCA_PX + 4}px`;
    regua.classList.add("is-visible");


    pilula.textContent = marcadorTempo(ts);
    pilula.style.top = `${topoAlca + this.ALCA_PX / 2}px`;
    pilula.style.right = `${direita + this.ALCA_PX + 8}px`;

    // Scroll sem gesto é o app se ajeitando (envio, abertura da conversa,
    // mensagem que chega) — e aí a linha do tempo não tem o que dizer. A
    // janela cobre a inércia, que continua rolando depois do dedo sair.
    const porGesto = performance.now() - this.ultimoGesto < 1500;
    if (!porGesto && !this.arrastandoAlca) return;

    pilula.classList.add("is-visible");
    alca.classList.add("is-visible");


    if (this.marcadorTimer !== null) window.clearTimeout(this.marcadorTimer);
    // Enquanto o dedo está na alça, nada some — sumir sob o dedo seria perder
    // justamente a referência que se está usando para navegar.
    if (this.arrastandoAlca) return;
    this.marcadorTimer = window.setTimeout(() => {
      pilula.classList.remove("is-visible");
      alca.classList.remove("is-visible");

      regua.classList.remove("is-visible");
      this.marcadorTimer = null;
    }, 900);
  }

  private async loadOlderMessages() {
    if (this.loadingOlder || !this.hasMoreOlder) return;
    if (!this.currentContact || this.messages.length === 0) return;
    this.loadingOlder = true;
    const contact = this.currentContact;
    const oldestTs = this.messages[0]!.ts;
    try {
      const older = await this.store.listMessagesPage(contact, {
        before: oldestTs,
        limit: this.pageLimit,
      });
      // O usuário pode ter trocado de conversa enquanto o IDB respondia.
      if (this.currentContact !== contact) return;
      if (older.length === 0) {
        this.hasMoreOlder = false;
        return;
      }
      this.hasMoreOlder = older.length === this.pageLimit;
      this.messages = older.concat(this.messages);
      this.renderMessages({ keepScrollFromBottom: true });
    } finally {
      this.loadingOlder = false;
    }
  }

  // ---------- Fila de saída (ações sem rede) ----------

  /**
   * Manda um frame; se a conexão estiver fora, guarda para quando voltar.
   *
   * Mensagem NÃO passa por aqui: ela já tem o caminho dela, gravada no IDB com
   * status "pending" e reenviada em `resendPendingOutgoing`. Dois mecanismos
   * para a mesma coisa seria a receita para uma regra valer num e não no
   * outro — foi assim que o servidor ficou com duas implementações de entrega.
   *
   * O que se perdia sem rede era o resto: reagir, editar, apagar e o visto
   * azul. Some sem aviso, e a pessoa acha que reagiu.
   *
   * A chave é `tipo:id` de propósito. Reagir duas vezes na mesma mensagem
   * enquanto o metrô não tem sinal deixa UM item na fila, com o último estado
   * — que é o que a pessoa quis dizer.
   */
  /** Marca o que é caixa fechada dentro do campo `text`.
   *
   *  O protocolo não tem campo próprio para isso, e inventar um exigiria mudar
   *  servidor e todos os clientes de uma vez. O prefixo cabe no que já existe e
   *  é reconhecível sem ambiguidade: texto normal não começa assim. */
  private static readonly MARCA_E2E = "\u0001E2E1:";

  /** Cifra o texto quando o destinatário tem chave publicada.
   *
   *  Quem ainda não publicou continua recebendo em claro — é o que permite
   *  ligar a criptografia sem partir as conversas de quem não migrou. Sem rede
   *  para buscar a chave, também sai em claro: preferir entregar a travar.
   *
   *  LIMITE DESTA PRIMEIRA FATIA: cifra para o PRIMEIRO aparelho do
   *  destinatário. Quem tem celular e navegador só lê no primeiro que publicou
   *  chave, e o eco para os próprios outros aparelhos não abre. Some quando a
   *  cifragem passar a ser feita uma vez por aparelho. */
  private async cifrarSePossivel(frame: WireOut): Promise<WireOut> {
    if (frame.type !== "send") return frame;
    const f = frame as WireSend;
    // Precisa aceitar mensagem SEM texto: anexo e áudio vão sozinhos, sem
    // legenda, e a guarda anterior ("!f.text") os deixava passar em claro —
    // o teste de anexo pegou o base64 do arquivo dentro do banco.
    const temConteudo = Boolean(f.text || f.audio?.data || f.attachment?.data);
    if (!temConteudo) return frame;
    if (f.text?.startsWith(ChatApp.MARCA_E2E)) return frame;
    try {
      // Os aparelhos do destinatário E os meus: sem os meus, eu não leria no
      // celular o que mandei do navegador. O eco multi-device já existia antes
      // da criptografia, e não pode ser quebrado por ela.
      const meuId = idDoAparelho();
      // Grupo: cifra para os aparelhos de CADA membro. Um membro sem chave não
      // impede os outros de receberem protegido — mas também não pode receber,
      // e é por isso que a interface precisa dizer quem ainda não migrou.
      const deles = f.to.startsWith("g:")
        ? await this.chavesDoGrupo(f.to)
        : await chavesDe(f.to.toLowerCase());
      if (!deles.length) return frame;
      const meus = (await chavesDe(this.sess.email)).filter((d) => d.device !== meuId);
      const par = await parDoAparelho();
      const destinos = [...deles, ...meus];
      const saida: WireSend = { ...f };

      if (f.text) {
        const env = await cifrarParaTodos(f.text, par.privada, meuId, destinos);
        if (env) saida.text = ChatApp.MARCA_E2E + JSON.stringify(env);
      }
      // Áudio e anexo carregam o conteúdo em base64 dentro do próprio frame —
      // sem cifrar isto, o servidor continua com o arquivo inteiro e a
      // proteção do texto seria só metade da promessa.
      if (f.audio?.data) {
        const e = await cifrarBase64ParaTodos(f.audio.data, par.privada, meuId, destinos);
        if (e) saida.audio = { ...f.audio, data: ChatApp.MARCA_E2E + JSON.stringify(e) };
      }
      if (f.attachment?.data) {
        const e = await cifrarBase64ParaTodos(f.attachment.data, par.privada, meuId, destinos);
        if (e) saida.attachment = { ...f.attachment, data: ChatApp.MARCA_E2E + JSON.stringify(e) };
      }
      return saida;
    } catch {
      // Falhar cifrando não pode impedir a mensagem de sair.
      return frame;
    }
  }

  /** Mostra o aviso de criptografia, e SÓ quando ela está mesmo valendo.
   *
   *  Depende de a chave deste aparelho ter sido publicada: sem isso as
   *  mensagens ainda saem em claro, e o aviso estaria afirmando uma proteção
   *  que não existe. Um banner que aparece antes da coisa funcionar é pior que
   *  banner nenhum — é o que faz alguém escrever no app o que não escreveria.
   *
   *  Fica escondido depois de fechado uma vez: aviso permanente vira paisagem
   *  e deixa de ser lido. */
  private mostrarAvisoE2E(chavePublicada: boolean): void {
    const el = document.getElementById("aviso-e2e");
    if (!el) return;
    if (!chavePublicada || localStorage.getItem("wuwei.aviso-e2e.fechado") === "1") return;
    el.classList.remove("hidden");
    document.getElementById("aviso-e2e-fechar")?.addEventListener(
      "click",
      () => {
        el.classList.add("hidden");
        localStorage.setItem("wuwei.aviso-e2e.fechado", "1");
      },
      { once: true },
    );
  }

  /** As chaves de todos os membros do grupo, menos as minhas.
   *
   *  As minhas entram depois, no caminho comum — juntar aqui as duplicaria e
   *  faria a mesma caixa ser gerada duas vezes. */
  private async chavesDoGrupo(gid: string): Promise<{ device: string; publicKey: string }[]> {
    const g = this.groups.get(gid);
    if (!g) return [];
    const outros = g.members.filter((m) => m.toLowerCase() !== this.sess.email);
    const listas = await Promise.all(outros.map((m) => chavesDe(m.toLowerCase())));
    return listas.flat();
  }

  /** Abre o que veio cifrado. Devolve um aviso legível quando não consegue —
   *  texto vazio pareceria mensagem em branco, e o usuário não saberia que
   *  existe conteúdo que este aparelho não alcança. */
  private async abrirCaixa(bruto: string, deQuem: string): Promise<string> {
    try {
      const env = JSON.parse(bruto.slice(ChatApp.MARCA_E2E.length)) as Envelope;
      if (!pareceEnvelope(env)) return bruto;
      const chaves = await chavesDe(deQuem);
      if (!chaves.length) return "🔒 mensagem cifrada — sem a chave de quem enviou";
      const par = await parDoAparelho();
      const claro = await abrirEnvelope(env, par.privada, idDoAparelho(), chaves);
      // Aparelho que entrou depois do envio não tem caixa endereçada a ele:
      // dizer isso é melhor que mostrar bolha vazia.
      return claro ?? "🔒 cifrada antes deste aparelho existir";
    } catch {
      return bruto;
    }
  }

  /** ÚNICA porta de saída de mensagem. Cifra e entrega.
   *
   *  Existe porque a suposição anterior — de que `enviarOuEnfileirar` já era o
   *  funil — estava errada: o envio de texto, o de áudio, o encaminhar e o
   *  reenvio da fila chamavam `conn.send` direto, cada um por seu caminho. A
   *  cifragem instalada só em um deles deixava os outros três em claro, e foi
   *  o teste de ponta a ponta que mostrou isso lendo o banco. */
  /** Abre anexo ou áudio cifrado. Devolve string vazia quando não consegue —
   *  base64 inválido quebraria o player e o download com erro obscuro. */
  private async abrirBinario(bruto: string, deQuem: string): Promise<string> {
    try {
      const env = JSON.parse(bruto.slice(ChatApp.MARCA_E2E.length)) as EnvelopeBinario;
      if (!pareceEnvelopeBinario(env)) return bruto;
      const chaves = await chavesDe(deQuem);
      if (!chaves.length) return "";
      const par = await parDoAparelho();
      return (await abrirEnvelopeBinario(env, par.privada, idDoAparelho(), chaves)) ?? "";
    } catch {
      return bruto;
    }
  }

  private async enviarSend(frame: WireSend): Promise<boolean> {
    const cifrado = (await this.cifrarSePossivel(frame)) as WireSend;
    return this.conn.send(cifrado);
  }

  private async enviarOuEnfileirar(frame: WireOut, chave: string): Promise<void> {
    // Antes do envio E antes da fila: mensagem que espera rede precisa já estar
    // cifrada, senão sairia em claro quando a conexão voltasse.
    frame = await this.cifrarSePossivel(frame);
    if (this.conn.send(frame)) return;
    await this.store.enqueue({ id: chave, ts: Date.now(), frame: frame as WireSend });
    this.pintarFilaPendente();
  }

  /** Esvazia a fila na ordem em que foi escrita. Um item só sai depois de
   *  entregue: se a conexão cair no meio, o resto continua lá. */
  private async drenarFila(): Promise<void> {
    const itens = await this.store.listOutbox();
    for (const item of itens) {
      if (!this.conn.send(item.frame)) break;
      await this.store.dequeue(item.id);
    }
    this.pintarFilaPendente();
  }

  /** Diz na tela quantas ações ainda não saíram. Silêncio aqui é o defeito:
   *  sem isso, "está tudo enviado" e "está tudo preso" têm a mesma cara. */
  private pintarFilaPendente(): void {
    void this.store.countOutbox().then((n) => {
      const el = document.getElementById("fila-pendente");
      if (!el) return;
      el.textContent = n ? `${n} ação${n > 1 ? "ões" : ""} esperando rede` : "";
      el.hidden = n === 0;
    });
  }

  // ---------- Reenvio de pendentes ----------

  /** Ao reconectar (após receber 'ready'), reenvia todas as mensagens out
   *  que ainda estão como 'pending' no IDB local. Idempotente: o destinatário
   *  dedupe por id. */
  private async resendPendingOutgoing() {
    const pendingList = await this.store.listPendingOutgoing();
    for (const msg of pendingList) {
      let audioWire;
      if (msg.audio) {
        audioWire = {
          mime: msg.audio.mime,
          data: await blobToBase64(msg.audio.blob),
          durationMs: msg.audio.durationMs,
          lang: msg.audio.lang,
        };
      }
      let attWire;
      if (msg.attachment) {
        attWire = {
          name: msg.attachment.name,
          mime: msg.attachment.mime,
          data: await blobToBase64(msg.attachment.blob),
        };
      }
      await this.enviarSend({
        type: "send",
        id: msg.id,
        to: msg.contact,
        text: msg.text,
        ...(audioWire ? { audio: audioWire } : {}),
        ...(attWire ? { attachment: attWire } : {}),
        ...(msg.transcribing ? { transcribing: true } : {}),
      });
    }
  }

  // ---------- Typing indicator ----------

  private onComposerTyping() {
    if (!this.currentContact) return;
    // Conversa com modelo local não emite "digitando…".
    //
    // Não é enfeite: o portão do spec 70 pegou este frame saindo. O texto já
    // não vazava, mas `{"type":"typing","to":"local:…"}` contava ao servidor
    // que esta conversa existe e a que horas alguém escreve nela. Prometer
    // conversa que não sai do aparelho e mandar o metadado dela é quase pior
    // que não prometer — ninguém veria, e a promessa seguiria escrita na tela.
    if (iaContato.ehContatoLocal(this.currentContact)) return;
    const now = Date.now();
    if (now - this.typingSentAt > 2000) {
      this.conn.send({ type: "typing", to: this.currentContact, on: true });
      this.typingSentAt = now;
    }
    if (this.typingStopHandle != null) clearTimeout(this.typingStopHandle);
    this.typingStopHandle = window.setTimeout(() => this.sendTypingStop(), 3000);
  }

  private sendTypingStop() {
    if (this.typingStopHandle != null) clearTimeout(this.typingStopHandle);
    this.typingStopHandle = null;
    if (
      this.typingSentAt > 0 &&
      this.currentContact &&
      !iaContato.ehContatoLocal(this.currentContact)
    ) {
      this.conn.send({ type: "typing", to: this.currentContact, on: false });
    }
    this.typingSentAt = 0;
  }

  // ---------- Notifications ----------

  private maybeNotify(from: string, text: string, hasAudio: boolean, hasAttach: boolean) {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    const body =
      text || (hasAudio ? "🎤 áudio" : hasAttach ? "📎 arquivo" : "nova mensagem");
    try {
      const n = new Notification("wuwei · " + from, {
        body: body.slice(0, 140),
        tag: from,
        icon: "/favicon.ico",
      } as NotificationOptions);
      n.onclick = () => {
        window.focus();
        void this.openContact(from);
        n.close();
      };
    } catch {
      /* alguns browsers proíbem em http inseguro — silencioso */
    }
  }

  // ---------- Web Push (VAPID) ----------

  /** Se permissão já está granted e há SW, garante que estamos subscribed. */
  private async ensurePushSubscribed() {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    if (!("serviceWorker" in navigator)) return;
    try {
      await this.subscribePush();
    } catch {
      /* silencioso — push é best-effort */
    }
  }

  private async subscribePush() {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const r = await fetch("/push/vapid-public");
      const { publicKey } = (await r.json()) as { publicKey: string };
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        // BufferSource — PushManager aceita Uint8Array em runtime, mas o
        // typing default é ArrayBuffer. Cast pra evitar ruído de SharedArrayBuffer.
        applicationServerKey: urlB64ToUint8Array(publicKey).buffer as ArrayBuffer,
      });
    }
    // Sempre re-envia ao server (idempotente — INSERT OR REPLACE)
    await fetch("/push/subscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        session: this.sess.session,
        subscription: sub.toJSON(),
      }),
    });
  }

  private async maybeAskNotificationPermission() {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "default") {
      this.updateNotifBtn();
      return;
    }
    this.updateNotifBtn();
  }

  private updateNotifBtn() {
    const btn = document.getElementById("notif-btn") as HTMLButtonElement | null;
    if (!btn) return;
    if (typeof Notification === "undefined" || Notification.permission !== "default") {
      btn.style.display = "none";
    } else {
      btn.style.display = "";
    }
  }

  // ---------- Edit / Delete UI ----------

  private async handleEditMessage(id: string) {
    const msg = this.messages.find((x) => x.id === id);
    if (!msg || msg.direction !== "out" || msg.deleted) return;
    const novo = window.prompt("Editar mensagem:", msg.text || "");
    if (novo === null) return;
    const text = novo.trim();
    if (!text || text === msg.text) return;
    const editedTs = Date.now();
    msg.text = text;
    msg.editedTs = editedTs;
    await this.store.editMessageText(id, text, editedTs);
    this.renderMessages();
    this.contacts = await this.store.listContacts();
    this.renderContacts();
    void this.enviarOuEnfileirar({ type: "edit", id, to: msg.contact, text }, `edit:${id}`);
  }

  private async handleDeleteMessage(id: string) {
    const msg = this.messages.find((x) => x.id === id);
    if (!msg || msg.direction !== "out" || msg.deleted) return;
    if (!window.confirm("Apagar esta mensagem para todos?")) return;
    const updated = await this.store.markDeleted(id);
    if (updated) {
      const i = this.messages.findIndex((x) => x.id === id);
      if (i >= 0) this.messages[i] = updated;
    }
    this.renderMessages();
    this.contacts = await this.store.listContacts();
    this.renderContacts();
    void this.enviarOuEnfileirar({ type: "delete", id, to: msg.contact }, `delete:${id}`);
  }

  // ---------- Read receipts ----------

  /** Envia "read" para todas as mensagens "in" desta conversa que ainda não
   *  foram marcadas. Chamado ao abrir conversa e ao receber nova mensagem. */
  private async flushReadReceipts() {
    if (!this.currentContact) return;
    const contact = this.currentContact;
    for (const m of this.messages) {
      if (m.direction === "in" && !m.readSent) {
        m.readSent = true;
        await this.store.markReadSent(m.id);
        this.conn.send({ type: "read", id: m.id, to: contact });
      }
    }
  }

  private applyStatus(id: string, status: MessageStatus, ts?: number) {
    const order: Record<MessageStatus, number> = {
      pending: 0,
      sent: 1,
      delivered: 2,
      read: 3,
    };
    const i = this.messages.findIndex((x) => x.id === id);
    if (i < 0) return;
    const cur = this.messages[i]!.status ?? "pending";
    if (order[status] > order[cur]) {
      this.messages[i]!.status = status;
      if (ts) this.messages[i]!.ts = ts;
      // Um tique que muda de ⏱ para ✓ não justifica reconstruir a lista
      // inteira. Cada envio dispara sent → delivered → read; com re-render
      // completo isso eram 3 rebuilds seguidos (a "piscada").
      if (!this.patchStatusInPlace(this.messages[i]!)) this.renderMessages();
    }
  }

  /** Troca só o tique/horário dentro da bolha, preservando todos os outros
   *  nós (e o scroll). Retorna false se a bolha não estiver no DOM — aí o
   *  chamador cai no re-render completo. */
  private patchStatusInPlace(msg: ChatMessage): boolean {
    const bubble = document.querySelector<HTMLElement>(
      `#messages .bubble[data-msg-id="${CSS.escape(msg.id)}"]`,
    );
    const receipt = bubble?.querySelector<HTMLElement>(".bubble-meta .receipt");
    if (!bubble || !receipt) return false;
    receipt.classList.toggle("is-read", msg.status === "read");
    receipt.innerHTML = renderTick(msg.status);
    return true;
  }

  private revokeAudioUrls() {
    for (const url of this.blobUrls.values()) URL.revokeObjectURL(url);
    this.blobUrls.clear();
  }

  private blobUrlFor(key: string, blob: Blob): string {
    let u = this.blobUrls.get(key);
    if (!u) {
      u = URL.createObjectURL(blob);
      this.blobUrls.set(key, u);
    }
    return u;
  }

  private renderBubble(
    msg: ChatMessage,
    opts?: { stackPos?: string },
  ): string {
    const dirCls = msg.direction === "out" ? "msg msg-out" : "msg msg-in";
    const tick = msg.direction === "out" ? renderTick(msg.status) : "";
    // `is-fresh` é o gatilho da animação de entrada. Só bolha inédita leva.
    const stackPos =
      (opts?.stackPos ? " " + opts.stackPos : "") +
      (this.freshBubbleIds.has(msg.id) ? " is-fresh" : "");

    if (msg.deleted) {
      return `
        <div class="bubble ${dirCls} bubble-deleted${stackPos}" data-msg-id="${escapeHtml(msg.id)}">
          <span class="bubble-text deleted-text">🚫 mensagem apagada</span>
        </div>`;
    }

    const trigger = `<button class="msg-trigger" data-msg-id="${escapeHtml(msg.id)}" data-direction="${msg.direction}" type="button" title="ações" aria-label="ações da mensagem">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
    </button>`;

    const senderBlock =
      msg.contact.startsWith("g:") && msg.direction === "in" && msg.fromEmail
        ? `<div class="bubble-sender">${escapeHtml(msg.fromEmail)}</div>`
        : "";

    const forwardedBlock = msg.forwardedFrom
      ? `<div class="forwarded-label bubble-forwarded">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 17 20 12 15 7"/><path d="M4 18v-2a4 4 0 014-4h12"/></svg>
          encaminhada${msg.forwardedFrom ? ` · <span class="mono">${escapeHtml(msg.forwardedFrom)}</span>` : ""}
         </div>`
      : "";

    const quoteBlock = msg.quoteOf ? this.renderQuoteBlock(msg.quoteOf) : "";
    const reactionsBlock = this.renderReactions(msg);
    const editedTag = msg.editedTs ? `<span class="edited edited-tag">· editada</span>` : "";
    // O horário saiu daqui: agora quem marca o tempo é o divisor de período
    // ("hoje à tarde"), e dentro da bolha ele competia com o texto. A hora
    // exata continua a um passe de mouse, no title.
    const metaInline = `<span class="bubble-meta" title="${escapeHtml(fmtTime(msg.ts))}">
      ${editedTag}
      ${msg.direction === "out" ? `<span class="receipt${msg.status === "read" ? " is-read" : ""}">${tick}</span>` : ""}
    </span>`;

    // ===== Variant: image (full-bleed) =====
    if (msg.attachment && msg.attachment.mime.startsWith("image/")) {
      return `
        <div class="bubble ${dirCls} is-media-image${stackPos}" data-msg-id="${escapeHtml(msg.id)}">
          ${trigger}
          ${senderBlock}
          ${forwardedBlock}
          ${quoteBlock}
          <img data-blob-id="${escapeHtml(msg.id)}" data-blob-kind="image" alt="${escapeHtml(msg.attachment.name)}" />
          ${
            msg.text
              ? `<span class="bubble-text bubble-caption">${this.renderCorpoTexto(msg.text)}</span>`
              : ""
          }
          ${metaInline}
          ${reactionsBlock}
        </div>`;
    }

    // ===== Variant: video =====
    // Antes o vídeo caía na variante "arquivo": virava um ícone para baixar,
    // sem poder assistir na conversa.
    if (msg.attachment && msg.attachment.mime.startsWith("video/")) {
      return `
        <div class="bubble ${dirCls} is-media-video${stackPos}" data-msg-id="${escapeHtml(msg.id)}">
          ${trigger}
          ${senderBlock}
          ${forwardedBlock}
          ${quoteBlock}
          <video data-blob-id="${escapeHtml(msg.id)}" data-blob-kind="video" controls preload="metadata" playsinline></video>
          ${
            msg.text
              ? `<span class="bubble-text bubble-caption">${this.renderCorpoTexto(msg.text)}</span>`
              : ""
          }
          ${metaInline}
          ${reactionsBlock}
        </div>`;
    }

    // ===== Variant: file =====
    if (msg.attachment) {
      const a = msg.attachment;
      const fileLbl = (a.name.split(".").pop() || "file").slice(0, 4).toUpperCase();
      return `
        <div class="bubble ${dirCls} is-media-file${stackPos}" data-msg-id="${escapeHtml(msg.id)}">
          ${trigger}
          ${senderBlock}
          ${forwardedBlock}
          ${quoteBlock}
          <a class="file-row attach-file" data-blob-id="${escapeHtml(msg.id)}" data-blob-kind="file" download="${escapeHtml(a.name)}">
            <span class="file-icon attach-file-icon">${escapeHtml(fileLbl)}</span>
            <span class="file-meta attach-file-meta">
              <span class="file-name attach-file-name">${escapeHtml(a.name)}</span>
              <span class="file-size attach-file-size">${formatBytes(a.blob.size)}</span>
            </span>
          </a>
          ${
            msg.text
              ? `<span class="bubble-text bubble-caption">${this.renderCorpoTexto(msg.text)}</span>`
              : ""
          }
          ${metaInline}
          ${reactionsBlock}
        </div>`;
    }

    // ===== Variant: audio =====
    if (msg.audio) {
      // Waveform "fake" — 17 barras com altura pseudo-randomizada e
      // primeiras N marcadas como played (visual de progresso).
      const N_BARS = 17;
      const playedCount = 0; // sem play em curso ainda; UI de progresso é cosmética
      const waveBars = Array.from({ length: N_BARS }, (_, i) => {
        const h = 30 + ((i * 23 + 7) % 65);
        const cls = i < playedCount ? "played" : "";
        return `<span class="${cls}" style="height:${h}%"></span>`;
      }).join("");
      const lang = msg.audio.lang ? shortLang(msg.audio.lang) : "";
      const transcriptId = `t-${msg.id}`;
      // Do lado de quem RECEBE, o fim do "transcrevendo…" depende de o outro
      // aparelho mandar o `transcript`. Se ele fechou o app antes, esse aviso
      // nunca sairia. Depois de um minuto tratamos como não transcrito — o
      // texto ainda chega e substitui isto se aparecer depois.
      const transcrevendo = !!msg.transcribing && Date.now() - msg.ts < 200_000;
      const transcriptInner = transcrevendo
        ? `<span class="trans-dots"><span></span><span></span><span></span></span>transcrevendo…`
        : msg.text
          ? escapeHtml(msg.text)
          : "";
      // Estado padrão: aberto. Colapsável pelo botão.
      const transcriptBlock =
        transcriptInner || transcrevendo
          ? `<div class="audio-transcript-wrap is-open">
              <button class="audio-transcript-toggle" type="button" aria-expanded="true" aria-controls="${transcriptId}">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                <span>transcrição${lang ? ` · ${lang}` : ""}</span>
              </button>
              <div class="audio-transcript${transcrevendo ? " transcribing" : ""}" id="${transcriptId}">${transcriptInner}</div>
            </div>`
          : "";
      return `
        <div class="bubble ${dirCls} is-media-audio${stackPos}" data-msg-id="${escapeHtml(msg.id)}">
          ${trigger}
          ${senderBlock}
          ${forwardedBlock}
          ${quoteBlock}
          <div class="audio-row">
            <button class="audio-play" type="button" aria-label="tocar áudio">
              <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><path d="M5 3l14 9-14 9V3z"/></svg>
            </button>
            <audio data-blob-id="${escapeHtml(msg.id)}" data-blob-kind="audio" preload="metadata" style="display:none"></audio>
            <div class="audio-wave" aria-hidden="true">${waveBars}</div>
            <div class="audio-meta">
              <button class="audio-speed" type="button" data-speed="1">1×</button>
              <span>${formatDuration(msg.audio.durationMs)}</span>
            </div>
          </div>
          ${transcriptBlock}
          ${metaInline}
          ${reactionsBlock}
        </div>`;
    }

    // ===== Variant: text (default) =====
    // Texto longo (>500 chars ou >8 quebras de linha) começa colapsado
    // com botão "ler mais" — evita o bubble cobrir a tela inteira.
    // Corte em duas partes no próprio markup, em vez de -webkit-line-clamp.
    // O clamp é um bloco: nada consegue ocupar a última linha junto do texto,
    // então o "ler mais" era empurrado para uma linha só dele. Cortando aqui,
    // o botão vira parte natural do fluxo e encaixa no fim da frase — como no
    // WhatsApp.
    const textCls = "bubble-text";
    // Mesmo caminho da legenda de imagem/vídeo: corte de "ler mais" e links
    // clicáveis. Antes esta variante tinha código próprio, e por isso os links
    // só viravam link nas legendas — nunca na mensagem de texto comum.
    const corpo = this.renderCorpoTexto(msg.text);
    const expandBtn = "";
    return `
      <div class="bubble ${dirCls}${stackPos}" data-msg-id="${escapeHtml(msg.id)}">
        ${trigger}
        ${senderBlock}
        ${forwardedBlock}
        ${quoteBlock}
        <span class="${textCls}">${corpo}${expandBtn}</span>
        ${metaInline}
        ${reactionsBlock}
      </div>`;
  }

  /** Transforma URLs em links clicáveis.
   *
   *  Recebe texto JÁ ESCAPADO e opera sobre ele — nunca sobre o texto cru.
   *  Assim o que o usuário escreveu não vira markup: um "<script>" na
   *  mensagem já chegou aqui como texto inofensivo. A pontuação final fica de
   *  fora do link, senão um ponto de fim de frase entraria na URL. */
  private linkificar(escapado: string): string {
    return escapado.replace(
      /\bhttps?:\/\/[^\s<]+/g,
      (bruto) => {
        const m = bruto.match(/[.,;:!?)\]}'"]+$/);
        const cauda = m ? m[0] : "";
        const url = cauda ? bruto.slice(0, -cauda.length) : bruto;
        if (!url) return bruto;
        return (
          `<a class="msg-link" href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>` +
          cauda
        );
      },
    );
  }

  /** Formata Markdown simples: negrito, itálico, código, listas, títulos e
   *  citações.
   *
   *  SEGURANÇA: recebe texto JÁ ESCAPADO e só então cria tags. Nunca o
   *  contrário — senão uma mensagem com HTML dentro viraria markup executável
   *  na conversa de quem recebe. As tags produzidas aqui são um conjunto
   *  fechado; nada do que o remetente escreveu vira tag.
   *
   *  Deliberadamente incompleto: sem tabelas, sem imagens embutidas, sem HTML
   *  cru. É mensagem de chat, não documento. */
  /** Destaca @menções no texto já escapado. */
  private marcarMencoes(escapado: string): string {
    return escapado.replace(
      /(^|\s)@([\w.-]+@[\w.-]+\.\w+)/g,
      (_m, antes, alvo) =>
        `${antes}<span class="mention${alvo.toLowerCase() === this.sess.email ? " is-me" : ""}" data-mention="${escapeHtml(alvo.toLowerCase())}" role="link" tabindex="0">@${this.displayName(alvo)}</span>`,
    );
  }

  private formatarMarkdown(escapado: string): string {
    // 1) Blocos de código saem de cena primeiro, protegidos por marcador, para
    //    que o que estiver dentro deles não seja interpretado como formatação.
    const blocos: string[] = [];
    let txt = escapado.replace(/```(\w*)\n?([\s\S]*?)```/g, (_m, _lang, corpo) => {
      blocos.push(`<pre class="md-pre"><code>${corpo.replace(/\n$/, "")}</code></pre>`);
      return `\u0001B${blocos.length - 1}\u0001`;
    });

    const inline = (s: string) =>
      s
        // código inline antes do resto, pelo mesmo motivo dos blocos
        .replace(/`([^`\n]+)`/g, '<code class="md-code">$1</code>')
        .replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>")
        .replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s).,!?]|$)/g, "$1<em>$2</em>")
        .replace(/(^|[\s(])_([^_\n]+)_(?=[\s).,!?]|$)/g, "$1<em>$2</em>")
        .replace(/~~([^~\n]+)~~/g, "<del>$1</del>");

    // Normaliza antes de interpretar:
    //  · "##" sozinho numa linha, com o título na seguinte — acontece quando o
    //    texto é quebrado no meio; sem isso o "##" aparecia cru na tela
    //  · sequências de linhas em branco viram UMA, senão a bolha fica cheia de
    //    buracos (foi o "espaço duplo" que apareceu na conversa)
    txt = txt
      .replace(/^(#{1,4})[ \t]*\n+[ \t]*(?=\S)/gm, "$1 ")
      .replace(/\n{3,}/g, "\n\n");
    const linhas = txt.split("\n");
    const saida: string[] = [];
    let lista: "ul" | "ol" | null = null;
    const fecharLista = () => {
      if (lista) {
        saida.push(`</${lista}>`);
        lista = null;
      }
    };

    for (let i = 0; i < linhas.length; i++) {
      const linha = linhas[i]!;

      // Tabela: cabeçalho + linha de traços + corpo. Precisa vir antes das
      // outras regras, porque suas linhas contêm símbolos que elas pegariam.
      const abaixo = linhas[i + 1] ?? "";
      if (linha.includes("|") && abaixo.includes("|") && /^[\s|:-]+$/.test(abaixo) && abaixo.includes("-")) {
        fecharLista();
        const celulas = (l: string) =>
          l.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map((c) => c.trim());
        const th = celulas(linha).map((c) => `<th>${inline(c)}</th>`).join("");
        const corpo: string[] = [];
        let j = i + 2;
        while (j < linhas.length && linhas[j]!.includes("|") && linhas[j]!.trim() !== "") {
          corpo.push(`<tr>${celulas(linhas[j]!).map((c) => `<td>${inline(c)}</td>`).join("")}</tr>`);
          j++;
        }
        // O invólucro rola: tabela larga desliza dentro da bolha em vez de
        // esticá-la e empurrar a conversa.
        saida.push(
          `<div class="md-tablewrap"><table class="md-table">` +
            `<thead><tr>${th}</tr></thead><tbody>${corpo.join("")}</tbody></table></div>`,
        );
        i = j - 1;
        continue;
      }

      if (/^\u0001B\d+\u0001$/.test(linha.trim())) {
        fecharLista();
        saida.push(linha.trim());
        continue;
      }
      const titulo = linha.match(/^(#{1,4})\s+(.*)$/);
      if (titulo) {
        fecharLista();
        // Título vira texto forte, não <h1>: numa bolha de chat um h1 quebraria
        // a escala tipográfica do app inteiro.
        saida.push(`<div class="md-h">${inline(titulo[2]!)}</div>`);
        continue;
      }
      const cit = linha.match(/^>\s?(.*)$/);
      if (cit) {
        fecharLista();
        saida.push(`<div class="md-quote">${inline(cit[1]!)}</div>`);
        continue;
      }
      const li = linha.match(/^\s*[-*+]\s+(.*)$/);
      if (li) {
        if (lista !== "ul") {
          fecharLista();
          saida.push('<ul class="md-list">');
          lista = "ul";
        }
        saida.push(`<li>${inline(li[1]!)}</li>`);
        continue;
      }
      const oli = linha.match(/^\s*\d+[.)]\s+(.*)$/);
      if (oli) {
        if (lista !== "ol") {
          fecharLista();
          saida.push('<ol class="md-list">');
          lista = "ol";
        }
        saida.push(`<li>${inline(oli[1]!)}</li>`);
        continue;
      }
      // Linha em branco NÃO fecha a lista, e é por isso que ela vem antes do
      // `fecharLista()`. Quem escreve "1. …", pula uma linha e escreve "2. …"
      // está fazendo uma lista de dois itens — e o parser fazia duas listas de
      // um item, cada uma com sua margem, o que abria um buraco no meio da
      // mensagem e reiniciava a numeração. A lista só fecha quando aparece
      // conteúdo que não é item.
      //
      // A linha em branco também não vira elemento: o espaçamento fica por
      // conta da margem dos blocos, que é previsível. Emitir <div> vazio
      // empilhava altura de linha inteira a cada quebra.
      if (linha.trim() === "") continue;
      fecharLista();
      if (/^\s*([-*_]\s*){3,}$/.test(linha)) {
        saida.push('<hr class="md-hr" />');
        continue;
      }
      saida.push(`<div class="md-p">${inline(linha)}</div>`);
    }
    fecharLista();

    // Junta SEM "\n". A bolha usa `white-space: pre-wrap` para preservar as
    // quebras de quem escreve, e sob essa regra a quebra de linha que eu
    // colocava só para deixar o HTML legível virava uma linha em branco DE
    // VERDADE entre cada bloco — o vão que aparecia entre os itens de uma
    // lista e entre parágrafos. O espaçamento é responsabilidade do CSS.
    return saida
      .join("")
      .replace(/\u0001B(\d+)\u0001/g, (_m, i) => blocos[Number(i)] ?? "");
  }

  /** Corpo de texto já com o corte de "ler mais" aplicado quando é longo.
   *  Usado tanto na bolha de texto puro quanto na legenda de imagem/arquivo. */
  private renderCorpoTexto(texto: string): string {
    const longo = texto.length > 500 || (texto.match(/\n/g)?.length ?? 0) > 8;
    if (!longo) return this.formatarMarkdown(this.marcarMencoes(this.linkificar(escapeHtml(texto))));
    const corte = this.pontoDeCorte(texto);
    return (
      `<span class="bt-head">${this.formatarMarkdown(this.linkificar(escapeHtml(texto.slice(0, corte).trimEnd())))}</span>` +
      `<span class="bt-rest" hidden>${this.formatarMarkdown(this.linkificar(escapeHtml(texto.slice(corte))))}</span>` +
      `<button type="button" class="bubble-expand" aria-expanded="false">… ler mais</button>`
    );
  }

  /** Onde cortar o texto longo: o que vier primeiro entre ~500 chars e 8
   *  linhas, recuando até o último espaço para não partir palavra ao meio. */
  private pontoDeCorte(texto: string): number {
    let corte = Math.min(texto.length, 500);
    let linhas = 0;
    for (let i = 0; i < texto.length; i++) {
      if (texto[i] === "\n" && ++linhas === 8) {
        corte = Math.min(corte, i);
        break;
      }
    }
    const espaco = texto.lastIndexOf(" ", corte);
    return espaco > corte - 40 && espaco > 0 ? espaco : corte;
  }

  private renderQuoteBlock(q: QuotedRef): string {
    const text = q.snippet || "";
    const isMedia = !text && !!q.hasMedia;
    const display = isMedia ? "🎤 mídia" : text || "(sem texto)";
    return `
      <button class="bubble-quote quote-block" data-quote-target="${escapeHtml(q.id)}" type="button">
        <span class="bubble-quote-name quote-block-from">${escapeHtml(q.from)}</span>
        <span class="bubble-quote-text quote-block-text${isMedia ? " is-media" : ""}">${escapeHtml(display)}</span>
      </button>`;
  }

  private renderReactions(msg: ChatMessage): string {
    const r = msg.reactions;
    if (!r || Object.keys(r).length === 0) return "";
    const me = this.sess.email;
    const pills = Object.entries(r)
      .map(([emoji, actors]) => {
        const isMine = actors.includes(me);
        const title = actors.join(", ");
        return `<button class="tapback reaction-pill${isMine ? " is-mine" : ""}"
                  data-msg-id="${escapeHtml(msg.id)}"
                  data-emoji="${escapeHtml(emoji)}"
                  type="button"
                  title="${escapeHtml(title)}">
          <span class="reaction-emoji">${escapeHtml(emoji)}</span>
          <span class="reaction-count">${actors.length}</span>
        </button>`;
      })
      .join("");
    return `<div class="tapbacks bubble-reactions">${pills}</div>`;
  }

  private renderEmpty() {
    this.showDashboardView();
  }

  private showDashboardView() {
    document.getElementById("agents-view")?.classList.add("hidden");
    document.getElementById("invite-view")?.classList.add("hidden");
    document.getElementById("group-view")?.classList.add("hidden");
    delete document.getElementById("chat")?.dataset.fullPage;
    const dash = document.getElementById("dashboard-view");
    const conv = document.getElementById("conv-view");
    const chat = document.getElementById("chat");
    dash?.classList.remove("hidden");
    conv?.classList.add("hidden");
    if (chat) delete chat.dataset.convOpen;
    this.currentContact = null;
    void this.syncUrl(null);
    this.renderDashboard();
  }

  private showConversationView() {
    document.getElementById("agents-view")?.classList.add("hidden");
    document.getElementById("invite-view")?.classList.add("hidden");
    document.getElementById("group-view")?.classList.add("hidden");
    delete document.getElementById("chat")?.dataset.fullPage;
    const dash = document.getElementById("dashboard-view");
    const conv = document.getElementById("conv-view");
    const chat = document.getElementById("chat");
    dash?.classList.add("hidden");
    conv?.classList.remove("hidden");
    if (chat) chat.dataset.convOpen = "true";
  }

  private renderDashboard() {
    // Greeting personalizado por hora
    const titleEl = document.getElementById("dash-greeting-title");
    const subEl = document.getElementById("dash-greeting-sub");
    if (titleEl) {
      const hour = new Date().getHours();
      const greeting = hour < 6 ? "Boa madrugada" : hour < 12 ? "Bom dia" : hour < 18 ? "Boa tarde" : "Boa noite";
      const localPart = this.sess.email.split("@")[0] ?? this.sess.email;
      const name = localPart.charAt(0).toUpperCase() + localPart.slice(1);
      titleEl.textContent = `${greeting}, ${name} 👋`;
    }
    if (subEl) {
      const d = new Date();
      const wk = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
      const months = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
      const hh = d.getHours().toString().padStart(2, "0");
      const mm = d.getMinutes().toString().padStart(2, "0");
      const totalUnread = this.contacts.reduce((s, c) => s + (c.unread || 0), 0);
      const tail =
        totalUnread === 0
          ? "tudo em dia"
          : totalUnread === 1
            ? "1 conversa pede atenção"
            : `${totalUnread} conversas pedem atenção`;
      subEl.textContent = `${wk[d.getDay()]}, ${d.getDate()} de ${months[d.getMonth()]} · ${hh}:${mm} · ${tail}`;
    }
    // Highlights: até 3 contatos com não-lidas mais recentes
    const hlEl = document.getElementById("dash-highlights");
    if (hlEl) {
      const highlights = this.contacts
        .filter((c) => (c.unread || 0) > 0)
        .sort((a, b) => b.lastTs - a.lastTs)
        .slice(0, 3);
      if (highlights.length === 0) {
        hlEl.innerHTML = `<div class="empty-hint">Inbox limpa. Hora de respirar 🌿</div>`;
      } else {
        hlEl.innerHTML = highlights
          .map((c) => {
            const isGroup = c.email.startsWith("g:");
            const display = this.displayName(c.email);
            const initials = isGroup ? "##" : avatarInitials(c.email);
            const bg = isGroup ? "linear-gradient(135deg, var(--accent), var(--info))" : colorFor(c.email);
            const ago = this.timeAgo(c.lastTs);
            return `
              <div class="highlight-card" data-email="${escapeHtml(c.email)}">
                <span class="hl-avatar" style="background:${bg}">${escapeHtml(initials)}</span>
                <div class="hl-body">
                  <div class="hl-head">
                    <span class="hl-name">${escapeHtml(display)}</span>
                    <span class="hl-time">${escapeHtml(ago)}</span>
                    <span class="hl-tag is-warm">aguardando você</span>
                  </div>
                  <div class="hl-text"><q>${escapeHtml((c.lastText || "").slice(0, 200))}</q></div>
                </div>
                <div class="hl-actions">
                  <button class="hl-act hl-act-primary" type="button" data-action="reply">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 00-4-4H4"/></svg>
                    responder
                  </button>
                </div>
              </div>`;
          })
          .join("");
        hlEl.querySelectorAll<HTMLElement>(".highlight-card").forEach((card) => {
          card.onclick = () => {
            const em = card.dataset.email!;
            void this.openContact(em);
          };
        });
      }
    }
    // Stats: contagem real desde 00:00 de hoje
    void this.refreshStats();
  }

  private async refreshStats() {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    try {
      const c = await this.store.countSince(todayStart.getTime());
      const set = (id: string, v: number) => {
        const el = document.getElementById(id);
        if (el) el.textContent = String(v);
      };
      set("stat-received", c.received);
      set("stat-sent", c.sent);
      set("stat-audio", c.audios);
      set("stat-media", c.media);
    } catch {
      /* silencioso — é só decoração */
    }
  }

  private timeAgo(ts: number): string {
    if (!ts) return "";
    const diff = Date.now() - ts;
    const m = Math.floor(diff / 60000);
    if (m < 1) return "agora";
    if (m < 60) return `há ${m}min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `há ${h}h`;
    return fmtTime(ts);
  }

  private renderMessages(opts?: { keepScrollFromBottom?: boolean }) {
    if (!this.currentContact) {
      this.renderEmpty();
      return;
    }
    this.showConversationView();
    this.foraDaConversa = false;
    const m = $("messages");
    // Perto do fim = está acompanhando ao vivo e espera que a conversa ande
    // sozinha. Longe = está LENDO, e mover a tela é tirar a página da mão.
    // Medido antes de reescrever a lista, senão scrollHeight já mudou.
    const estavaNoFim = m.scrollHeight - m.scrollTop - m.clientHeight < 80;
    const head = $("chat-header-name");
    // Só reescreve se mudou de verdade: `renderMessages` roda a cada mensagem
    // nova e a cada recibo, e regravar o texto zera a rolagem horizontal do
    // nome — era isso que jogava o nome de volta ao início, não o gesto.
    const nomeAtual = this.displayName(this.currentContact);
    if (head.textContent !== nomeAtual) head.textContent = nomeAtual;
    // Em grupo, o nome é o caminho para a edição — como no WhatsApp.
    if (this.currentContact.startsWith("g:")) {
      head.style.cursor = "pointer";
      head.onclick = () => this.abrirEditarGrupo(this.currentContact!);
    } else {
      head.style.cursor = "";
      head.onclick = null;
    }
    this.instalarPeek(head);
    // Sem selo de agente: o endereço já diz o que é, e o rótulo só disputava
    // espaço com o nome no cabeçalho.
    head.parentElement?.querySelectorAll(".agent-badge").forEach((b) => b.remove());
    this.pintarCabecalhoConversa();

    const offsetFromBottom = opts?.keepScrollFromBottom
      ? m.scrollHeight - m.scrollTop
      : null;

    // Trocou de conversa → o histórico que aparece não é "novidade": marca
    // tudo como já visto para nada animar na abertura.
    if (this.seenBubbleContact !== this.currentContact) {
      this.seenBubbleContact = this.currentContact;
      this.seenBubbleIds = new Set(this.messages.map((x) => x.id));
      this.freshBubbleIds = new Set();
    }

    if (this.messages.length === 0) {
      m.innerHTML = `
        <div class="empty-state">
          <h2>${escapeHtml(this.displayName(this.currentContact))}</h2>
          <p>Sem mensagens ainda. Diga oi.</p>
        </div>`;
    } else {
      const sorted = this.messages.slice().sort((a, b) => a.ts - b.ts);

      // Bolhas inéditas ganham `is-fresh` (única classe que dispara
      // `bubble-in`). Carregar histórico antigo (keepScrollFromBottom) conta
      // como visto: não é mensagem chegando.
      this.freshBubbleIds = new Set();
      for (const x of sorted) {
        if (this.seenBubbleIds.has(x.id)) continue;
        this.seenBubbleIds.add(x.id);
        if (!opts?.keepScrollFromBottom) this.freshBubbleIds.add(x.id);
      }

      const STACK_GAP_MS = 5 * 60 * 1000;
      const sameAuthor = (a: ChatMessage, b: ChatMessage): boolean => {
        if (a.direction !== b.direction) return false;
        if (a.direction === "out") return true;
        return (a.fromEmail || a.contact) === (b.fromEmail || b.contact);
      };
      // Agrupa em stacks (mesma direção + mesmo autor + mesmo dia + < 5min de gap)
      type Stack = { dir: "in" | "out"; author: string; messages: ChatMessage[] };
      const stacks: { day: string; stack: Stack }[] = [];
      for (const msg of sorted) {
        const day = chavePeriodo(msg.ts);
        const author = msg.direction === "out" ? "__me__" : (msg.fromEmail || msg.contact);
        const prev = stacks[stacks.length - 1];
        const prevMsg = prev?.stack.messages[prev.stack.messages.length - 1];
        if (
          prev &&
          prev.day === day &&
          prev.stack.dir === msg.direction &&
          prev.stack.author === author &&
          prevMsg &&
          msg.ts - prevMsg.ts < STACK_GAP_MS
        ) {
          prev.stack.messages.push(msg);
        } else {
          stacks.push({ day, stack: { dir: msg.direction, author, messages: [msg] } });
        }
      }
      // Renderiza com day-dividers entre dias
      const html: string[] = [];
      let prevDay = "";
      for (const { day, stack } of stacks) {
        if (day !== prevDay) {
          html.push(this.renderDayDivider(stack.messages[0]!.ts));
          prevDay = day;
        }
        html.push(this.renderStack(stack));
      }
      m.innerHTML = html.join("");

      // Atribui src dos <audio>/<img> a partir dos blobs
      m.querySelectorAll<HTMLElement>("[data-blob-id]").forEach((el) => {
        const id = el.dataset.blobId!;
        const kind = el.dataset.blobKind!;
        const msg = sorted.find((x) => x.id === id);
        if (!msg) return;
        if (kind === "audio" && msg.audio) {
          (el as HTMLAudioElement).src = this.blobUrlFor("a:" + id, msg.audio.blob);
        } else if (kind === "image" && msg.attachment) {
          (el as HTMLImageElement).src = this.blobUrlFor("f:" + id, msg.attachment.blob);
          (el as HTMLImageElement).style.cursor = "zoom-in";
          (el as HTMLImageElement).onclick = (ev) => {
            ev.stopPropagation();
            this.openImageViewer(id);
          };
        } else if (kind === "video" && msg.attachment) {
          (el as HTMLVideoElement).src = this.blobUrlFor("f:" + id, msg.attachment.blob);
        } else if (kind === "file" && msg.attachment) {
          (el as HTMLAnchorElement).href = this.blobUrlFor("f:" + id, msg.attachment.blob);
        }
      });

      m.querySelectorAll<HTMLButtonElement>(".msg-trigger").forEach((btn) => {
        btn.onclick = (ev) => {
          ev.stopPropagation();
          const id = btn.dataset.msgId!;
          const direction = (btn.dataset.direction || "in") as "in" | "out";
          this.openMessageMenu(btn, id, direction);
        };
      });

      // Tocar numa menção abre a conversa com aquela pessoa.
      m.querySelectorAll<HTMLElement>(".mention[data-mention]").forEach((el) => {
        el.onclick = (ev) => {
          ev.stopPropagation();
          const alvo = el.dataset.mention!;
          if (alvo === this.sess.email) return;   // citar a si mesmo não navega
          void this.openContact(alvo);
        };
      });

      // Segurar a mensagem abre as reações — o gesto que se espera no celular.
      // No desktop o equivalente é o botão direito; lá o botão de ações no
      // canto da bolha continua sendo o caminho principal.
      m.querySelectorAll<HTMLElement>(".bubble").forEach((bubble) => {
        const id = bubble.dataset.msgId;
        if (!id) return;
        let timer: number | undefined;
        let moveu = false;
        const cancelar = () => {
          if (timer !== undefined) {
            clearTimeout(timer);
            timer = undefined;
          }
        };
        bubble.addEventListener(
          "touchstart",
          () => {
            moveu = false;
            cancelar();
            timer = window.setTimeout(() => {
              if (moveu) return;
              // Vibração curta confirma o gesto, como no WhatsApp.
              navigator.vibrate?.(15);
              const dir = bubble.classList.contains("msg-out") ? "out" : "in";
              this.openSelectionBar(id, dir);
              this.openReactPicker(id, bubble);
            }, 450);
          },
          { passive: true },
        );
        // Rolar a conversa não pode virar long-press.
        bubble.addEventListener("touchmove", () => {
          moveu = true;
          cancelar();
        }, { passive: true });
        bubble.addEventListener("touchend", cancelar);
        bubble.addEventListener("touchcancel", cancelar);
        // Com o modo seleção ligado, tocar em outra mensagem marca/desmarca —
        // é assim que se seleciona várias.
        bubble.addEventListener("click", (ev) => {
          // Link é para navegar, não para selecionar.
          if ((ev.target as HTMLElement)?.closest("a.msg-link")) return;
          if (this.selecionadas.size === 0) return;
          ev.stopPropagation();
          ev.preventDefault();
          this.alternarSelecao(id);
        });
        bubble.addEventListener("contextmenu", (ev) => {
          ev.preventDefault();
          const dir = bubble.classList.contains("msg-out") ? "out" : "in";
          this.openSelectionBar(id, dir);
          this.openReactPicker(id, bubble);
        });
      });

      m.querySelectorAll<HTMLButtonElement>(".reaction-pill, .tapback").forEach((btn) => {
        btn.onclick = (ev) => {
          ev.stopPropagation();
          const el = btn as HTMLButtonElement;
          const id = el.dataset.msgId!;
          const emoji = el.dataset.emoji!;
          if (id && emoji) void this.toggleReaction(id, emoji);
        };
      });

      m.querySelectorAll<HTMLButtonElement>(".quote-block, .bubble-quote").forEach((btn) => {
        btn.onclick = (ev) => {
          ev.stopPropagation();
          const target = (btn as HTMLButtonElement).dataset.quoteTarget;
          if (target) void this.jumpToMessage(target);
        };
      });

      // Player de áudio: play/pause, progresso visual, seek na waveform.
      // Os listeners do <audio> e da .audio-wave ficam juntos por bubble pra
      // compartilhar o mesmo escopo (sem refs em DOM-level state).
      const PLAY_PATH = "M5 3l14 9-14 9V3z";
      const PAUSE_PATH = "M6 4h3.5v16H6zM14.5 4H18v16h-3.5z";
      m.querySelectorAll<HTMLAudioElement>('audio[data-blob-kind="audio"]').forEach(
        (audioEl) => {
          const bubble = audioEl.closest<HTMLElement>(".bubble");
          if (!bubble) return;
          const wave = bubble.querySelector<HTMLElement>(".audio-wave");
          const bars = wave
            ? Array.from(wave.querySelectorAll<HTMLElement>("span"))
            : [];
          const playBtn = bubble.querySelector<HTMLButtonElement>(".audio-play");
          const playPath = playBtn?.querySelector<SVGPathElement>("svg path") ?? null;
          const timeLabel = bubble.querySelector<HTMLElement>(".audio-meta span");
          const totalMs = (() => {
            const id = audioEl.dataset.blobId || "";
            const msg = sorted.find((x) => x.id === id);
            return msg?.audio?.durationMs ?? 0;
          })();

          const renderProgress = () => {
            const dur =
              isFinite(audioEl.duration) && audioEl.duration > 0
                ? audioEl.duration
                : totalMs / 1000;
            if (!dur) return;
            const pct = Math.max(0, Math.min(1, audioEl.currentTime / dur));
            const playedTo = Math.round(pct * bars.length);
            for (let i = 0; i < bars.length; i++) {
              bars[i].classList.toggle("played", i < playedTo);
            }
            if (timeLabel) {
              const showRemaining = !audioEl.paused || audioEl.currentTime > 0.05;
              const ms = showRemaining
                ? Math.max(0, (dur - audioEl.currentTime) * 1000)
                : dur * 1000;
              timeLabel.textContent = formatDuration(ms);
            }
          };
          const setIcon = (playing: boolean) => {
            if (playPath) playPath.setAttribute("d", playing ? PAUSE_PATH : PLAY_PATH);
            playBtn?.setAttribute(
              "aria-label",
              playing ? "pausar áudio" : "tocar áudio",
            );
          };

          audioEl.addEventListener("loadedmetadata", renderProgress);
          audioEl.addEventListener("timeupdate", renderProgress);
          audioEl.addEventListener("play", () => setIcon(true));
          audioEl.addEventListener("pause", () => setIcon(false));
          audioEl.addEventListener("ended", () => {
            setIcon(false);
            // Whatsapp-style: ao terminar, volta cursor pra 0 visualmente
            audioEl.currentTime = 0;
            renderProgress();
          });
          renderProgress();

          if (playBtn) {
            playBtn.onclick = (ev) => {
              ev.stopPropagation();
              if (audioEl.paused) void audioEl.play();
              else audioEl.pause();
            };
          }

          if (wave) {
            const seekFromX = (clientX: number) => {
              const rect = wave.getBoundingClientRect();
              if (rect.width <= 0) return;
              const pct = Math.max(
                0,
                Math.min(1, (clientX - rect.left) / rect.width),
              );
              const dur =
                isFinite(audioEl.duration) && audioEl.duration > 0
                  ? audioEl.duration
                  : totalMs / 1000;
              if (!dur) return;
              audioEl.currentTime = pct * dur;
              renderProgress();
            };
            let dragging = false;
            wave.addEventListener("pointerdown", (ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              dragging = true;
              try {
                wave.setPointerCapture(ev.pointerId);
              } catch {
                /* not supported */
              }
              seekFromX(ev.clientX);
            });
            wave.addEventListener("pointermove", (ev) => {
              if (!dragging) return;
              ev.preventDefault();
              seekFromX(ev.clientX);
            });
            const endDrag = (ev: PointerEvent) => {
              if (!dragging) return;
              dragging = false;
              try {
                wave.releasePointerCapture(ev.pointerId);
              } catch {
                /* */
              }
            };
            wave.addEventListener("pointerup", endDrag);
            wave.addEventListener("pointercancel", endDrag);
          }
        },
      );

      // "ler mais" / "mostrar menos" em mensagens texto longas
      m.querySelectorAll<HTMLButtonElement>(".bubble-expand").forEach((btn) => {
        btn.onclick = (ev) => {
          ev.stopPropagation();
          const bubble = btn.closest(".bubble");
          // A truncagem agora vive no markup (bt-head + bt-rest oculto), não
          // mais na classe is-clipped do CSS.
          const rest = bubble?.querySelector<HTMLElement>(".bt-rest");
          if (!rest) return;
          const expanded = btn.getAttribute("aria-expanded") === "true";
          btn.setAttribute("aria-expanded", String(!expanded));
          rest.hidden = expanded;
          btn.textContent = expanded ? "… ler mais" : " ler menos";
        };
      });

      // toggle de transcrição (aberto por default; colapsável)
      m.querySelectorAll<HTMLButtonElement>(".audio-transcript-toggle").forEach((btn) => {
        btn.onclick = (ev) => {
          ev.stopPropagation();
          const wrap = btn.closest(".audio-transcript-wrap");
          if (!wrap) return;
          const open = btn.getAttribute("aria-expanded") === "true";
          btn.setAttribute("aria-expanded", String(!open));
          wrap.classList.toggle("is-open", !open);
        };
      });

      // ciclo de velocidade do áudio (1× → 1.5× → 2× → 1×)
      m.querySelectorAll<HTMLButtonElement>(".audio-speed").forEach((btn) => {
        btn.onclick = (ev) => {
          ev.stopPropagation();
          const cur = parseFloat(btn.dataset.speed || "1");
          const next = cur >= 2 ? 1 : cur >= 1.5 ? 2 : 1.5;
          btn.dataset.speed = String(next);
          btn.textContent = (next % 1 === 0 ? next.toString() : next.toFixed(1)) + "×";
          const audioEl = btn
            .closest(".bubble")
            ?.querySelector<HTMLAudioElement>('audio[data-blob-kind="audio"]');
          if (audioEl) audioEl.playbackRate = next;
        };
      });

      // Stack actions (responder / mais) ao lado da stack
      m.querySelectorAll<HTMLButtonElement>(".stack-actions .ab").forEach((btn) => {
        btn.onclick = (ev) => {
          ev.stopPropagation();
          const id = btn.dataset.msgId!;
          const act = btn.dataset.act;
          if (act === "reply") {
            void this.startReply(id);
          } else if (act === "more") {
            const direction = (btn.dataset.direction || "in") as "in" | "out";
            this.openMessageMenu(btn, id, direction);
          }
        };
      });

      if (offsetFromBottom != null) {
        m.scrollTop = m.scrollHeight - offsetFromBottom;
      } else if (estavaNoFim) {
        m.scrollTop = m.scrollHeight;
      }
      // Se não estava no fim, NÃO mexe. Arrastar a tela de quem está lendo
      // mensagem antiga é o defeito relatado em 7/ago/2026 ("o scroll se move
      // sozinho enquanto estou lendo"): chegava mensagem nova e a conversa
      // pulava para o rodapé, medido em 335px de salto.
    }

    ($("composer-form") as HTMLFormElement).classList.remove("hidden");
  }

  private renderDayDivider(ts: number): string {
    return `
      <div class="day-divider">
        <span class="day-line"></span>
        <span class="day-pill">${escapeHtml(periodoLabel(ts))}</span>
        <span class="day-line"></span>
      </div>`;
  }

  /** Mensagem de "sistema" com o log de ingest da mensagem associada.
   *  Renderiza fora do bubble normal, em estilo monoespaçado, alinhada com a
   *  direção da própria mensagem. Só aparece quando há status ou log. */
  private renderIngestSystemBubble(msg: ChatMessage): string {
    // O log de vetorização é diagnóstico, não conversa: fica fora do fluxo por
    // padrão. Para reativar sem rebuild:
    //   localStorage.setItem("wuwei.showIngest", "1")  e recarregar.
    if (localStorage.getItem("wuwei.showIngest") !== "1") return "";
    const hasLog = !!(msg.ingestLog && msg.ingestLog.length > 0);
    const hasStatus = !!msg.ingestStatus;
    const pages = msg.attachment?.pageEmbeddings;
    const hasPages = !!(pages && pages.length > 0);
    const hasVec = !!(msg.audio?.embedding || msg.embedding) || hasPages;
    if (!hasLog && !hasStatus && !hasVec) return "";

    const cls =
      msg.ingestStatus === "pending"
        ? "is-pending"
        : msg.ingestStatus === "error"
          ? "is-error"
          : hasVec
            ? "is-done"
            : "is-skip";
    const head =
      msg.ingestStatus === "pending"
        ? "ingest · vetorizando…"
        : msg.ingestStatus === "error"
          ? "ingest · falhou"
          : msg.audio?.embedding && msg.audio.embeddingModelId
            ? `ingest · áudio→${escapeHtml(msg.audio.embeddingModelId)} · ${msg.audio.embedding.length}d`
            : hasPages && msg.attachment?.embeddingModelId
              ? `ingest · doc→${escapeHtml(msg.attachment.embeddingModelId)} · ${pages!.length} págs`
              : msg.embedding && msg.embeddingModelId
                ? `ingest · texto→${escapeHtml(msg.embeddingModelId)} · ${msg.embedding.length}d`
                : "ingest · sem vetor";
    // Sem log não há o que expandir: fica só o cabeçalho, sem afordância de
    // clique. Com log, o bloco nasce fechado — o detalhe da vetorização é
    // ruído no fluxo da conversa até alguém pedir por ele.
    if (!hasLog) {
      return `
      <div class="system-msg ${cls}" data-for-msg="${escapeHtml(msg.id)}">
        <div class="system-msg-head">${head}</div>
      </div>`;
    }
    const logLines = `<div class="system-msg-log">${msg
      .ingestLog!.map((l) => `<div>${escapeHtml(l)}</div>`)
      .join("")}</div>`;
    const n = msg.ingestLog!.length;
    return `
      <div class="system-msg ${cls}" data-for-msg="${escapeHtml(msg.id)}">
        <details class="system-msg-details">
          <summary class="system-msg-head">${head}<span class="system-msg-count">${n} ${
            n === 1 ? "linha" : "linhas"
          }</span></summary>
          ${logLines}
        </details>
      </div>`;
  }

  private renderStack(stack: { dir: "in" | "out"; author: string; messages: ChatMessage[] }): string {
    const dirCls = stack.dir === "out" ? "stack-out" : "stack-in";
    const N = stack.messages.length;
    const bubbles = stack.messages
      .map((msg, i) => {
        let pos = "";
        if (N > 1) {
          if (i === 0) pos = "is-first";
          else if (i === N - 1) pos = "is-last";
          else pos = "is-mid";
        }
        return this.renderBubble(msg, { stackPos: pos }) + this.renderIngestSystemBubble(msg);
      })
      .join("");
    const avatarHtml =
      stack.dir === "in"
        ? `<span class="stack-avatar" style="background:${colorFor(stack.author)}">${escapeHtml(avatarInitials(stack.author))}</span>`
        : "";
    // Quick-actions ao lado da stack (hover-revealed). Last msg id é usado
    // pelos handlers de reply/forward/menu pra agir sobre a última bolha.
    const lastMsg = stack.messages[N - 1]!;
    const stackActions = `
      <div class="stack-actions">
        <button class="ab" type="button" data-act="reply" data-msg-id="${escapeHtml(lastMsg.id)}" aria-label="responder">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 00-4-4H4"/></svg>
        </button>
        <button class="ab" type="button" data-act="more" data-msg-id="${escapeHtml(lastMsg.id)}" data-direction="${stack.dir}" aria-label="mais">
          <svg viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/></svg>
        </button>
      </div>`;
    // A hora vive FORA da caixa, logo ABAIXO do bloco e do lado de quem falou.
    // Dentro da bolha ela disputava espaço com o texto; acima, empurrava as
    // mensagens para baixo e afastava o bloco do anterior. Embaixo e colada,
    // ela fecha a troca sem ocupar linha de conversa.
    // A data completa fica no title, para o dia não precisar ser repetido em
    // cada bloco (o divisor de período já diz que dia é).
    const carimbo = `<span class="stack-hora" title="${escapeHtml(fmtDataHora(lastMsg.ts))}">${escapeHtml(fmtTime(lastMsg.ts))}</span>`;
    // data-ts: a âncora que a linha do tempo lê durante o scroll para saber a
    // que altura da conversa o olho está. Fica na stack, não na bolha, porque
    // é o menor bloco que já tem um instante próprio e não multiplica nós.
    return `
      <div class="stack ${dirCls}" data-ts="${stack.messages[0]!.ts}">
        ${avatarHtml}
        <div class="stack-bubbles">${bubbles}${carimbo}</div>
        ${stackActions}
      </div>`;
  }
}

/** Data e hora por extenso, para o title do carimbo: "5 de agosto, 16:14". */
function fmtDataHora(ts: number): string {
  const d = new Date(ts);
  const meses = [
    "janeiro", "fevereiro", "março", "abril", "maio", "junho",
    "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
  ];
  return `${d.getDate()} de ${meses[d.getMonth()]}, ${fmtTime(ts)}`;
}

// -------- helpers fora da classe --------

function renderTick(status?: MessageStatus): string {
  if (!status || status === "pending") {
    return `<span class="tick tick-pending" title="enviando">⏱</span>`;
  }
  if (status === "sent") {
    return `<span class="tick tick-sent" title="enviada">
      <svg viewBox="0 0 16 11" width="16" height="11" aria-hidden="true">
        <path d="M2 6.5 L6 10 L14 2" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </span>`;
  }
  // delivered ou read usam tique duplo; cor muda
  const cls = status === "read" ? "tick-read" : "tick-delivered";
  const title = status === "read" ? "visualizada" : "entregue";
  return `<span class="tick ${cls}" title="${title}">
    <svg viewBox="0 0 16 11" width="16" height="11" aria-hidden="true">
      <path d="M1 6.5 L4 9.5 L9 4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M6.5 6.5 L9.5 9.5 L15 4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  </span>`;
}

function fileEmoji(mime: string, name: string): string {
  if (mime.startsWith("image/")) return "🖼️";
  if (mime.startsWith("audio/")) return "🎵";
  if (mime.startsWith("video/")) return "🎬";
  if (mime === "application/pdf" || /\.pdf$/i.test(name)) return "📕";
  if (/^application\/(zip|x-(?:tar|gzip|7z-compressed)|x-rar-compressed)/.test(mime)) return "🗜️";
  if (/^(text\/|application\/(json|xml|javascript))/.test(mime)) return "📄";
  if (/\.(docx?|odt)$/i.test(name)) return "📘";
  if (/\.(xlsx?|ods|csv)$/i.test(name)) return "📗";
  return "📎";
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// -------- composer auto-resize --------

/** Dedo em vez de mouse: é o teclado do celular que tem "quebrar linha" no
 * lugar onde o do computador tem "enviar". Vai por `pointer: coarse` e não por
 * user-agent, que mente e envelhece; e a consulta é feita a cada tecla porque
 * um tablet com teclado acoplado muda de categoria sem recarregar a página. */
/** Reduz a foto de perfil a um quadrado de 256px antes de subir.
 *
 * A câmera do celular entrega 4 MB; a lista de contatos precisa de 40 pixels.
 * Reduzir aqui é o que permite guardar o avatar dentro do banco, junto do
 * resto do perfil, em vez de virar arquivo solto no disco do servidor — e é o
 * que faz a lista abrir rápido, porque a foto viaja embutida na resposta.
 *
 * O corte é central e quadrado: a lista mostra um círculo, e uma foto
 * retangular esticada dentro dele deforma o rosto.
 */
/** Diagnóstico temporário do seletor de foto.
 *
 *  Manda uma linha para o servidor, que a imprime no journal. Só sai da conta
 *  do dono — telemetria de quem não pediu não entra aqui — e existe porque um
 *  toque que "não abre nada" no celular de outra pessoa não tem como ser
 *  depurado de fora. REMOVER quando o problema estiver fechado.
 *  Ver a rota `/diag` no server.py. */
const DIAG_EMAIL = "tcharliesschmitz@gmail.com";
function diag(quem: string, evento: string, extra?: Record<string, unknown>) {
  if (quem.toLowerCase() !== DIAG_EMAIL) return;
  try {
    const corpo = JSON.stringify({
      evento,
      quando: new Date().toISOString().slice(11, 23),
      standalone: window.matchMedia("(display-mode: standalone)").matches,
      ...extra,
    });
    if (navigator.sendBeacon) navigator.sendBeacon("/diag", corpo);
    else void fetch("/diag", { method: "POST", body: corpo, keepalive: true });
  } catch {
    // Diagnóstico que quebra o app seria pior que não ter diagnóstico.
  }
}

/** Abre a galeria e devolve o arquivo escolhido (ou null, se cancelou).
 *
 *  Copiado do `expo-image-picker` (ExponentImagePicker.web.js), que é o caminho
 *  já provado no celular pelo projeto da rede social. Três detalhes são o
 *  conserto, e nenhum deles é decoração:
 *
 *  - o input é criado NO MOMENTO do toque e pendurado no `<body>`, fora desta
 *    tela: um input que mora dentro do perfil herda estilo, empilhamento e
 *    handlers da tela em volta, e era isso que fazia tocar na foto não abrir
 *    nada no celular;
 *  - o disparo é `dispatchEvent(new MouseEvent("click"))`, não `.click()`;
 *  - some do DOM assim que responde, para não acumular um input por toque.
 *
 *  O `cancel` existe só onde o navegador o implementa; sem ele a promessa
 *  simplesmente nunca resolve, que é o mesmo que o usuário ter desistido. */
function abrirSeletorDeImagem(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.style.display = "none";
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.setAttribute("id", String(Math.random()));
    document.body.appendChild(input);
    let respondido = false;
    const terminar = (f: File | null) => {
      if (respondido) return;
      respondido = true;
      input.remove();
      resolve(f);
    };
    input.addEventListener("change", () => terminar(input.files?.[0] ?? null));
    input.addEventListener("cancel", () => terminar(null));
    input.dispatchEvent(new MouseEvent("click"));
  });
}

/** O arquivo como data URL. `FileReader` e não `URL.createObjectURL`: a URL de
 *  objeto depende do documento que a criou continuar vivo e é o que falha
 *  primeiro dentro de WebView. */
function lerComoDataURL(f: File): Promise<string> {
  return new Promise((ok, erro) => {
    const r = new FileReader();
    r.onerror = () => erro(new Error("não consegui ler o arquivo"));
    r.onload = () => ok(String(r.result || ""));
    r.readAsDataURL(f);
  });
}

async function reduzirImagem(f: File, lado = 256): Promise<string> {
  const url = await lerComoDataURL(f);
  {
    const img = await new Promise<HTMLImageElement>((ok, erro) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = () => erro(new Error("imagem inválida"));
      i.src = url;
    });
    const menor = Math.min(img.naturalWidth, img.naturalHeight) || lado;
    const cv = document.createElement("canvas");
    cv.width = cv.height = lado;
    const ctx = cv.getContext("2d");
    if (!ctx) throw new Error("sem canvas");
    ctx.drawImage(
      img,
      (img.naturalWidth - menor) / 2,
      (img.naturalHeight - menor) / 2,
      menor,
      menor,
      0,
      0,
      lado,
      lado,
    );
    // JPEG e não PNG: um retrato em PNG passa fácil dos 300 KB que o servidor
    // aceita, e a diferença visual num círculo de 40px é nenhuma.
    return cv.toDataURL("image/jpeg", 0.82);
  }
}

/** Um arquivo colado da área de transferência costuma chegar como
 * "image.png" ou sem nome nenhum. Batizar aqui, com data e hora, é o que faz
 * três prints colados no mesmo dia continuarem distinguíveis na aba Arquivos. */
function nomeSeFaltar(f: File): File {
  const generico = !f.name || /^(image|screenshot|arquivo)\.?\w*$/i.test(f.name);
  if (!generico) return f;
  const ext = (f.type.split("/")[1] || "png").split(";")[0];
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  const nome =
    `colado-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}` +
    `-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}.${ext}`;
  return new File([f], nome, { type: f.type });
}

function enterQuebraLinha(): boolean {
  return window.matchMedia?.("(pointer: coarse)").matches ?? false;
}

function wireComposerResize() {
  const ta = $("composer-input") as HTMLTextAreaElement;
  ta.addEventListener("input", () => {
    ta.style.height = "auto";
    ta.style.height = Math.min(140, ta.scrollHeight) + "px";
  });
  ta.addEventListener("keydown", (e) => {
    // No celular, Enter escreve a segunda linha e o envio é só pelo botão.
    // Antes, quem escrevia em duas linhas mandava a primeira metade sem
    // querer — e a mensagem partida já tinha saído.
    if (e.key === "Enter" && !e.shiftKey && !enterQuebraLinha()) {
      e.preventDefault();
      (ta.form as HTMLFormElement).requestSubmit();
    }
  });
}

// -------- bootstrap --------

async function main() {
  wireComposerResize();
  const sess = getSession();
  if (!sess) {
    showLogin();
    return;
  }
  const app = new ChatApp(sess);
  await app.start();
}

main().catch((err) => {
  console.error(err);
  alert("Erro ao iniciar: " + (err instanceof Error ? err.message : String(err)));
});
